import React, { useState, useRef, useEffect } from 'react';
import { 
  BrainCircuit, 
  Network, 
  Radio, 
  Cpu, 
  Rocket, 
  TrendingUp, 
  Factory, 
  LogOut, 
  Crosshair,
  Shield,
  Activity,
  ArrowRight,
  ArrowDown,
  BookOpen,
  Layers,
  Zap,
  LayoutGrid,
  Globe,
  X,
  Award,
  Building,
  PieChart,
  ClipboardCheck,
  Briefcase,
  GitMerge,
  Target,
  AlertTriangle,
  FileText,
  Lock,
  Scale,
  Landmark,
  Server,
  Command,
  Filter,
  Menu,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  List,
  Fingerprint,
  SlidersHorizontal,
  CheckCircle2,
  UserCheck,
  MapPin,
  Linkedin,
  Search,
  Power,
  Send
} from 'lucide-react';

// --- DICTIONARY ENGINES ---
const jargonDict = {
  // System & Network
  "JADC2": "Joint All-Domain Command and Control: MOD/Global Defense initiative to connect sensors from all military services into a single network.",
  "CJADC2": "Combined JADC2: The extension of the JADC2 network to include allied and partner nations.",
  "MOSA": "Modular Open Systems Approach: MOD/Global Defense strategy to design systems with highly cohesive, loosely coupled modules.",
  "EW": "Electronic Warfare: Controlling the electromagnetic spectrum to attack or impede enemy operations.",
  "EWaaS": "Electronic Warfare as a Service: Provisioning EW capabilities on-demand via contractor platforms.",
  "LPI/LPD": "Low Probability of Intercept/Detection: Stealthy comms resistant to enemy jamming.",
  "SaaS": "Software as a Service: Cloud-based software delivery model commanding premium valuation multiples.",
  
  // Vehicles & Hardware
  "UAS": "Unmanned Aerial System: Drones and their associated autonomous control systems.",
  "C-UAS": "Counter-UAS: Systems designed to detect, track, and destroy enemy drones.",
  "USV": "Unmanned Surface Vessel: Autonomous or remotely operated maritime surface ships.",
  "USVs": "Uncrewed Surface Vessels: Explosive-laden drone boats often used for waterline strikes.",
  "UUV": "Unmanned Underwater Vehicle: Autonomous or remotely operated submarines.",
  "ISR": "Intelligence, Surveillance, & Reconnaissance: Integrated capabilities to collect and process battlefield data.",
  "OWA": "One-Way Attack: Low-cost 'kamikaze' or loitering munitions often costing under $2,000.",
  "ASBM": "Anti-Ship Ballistic Missile: Ballistic missiles designed to track and hit moving targets at sea.",
  "SM-2": "Standard Missile-2: A $2.1M fleet-area air defense weapon used by naval destroyers.",
  "CCA": "Collaborative Combat Aircraft: MOD/Global Defense program for autonomous drones that fly alongside manned fighter jets.",
  "NGAD": "Next Generation Air Dominance: 6th-generation fighter initiative.",
  
  // Contracting & Finance
  "PoR": "Program of Record: A finalized, long-term MOD/Global Defense budget allocation ensuring recurring revenue.",
  "PoRs": "Programs of Record: Finalized, long-term MOD/Global Defense budget allocations ensuring recurring revenue.",
  "PORs": "Programs of Record: Finalized, long-term MOD/Global Defense budget allocations ensuring recurring revenue.",
  "OTA": "Other Transaction Authority: Fast-track MOD/Global Defense contracting mechanism bypassing traditional procurement.",
  "OTAs": "Other Transaction Authorities: Fast-track MOD/Global Defense contracting mechanisms bypassing traditional procurement.",
  "SBIR": "Small Business Innovation Research: US gov funding for domestic small businesses R&D.",
  "CVC": "Corporate Venture Capital: Strategic investment arms of legacy prime contractors.",
  "Enterprise": "A $25B Air Force program focused on the rapid, large-scale production of low-cost, modular autonomous weapons.",
  
  // Orgs & Regulations
  "DIU": "Defense Innovation Unit: MOD/Global Defense organization focused on accelerating and scaling commercial technology.",
  "ITAR": "International Traffic in Arms Regs: US regime strictly restricting export of defense tech.",
  "FedRAMP": "Federal Risk & Authorization Management Program: Standardized security assessment for cloud products.",
  "AUKUS": "Australia, UK, US: Trilateral security pact focusing on nuclear subs and advanced capabilities.",
  "NATO": "North Atlantic Treaty Organization: Intergovernmental military alliance of North American and European countries.",
  "SDA": "Space Development Agency: MOD/Global Defense agency developing the proliferated National Defense Space Architecture.",
  "CDAO": "Chief Digital and Artificial Intelligence Office: MOD/Global Defense office responsible for scaling data, analytics, and AI.",
  "ATO": "Authority to Operate: The official security approval required to deploy software on government networks.",
  "ATOs": "Authorities to Operate: The official security approvals required to deploy software on government networks.",
  
  // Tech Specs
  "LEO": "Low Earth Orbit: Altitude of 2,000 km or less, crucial for mesh satellite networks and low-latency comms.",
  "GEO": "Geosynchronous Orbit: High altitude orbit used for traditional, massive communication satellites.",
  "IL4/IL5": "Impact Level 4/5: Stringent MOD/Global Defense cloud security requirements for national security systems.",
  "EBITDA": "Earnings Before Interest, Taxes, D&A: A measure of operational profitability.",
  "TAM": "Total Addressable Market: The overall revenue opportunity available for a product or service.",
  "MOIC": "Multiple on Invested Capital: A critical metric describing the gross return of an investment.",
  "RF": "Radio Frequency: Electromagnetic wave frequencies used for communications and radar.",
  "PNT": "Positioning, Navigation, and Timing: Critical targeting and orientation data historically provided by GPS.",
  "GPS": "Global Positioning System: US satellite-based radionavigation system.",
  "MOD/Global Defense": "MOD/Global Defense: Executive branch department of the national government responsible for security and military forces."
};

const thesisDict = {
  "High-Margin IP": "Software-defined assets with scalable economics, distinct from low-margin hardware manufacturing.",
  "Attritable Mass": "Low-cost, high-volume systems designed to be expendable in combat without significant financial or strategic loss.",
  "Deep Tech": "Highly complex, R&D-intensive hardware innovations (e.g., advanced materials, energetics, propulsion).",
  "Missing Middle": "The connective tissue and integration layers that bridge legacy systems with next-generation autonomous platforms.",
  "Dual-Use": "Technologies with viable and significant commercial market applications alongside their defense utility.",
  "Decision Advantage": "AI and cognitive systems that allow commanders to process data and act faster than adversaries.",
  "DFARS Qualifying": "Originating from a country exempt from the Buy American Act (BAA), granting a massive structural advantage for direct U.S. DoD procurement.",
  "Onshore JV Required": "Subject to Buy American Act (BAA) restrictions. Requires the establishment of a U.S.-based Joint Venture or holding entity to satisfy domestic-content thresholds for DoD procurement.",
  "CMMC L2 Ready": "Cybersecurity Maturity Model Certification Level 2. Mandatory for handling Controlled Unclassified Information (CUI). Crucial for supply chain integration.",
  "DoD IL6 / Secret": "Authorized to host and process classified 'Secret' level data in DoD cloud environments. Represents a massive barrier to entry and a premium SaaS moat.",
  "FOCI Mitigated": "Foreign Ownership, Control, or Influence mitigation. Cleared to execute classified U.S. contracts despite foreign allied investment via Special Security Agreements.",
  "DUAL": "Export Administration Regulations (EAR) controlled. Indicates technology is not strictly weaponized under ITAR, significantly reducing cross-border licensing friction and offering commercial downside protection."
};

// Safe regex logic to isolate whole acronyms without breaking formatting
const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const sortedJargonKeys = Object.keys(jargonDict).sort((a, b) => b.length - a.length);
// Explicit string concatenation avoids deep template-literal escape issues in strict parsers
const jargonRegex = new RegExp("(^|\\s|[([{])(" + sortedJargonKeys.map(escapeRegExp).join('|') + ")(?=\\s|$|[,.)}\\]s:/])", "g");

const renderWithJargon = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  const parts = text.split(jargonRegex);
  
  return parts.map((part, i) => {
    if (part && jargonDict[part]) {
      const splitPoint = jargonDict[part].indexOf(':');
      const title = splitPoint > -1 ? jargonDict[part].slice(0, splitPoint) : part;
      const desc = splitPoint > -1 ? jargonDict[part].slice(splitPoint + 1).trim() : jargonDict[part];
      
      return (
        <span key={i} className="group/jargon relative inline-block border-b border-dotted border-slate-500 cursor-help text-slate-200 hover:text-white transition-colors">
          {part}
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 bg-[#0B1120] text-slate-300 text-[11px] leading-relaxed rounded-xl border border-slate-700 shadow-[0_15px_40px_rgba(0,0,0,0.8)] opacity-0 invisible group-hover/jargon:opacity-100 group-hover/jargon:visible transition-all z-[100] pointer-events-none font-sans font-normal normal-case text-left tracking-normal">
            <span className="font-bold text-cyan-400 block mb-1">{title}</span>
            <span className="text-slate-400">{desc}</span>
          </span>
        </span>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

// --- DATA MODELS ---
const geoFilters = [
  { id: 'Global', label: 'Global Matrix' },
  { id: 'AUKUS', label: 'AUKUS', codes: ['US', 'UK', 'AU'] },
  { id: 'NATO', label: 'NATO / Europe', codes: ['UK', 'DE', 'FR', 'EE', 'NO'] },
  { id: 'GCC', label: 'GCC / MENA', codes: ['AE', 'SA', 'QA'] },
  { id: 'Israel', label: 'Israel (Battle-Tested)', codes: ['IL'] },
  { id: 'IndoPac', label: 'Indo-Pacific', codes: ['KR', 'IN', 'JP', 'TW', 'AU'] }
];

const layers = [
  {
    id: 5,
    name: "Mission Intelligence",
    subtitle: "Agentic Autonomy & Multi-Agent Orchestration",
    icon: BrainCircuit,
    color: "bg-indigo-900/30 border-indigo-500",
    activeColor: "bg-indigo-800/50 shadow-[0_0_20px_rgba(99,102,241,0.3)] border-indigo-400",
    textColor: "text-indigo-400",
    margin: "Very High (SaaS / IP)",
    capex: "Medium (R&D focus)",
    exit: "Extreme (Tech Multiples)",
    interest: "Extreme (AI Hype)",
    players: ["Shield AI", "Helsing", "Rebellion Defense", "NODA AI", "Shift5"],
    thesis: "Software is shifting from static target visualization to Agentic Multi-Agent Orchestration. The value has migrated to platforms that automate tactical decision workflows and execute edge coordination autonomously, commanding standard SaaS multiples of 20x-30x.",
    maturity: "Growth",
    finance: { tam: "$38.8B Est.", multiple: "15x - 30x FWD REV", margin: "85%+ Gross", cagr: "31.5%" },
    alphaInsights: [
      { icon: BrainCircuit, title: "Agentic Shift", desc: "Value is shifting from Generative AI (analysis) to Agentic execution (taking action). Autonomy commands the highest multiples." },
      { icon: Layers, title: "Full-Stack Autonomy", desc: "Hardware-agnostic software like Hivemind enables a 'winner-takes-most' ecosystem that can be ported across legacy platforms." }
    ],
    orientationDesc: "Software is shifting from static target visualization to Agentic Multi-Agent Orchestration. The value has migrated to platforms that automate tactical decision workflows and execute edge coordination autonomously, commanding standard SaaS multiples of 20x-30x.",
    orientationSegments: "Computer Vision for targeting, Autonomous Piloting (Hivemind), and Electronic Warfare (EW) algorithms.",
    orientationThesis: "Whoever owns the \"Decision Advantage\" (the ability to act faster than the enemy) wins the war.",
    orientationPlayers: [
      { name: "Shield AI", signal: "Targeting Mega-IPO; Hivemind autonomy" },
      { name: "Helsing", signal: "Series C @ €5B Valuation; NATO champion" },
      { name: "Rebellion Defense", signal: "Restructuring for automated adversary simulation" },
      { name: "Fujitsu / ATLA", signal: "Pioneering Defense Multi-AI Agents for Japan" }
    ],
    archetype: "Software Pure-Play / Neo-Primes",
    segments: [
      { 
        name: "Agentic AI & Command and Control", 
        conviction: "high",
        maturity: "Emerging",
        desc: "AI 'Agents' that can autonomously plan a mission or optimize logistics.", 
        examples: "Helsing, NODA AI, Oxford Dynamics, Harmattan AI",
        tags: ["High-Margin IP", "Decision Advantage"],
        companies: [
          { name: "Helsing", tier: "neoprime", tags: ["NATO", "DUAL"], desc: "Software-only AI company for defense platforms.", moat: "Deep integration with European prime contractors.", signal: "Series C valued at €5B+.", hq: "DE", stage: "Series C", emp: "300+", founded: "2021", ownership: "Venture-Backed", itar: "NATO Sovereign" },
          { name: "NODA AI", tier: "challenger", tags: ["DIU"], desc: "Swarm logic and autonomous planning.", moat: "Agentic reinforcement learning algorithms.", signal: "High M&A target for Neoprimes.", hq: "US", stage: "Series A", emp: "50+", founded: "2018", ownership: "Private", itar: "ITAR Restricted" },
          { name: "OneBrief", tier: "challenger", tags: [], desc: "Agile military planning software.", moat: "Replaces legacy PowerPoint/Excel mission planning.", signal: "Rapid user adoption in US commands.", hq: "US", stage: "Series B", emp: "100+", founded: "2019", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "Scale AI", tier: "neoprime", tags: ["DUAL", "DIU", "DoD IL6 / Secret", "CMMC L2 Ready"], desc: "LLMs and data platforms for national security.", moat: "Donovan platform and massive defense data contracts.", signal: "$14B valuation and broad AI dominance.", hq: "US", stage: "Series F", emp: "1K+", founded: "2016", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "Oxford Dynamics", tier: "challenger", tags: ["DUAL"], desc: "AI solutions for complex defense challenges.", moat: "Deep commercial AI expertise applied to defense.", signal: "Backed by BAE Systems strategic CVC.", hq: "UK", stage: "Seed", emp: "20+", founded: "2022", ownership: "CVC-Backed", itar: "AUKUS Aligned" },
          { name: "Harmattan AI", tier: "challenger", tags: ["Decision Advantage", "DUAL"], desc: "Agentic AI and cognitive decision systems.", moat: "Localized GCC cognitive processing and decision advantage.", signal: "Emerging AI leader in the UAE defense ecosystem.", hq: "AE", stage: "Early Stage", emp: "50+", founded: "2023", ownership: "Private", itar: "Sovereign Restricted" },
          { name: "BAE Systems (Digital Intel)", tier: "legacy", tags: ["AUKUS", "NATO"], desc: "Cyber, intelligence, and security division of BAE.", moat: "Massive European/AUKUS incumbency and GCAP (6th Gen Fighter) prime status.", signal: "Competing aggressively for software-defined defense contracts across NATO.", hq: "UK", stage: "Public", emp: "10K+", founded: "1999", ownership: "Public (BA.L)", itar: "AUKUS Aligned" },
          { name: "Fujitsu (ATLA)", tier: "legacy", tags: ["Decision Advantage", "DUAL"], desc: "Developing Defense Multi-AI Agents for ATLA.", moat: "Japan's first specialized open innovation defense program for automated operational support.", signal: "Validates active sovereign transition to agentic, autonomous multi-agent systems.", hq: "JP", stage: "Public", emp: "100K+", founded: "1935", ownership: "Public", itar: "DFARS Qualifying" }
        ]
      },
      { 
        name: "Targeting & Computer Vision", 
        conviction: "high",
        maturity: "Growth",
        desc: "Computer Vision that identifies targets in real-time under camouflage.", 
        examples: "Shield AI, TurbineOne, Corsight, Shift Dynamics",
        tags: ["High-Margin IP", "Dual-Use"],
        companies: [
          { name: "Shield AI", tier: "neoprime", tags: ["DUAL", "DIU"], desc: "AI pilot (Hivemind) for aircraft, drones, and tactical swarms.", moat: "Hardware-agnostic autonomy stack with edge swarming capabilities.", signal: "Closed $2B transaction (including $1.5B Series G at $12.7B valuation led by Advent International) in March 2026.", hq: "US", stage: "Series G", emp: "1,000+", founded: "2015", ownership: "Venture-Backed ($12.7B Valuation)", itar: "ITAR Restricted" },
          { name: "TurbineOne", tier: "challenger", tags: ["DIU"], desc: "Frontline ML for threat recognition.", moat: "No-code model training at the tactical edge.", signal: "Strong OTA growth.", hq: "US", stage: "Series A", emp: "30+", founded: "2021", ownership: "Venture-Backed", itar: "ITAR Compliant" },
          { name: "Palantir (AIP)", tier: "neoprime", tags: ["DUAL", "DoD IL6 / Secret", "CMMC L2 Ready"], desc: "AI Platform for defense targeting chains.", moat: "Entrenched in Project Maven and JADC2.", signal: "Strategic integration with GDLS combat vehicles.", hq: "US", stage: "Public", emp: "3K+", founded: "2003", ownership: "Public (PLTR)", itar: "FedRAMP High" },
          { name: "Primer AI", tier: "challenger", tags: ["DUAL"], desc: "NLP and CV for threat detection.", moat: "Specialized defense-trained models.", signal: "High adoption in SOCOM.", hq: "US", stage: "Series D", emp: "250+", founded: "2015", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "Corsight AI", tier: "challenger", tags: ["DUAL"], desc: "Real-time facial recognition under extreme conditions.", moat: "High accuracy in low-light and concealed environments.", signal: "Battle-tested integrations in recent conflicts.", hq: "IL", stage: "Growth", emp: "100+", founded: "2019", ownership: "Private", itar: "Dual-Use Open" },
          { name: "Shift Dynamics", tier: "challenger", tags: ["High-Margin IP", "Decision Advantage"], desc: "Integrating AI, computer vision, and path-planning into tactical systems.", moat: "The 'AI Brain' for small UAS and FPV drone ecosystems.", signal: "Strategic subsidiary of Red Cat, targeting Army SRR requirements.", hq: "US", stage: "Subsidiary", emp: "50+", founded: "2023", ownership: "Public (RCAT)", itar: "ITAR Compliant" },
          { name: "GenGen AI", tier: "challenger", tags: ["Onshore JV Required", "High-Margin IP", "Dual-Use"], desc: "Generative AI & Sim2Real synthetic video generation for defense vision.", moat: "Overcomes data scarcity by creating hyper-realistic multi-sensor training environments.", signal: "KAI acquired a 10% equity stake to accelerate AI pilot development via GenGenStudio.", hq: "KR", stage: "Seed", emp: "30+", founded: "2020", ownership: "Venture/Strategic-Backed", itar: "Dual-Use Open" }
        ]
      },
      { 
        name: "Autonomous Cyber Warfare", 
        conviction: "high",
        maturity: "Growth",
        desc: "Software that autonomously hunts for vulnerabilities or defends networks in real-time.", 
        examples: "Claroty, Dragos",
        tags: ["High-Margin IP", "Decision Advantage"],
        companies: [
          { name: "Dragos", tier: "challenger", tags: ["DUAL"], desc: "Industrial Control Systems (ICS) cybersecurity.", moat: "Deep expertise in critical infrastructure defense.", signal: "Critical for base defense.", hq: "US", stage: "Series D", emp: "500+", founded: "2016", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "Rebellion Defense", tier: "challenger", tags: ["AUKUS"], desc: "Automated adversary simulation.", moat: "Continuous automated red-teaming.", signal: "Rebuilding post-restructuring.", hq: "US", stage: "Growth", emp: "200+", founded: "2019", ownership: "Venture-Backed", itar: "ITAR Compliant" },
          { name: "Horizon3.ai", tier: "challenger", tags: ["DUAL"], desc: "Autonomous penetration testing.", moat: "NodeZero continuous verification.", signal: "High commercial crossover.", hq: "US", stage: "Series C", emp: "150+", founded: "2019", ownership: "Venture-Backed", itar: "Dual-Use Open" }
        ]
      },
      { 
        name: "Predictive Maintenance & Logistics AI", 
        conviction: "medium",
        maturity: "Mainstream",
        desc: "AI that predicts part failures before they happen for aging fleets.", 
        examples: "Shift5, C3.ai Defense",
        tags: ["High-Margin IP", "Dual-Use"],
        companies: [
          { name: "Shift5", tier: "challenger", tags: ["DUAL"], desc: "Observability for onboard fleet systems.", moat: "Direct access to serial bus data on legacy vehicles.", signal: "High commercial dual-use in rail/aviation.", hq: "US", stage: "Series B", emp: "150+", founded: "2019", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "C3.ai", tier: "legacy", tags: ["DUAL"], desc: "Enterprise AI for predictive maintenance.", moat: "Massive existing enterprise footprint.", signal: "Pivoting hard into defense.", hq: "US", stage: "Public", emp: "1K+", founded: "2009", ownership: "Public (AI)", itar: "FedRAMP High" },
          { name: "SparkCognition", tier: "challenger", tags: ["DUAL"], desc: "AI-driven asset management.", moat: "Predictive analytics for aviation.", signal: "Expanding from commercial to DoD.", hq: "US", stage: "Series D", emp: "300+", founded: "2013", ownership: "Venture-Backed", itar: "Dual-Use Open" }
        ]
      }
    ]
  },
  {
    id: 4,
    name: "Interoperability Fabric",
    subtitle: "Data Fusion & The Missing Middle",
    icon: Network,
    color: "bg-blue-900/30 border-blue-500",
    activeColor: "bg-blue-800/50 shadow-[0_0_20px_rgba(59,130,246,0.3)] border-blue-400",
    textColor: "text-blue-400",
    margin: "Very High (SaaS / OS)",
    capex: "Low (Software scaling)",
    exit: "High Multiples / IPO",
    interest: "Extreme (SaaS Moats)",
    players: ["Palantir", "Anduril (Lattice)", "Defense Unicorns", "Applied Intuition", "Xage Security"],
    thesis: "The middleware that breaks vendor lock-in. Allows legacy Boeing jets to communicate instantly with autonomous drones. Extremely sticky revenue once integrated into a Program of Record.",
    maturity: "Emerging",
    finance: { tam: "$15.0B+ Annual", multiple: "10x - 20x FWD REV", margin: "80%+ Gross", cagr: "17.5%" },
    alphaInsights: [
      { icon: Lock, title: "Vendor Lock-in Breaker", desc: "Fixes technical debt of legacy primes by acting as the OS of the battlefield, turning closed hardware into open platforms." },
      { icon: Network, title: "CJADC2 Anchor", desc: "The core enabler of the MOD/Global Defense's Joint All-Domain Command and Control initiative; extreme contract stickiness once integrated." }
    ],
    orientationDesc: "This is the middleware that allows a Boeing plane to \"talk\" to an Anduril drone and a Lockheed missile. Historically, these systems were closed silos.",
    orientationSegments: "Data Fusion, Open Architecture (MOSA) tools, and API-first mission systems.",
    orientationThesis: "Companies that break \"vendor lock-in.\" This layer is high-margin software with extreme \"stickiness.\"",
    orientationPlayers: [
      { name: "Palantir", signal: "AIP entrenched in Project Maven and JADC2" },
      { name: "Defense Unicorns", signal: "Open-source mission software delivery" },
      { name: "Lattice (Anduril)", signal: "OS for autonomous systems and sensor fusion" }
    ],
    archetype: "The 'Missing Middle' / Integrators",
    segments: [
      { 
        name: "Data Fusion & Operating Systems", 
        conviction: "high",
        maturity: "Growth",
        desc: "Operating systems that manage hundreds of autonomous assets at once.", 
        examples: "Anduril (Lattice), Palantir, Pablo Air",
        tags: ["High-Margin IP", "Missing Middle"],
        companies: [
          { name: "Anduril (Lattice)", tier: "neoprime", tags: ["DIU", "AUKUS", "DoD IL6 / Secret", "CMMC L2 Ready"], desc: "OS for autonomous systems and sensor fusion.", moat: "Closed-loop sensor-to-shooter capabilities via Lattice OS.", signal: "Closed historic $5B Series H at $61B valuation (May 2026); awarded $20B 10-yr US Army Enterprise contract.", hq: "US", stage: "Series H", emp: "7,000+", founded: "2017", ownership: "Venture-Backed ($61B Valuation)", itar: "ITAR Restricted" },
          { name: "Defense Unicorns", tier: "challenger", tags: ["DUAL", "CMMC L2 Ready"], desc: "Continuous delivery for national security systems.", moat: "Open-source mission software delivery.", signal: "High-growth software enabler.", hq: "US", stage: "Series A", emp: "150+", founded: "2021", ownership: "Venture-Backed", itar: "FedRAMP High" },
          { name: "Second Front Systems", tier: "challenger", tags: ["DUAL", "CMMC L2 Ready"], desc: "Software delivery platform.", moat: "Game Warden DevSecOps platform.", signal: "Accelerating IL4/IL5 accreditations.", hq: "US", stage: "Series B", emp: "100+", founded: "2014", ownership: "Venture-Backed", itar: "FedRAMP High" },
          { name: "Pablo Air", tier: "challenger", tags: ["Onshore JV Required", "CMMC L2 Ready", "Attritable Mass", "Missing Middle"], desc: "Swarm intelligence and cross-domain orchestration software (PAMNet).", moat: "Swarm-as-a-Service, allowing a single operator to command massive drone fleets.", signal: "Primary tech anchor for DAPA's MUM-T (Manned-Unmanned Teaming) combat integration drills.", hq: "KR", stage: "Series B", emp: "150+", founded: "2018", ownership: "Private", itar: "Dual-Use Open" }
        ]
      },
      { 
        name: "Digital Twins & Simulation", 
        conviction: "high",
        maturity: "Growth",
        desc: "Simulation environments to test software before it hits the field.", 
        examples: "Applied Intuition, Schemata",
        tags: ["High-Margin IP", "Missing Middle"],
        companies: [
          { name: "Applied Intuition", tier: "neoprime", tags: ["DUAL"], desc: "Vehicle software and simulation platform.", moat: "Dominant in commercial autonomous driving.", signal: "Strategic partnership with General Dynamics (GDLS).", hq: "US", stage: "Series E", emp: "500+", founded: "2017", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "EpiSci", tier: "challenger", tags: ["DIU"], desc: "Tactical AI for swarming and dogfighting.", moat: "Simulation-to-reality transition architecture.", signal: "Key player in loyal wingman programs.", hq: "US", stage: "Growth", emp: "50+", founded: "2012", ownership: "Private", itar: "ITAR Restricted" },
          { name: "Hadean", tier: "challenger", tags: ["NATO", "DUAL"], desc: "Spatial computing and spatial simulation.", moat: "Distributed computing for massive simulations.", signal: "UK MOD contracts.", hq: "UK", stage: "Series A", emp: "100+", founded: "2015", ownership: "Venture-Backed", itar: "AUKUS Aligned" }
        ]
      },
      { 
        name: "Zero-Trust Security & Identity", 
        conviction: "medium",
        maturity: "Mainstream",
        desc: "Authenticating machine-to-machine communications in decentralized networks.", 
        examples: "Xage Security, Defense Unicorns",
        tags: ["High-Margin IP", "Dual-Use"],
        companies: [
          { name: "Xage Security", tier: "challenger", tags: ["DUAL", "DIU"], desc: "Zero-trust for distributed IoT and edge.", moat: "Blockchain-backed decentralized identity.", signal: "Securing Space Force and critical infra.", hq: "US", stage: "Series B", emp: "80+", founded: "2017", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "Babel Street", tier: "legacy", tags: ["DUAL"], desc: "Identity and threat intelligence.", moat: "Massive open-source data aggregation.", signal: "PE-backed roll-up platform.", hq: "US", stage: "Mature", emp: "300+", founded: "2009", ownership: "PE-Backed", itar: "FedRAMP High" },
          { name: "Zscaler (Defense)", tier: "legacy", tags: ["DUAL"], desc: "Cloud-native zero trust network access.", moat: "Massive commercial scale applied to FedRAMP.", signal: "Displacing legacy VPNs across MOD/Global Defense.", hq: "US", stage: "Public", emp: "5K+", founded: "2007", ownership: "Public (ZS)", itar: "FedRAMP High" }
        ]
      }
    ]
  },
  {
    id: 3,
    name: "Transport Mesh",
    subtitle: "Resilient Connectivity & EW",
    icon: Radio,
    color: "bg-teal-900/30 border-teal-500",
    activeColor: "bg-teal-800/50 shadow-[0_0_20px_rgba(20,184,166,0.3)] border-teal-400",
    textColor: "text-teal-400",
    margin: "High (Recurring Service)",
    capex: "High (Constellations/Hardware)",
    exit: "Long-term Yield / Strategic M&A",
    interest: "Strategic (Primes & PE)",
    players: ["SpaceX (Starshield)", "Chaos Industries", "Epirus", "Muon Space", "Xona Space"],
    thesis: "The plumbing of the battlefield. Creating self-healing, anti-jam networks using pLEO satellites, laser comms, and tactical 5G/6G. Critical for operating in GPS-denied environments.",
    maturity: "High",
    finance: { tam: "$12.5B+ Est.", multiple: "8x - 12x EBITDA", margin: "40-60% Gross", cagr: "15.4%" },
    alphaInsights: [
      { icon: Shield, title: "Commodity vs. SLA", desc: "Transport itself is increasingly commoditized; the pricing premium is strictly in Anti-Jam and LPI/LPD (Low Probability of Intercept) features." },
      { icon: Globe, title: "Proliferated LEO", desc: "Aggressive shift from single, exquisite GEO satellites to massive, resilient mesh networks of disposable LEO nodes." }
    ],
    orientationDesc: "How data moves between units. This is the \"plumbing\" of the battlefield.",
    orientationSegments: "Counter-UAS (C-UAS), LEO Satellite Constellations (Starshield), Laser Communications, Mesh Networking, and Tactical 5G/6G.",
    orientationThesis: "Building \"Self-healing\" networks. If one satellite is shot down, the network automatically reroutes data through another.",
    orientationPlayers: [
      { name: "SpaceX/Starshield", signal: "Effectively monopolizing military-grade proliferated LEO transport" },
      { name: "Honeywell", signal: "Laser-comms and mesh networking capabilities" },
      { name: "Chaos Industries", signal: "High-performance software-defined RF and EW" },
      { name: "Synspective", signal: "Deploying allied SAR constellation with deep-learning anomaly detection" }
    ],
    archetype: "Space 2.0 / Deep Tech",
    segments: [
      { 
        name: "Resilient Communications", 
        conviction: "high",
        maturity: "Growth",
        desc: "Multi-modal mesh networks using laser, 5G/6G, and satellite.", 
        examples: "AnySignal, Honeywell",
        tags: ["Deep Tech", "Dual-Use"],
        companies: [
          { name: "Starshield (SpaceX)", tier: "neoprime", tags: ["DUAL"], desc: "Military-grade proliferated LEO comms.", moat: "Unmatched launch economics and scale.", signal: "Effectively a monopoly in LEO transport.", hq: "US", stage: "Private", emp: "10K+", founded: "2002", ownership: "Private", itar: "ITAR Restricted" },
          { name: "AnySignal", tier: "challenger", tags: ["DUAL"], desc: "Software-defined radio platform.", moat: "Replaces fragmented legacy radios with API-first hardware.", signal: "Fastest growing space-comms startup.", hq: "US", stage: "Seed", emp: "30+", founded: "2022", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "Viasat", tier: "legacy", tags: [], desc: "Legacy SATCOM provider.", moat: "Incumbent contracts and massive GEO satellites.", signal: "Vulnerable to LEO disruption.", hq: "US", stage: "Public", emp: "7K+", founded: "1986", ownership: "Public (VSAT)", itar: "ITAR Compliant" },
          { name: "Astranis", tier: "challenger", tags: ["DUAL"], desc: "MicroGEO satellites.", moat: "Dedicated, jam-resistant GEO nodes at lower cost.", signal: "Providing dedicated bandwidth for military.", hq: "US", stage: "Series C", emp: "300+", founded: "2015", ownership: "Venture-Backed", itar: "ITAR Compliant" }
        ]
      },
      { 
        name: "Counter-UAS & Directed Energy", 
        conviction: "high",
        maturity: "Mainstream",
        desc: "Systems designed to detect, track, and neutralize drone threats via electronic jamming, high-power microwaves, or kinetic interceptors.", 
        examples: "Anduril (Roadrunner), Epirus, Frankenburg Tech, TORIS SQUARE",
        tags: ["Deep Tech", "Decision Advantage"],
        companies: [
          { name: "Anduril (Roadrunner)", tier: "neoprime", tags: ["Attritable Mass"], desc: "Reusable vertical takeoff C-UAS interceptor.", moat: "Flipping the cost equation (~$100k per unit).", signal: "Rapidly scaling to solve the interception dilemma.", hq: "US", stage: "Series F", emp: "2K+", founded: "2017", ownership: "Venture-Backed", itar: "ITAR Restricted" },
          { name: "Epirus", tier: "neoprime", tags: ["DIU"], desc: "High-power microwave (HPM) directed energy.", moat: "Solid-state architecture allowing swarm-defeat.", signal: "Transitioning to major Program of Record.", hq: "US", stage: "Series C", emp: "200+", founded: "2018", ownership: "Venture-Backed", itar: "ITAR Restricted" },
          { name: "Frankenburg Tech", tier: "challenger", tags: ["Attritable Mass"], desc: "Low-cost missile technology.", moat: "Mass-manufacturable kinetic effectors.", signal: "Partnered with BAE Systems for C-UAS.", hq: "EE", stage: "Seed", emp: "20+", founded: "2023", ownership: "Private", itar: "NATO Sovereign" },
          { name: "TORIS SQUARE", tier: "challenger", tags: ["Decision Advantage", "Dual-Use"], desc: "Radar-based anti-drone solution platforms.", moat: "Software-defined distributed radar for the C-UAS detection phase.", signal: "Sensing foundation for kinetic effectors in the Indo-Pacific.", hq: "KR", stage: "Growth", emp: "50+", founded: "2020", ownership: "Private", itar: "Dual-Use Open" }
        ]
      },
      { 
        name: "Spectrum & Electronic Warfare", 
        conviction: "high",
        maturity: "Mainstream",
        desc: "Systems that protect the RF spectrum or jam the enemy.", 
        examples: "Chaos Industries, Metrea",
        tags: ["Deep Tech", "Decision Advantage"],
        companies: [
          { name: "Chaos Industries", tier: "neoprime", tags: ["Deep Tech", "Decision Advantage"], desc: "Software-defined advanced sensing, radar, and electronic warfare.", moat: "Vanir platform combining agile software cycles with exquisite RF/EW performance.", signal: "$145M Series B; rapidly deploying advanced sensors for Great Power Competition.", hq: "US", stage: "Series B", emp: "150+", founded: "2022", ownership: "Venture-Backed", itar: "ITAR Restricted" },
          { name: "Metrea", tier: "challenger", tags: ["AUKUS"], desc: "Commercial EW and airborne ISR.", moat: "Provides EW as a Service (EWaaS).", signal: "Pioneering private-sector adversary air.", hq: "US", stage: "Mature", emp: "500+", founded: "1999", ownership: "Private", itar: "ITAR Compliant" },
          { name: "HawkEye 360", tier: "challenger", tags: ["DUAL"], desc: "Space-based RF mapping.", moat: "Constellation dedicated to RF signal geolocation.", signal: "Critical for dark-ship tracking.", hq: "US", stage: "Series D", emp: "150+", founded: "2015", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "EDGE Group (SIGN4L)", tier: "legacy", tags: ["Deep Tech", "Decision Advantage"], desc: "Electronic warfare and cyber-electromagnetic activities.", moat: "Sovereign IP enabling customized EW without ITAR constraints.", signal: "Aggressively exporting EW capabilities to non-AUKUS allies.", hq: "AE", stage: "Mature", emp: "10K+", founded: "2019", ownership: "Sovereign", itar: "Sovereign Restricted" },
          { name: "Synspective", tier: "challenger", tags: ["DFARS Qualifying", "CMMC L2 Ready", "DUAL", "Deep Tech"], desc: "StriX SAR (Synthetic Aperture Radar) Satellite constellation.", moat: "Small 100kg-class SAR satellites with proprietary deep-learning engine for Dark Vessel Detection.", signal: "High M&A interest for allied space domain situational awareness.", hq: "JP", stage: "Series C", emp: "150+", founded: "2018", ownership: "Venture-Backed", itar: "DFARS Qualifying" }
        ]
      },
      { 
        name: "Alternative PNT", 
        conviction: "high",
        maturity: "Emerging",
        desc: "Navigation without GPS using quantum sensing, visual odometry, or LEO timing.", 
        examples: "Xona Space Systems, Infleqtion",
        tags: ["Deep Tech", "Dual-Use"],
        companies: [
          { name: "Xona Space", tier: "challenger", tags: ["DUAL"], desc: "Commercial satellite navigation network.", moat: "High-power LEO signals resistant to jamming.", signal: "Critical backup to US GPS.", hq: "US", stage: "Series A", emp: "50+", founded: "2019", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "Infleqtion", tier: "challenger", tags: ["DUAL"], desc: "Quantum tech for precision timing.", moat: "Miniaturized quantum clocks.", signal: "Solving the 'GPS-denied' hard problem.", hq: "US", stage: "Series B", emp: "150+", founded: "2007", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "TrustPoint", tier: "challenger", tags: ["DUAL"], desc: "Next-gen GPS infrastructure.", moat: "Commercial LEO PNT signals.", signal: "Seed/Series A traction for GPS alternatives.", hq: "US", stage: "Seed", emp: "20+", founded: "2020", ownership: "Venture-Backed", itar: "Dual-Use Open" }
        ]
      }
    ]
  },
  {
    id: 2,
    name: "Compute & Energy Foundation",
    subtitle: "Edge Processing & Energetics",
    icon: Cpu,
    color: "bg-emerald-900/30 border-emerald-500",
    activeColor: "bg-emerald-800/50 shadow-[0_0_20px_rgba(16,185,129,0.3)] border-emerald-400",
    textColor: "text-emerald-400",
    margin: "High (Deep Tech)",
    capex: "Very High (Foundries/Labs)",
    exit: "IPO / Strategic M&A",
    interest: "Medium (High CAPEX risk)",
    players: ["NVIDIA (Defense)", "Mercury Systems", "Mach Industries", "Hadrian", "Radiant"],
    thesis: "The physical 'brain' and 'metabolism'. Hardware that allows AI to run locally without cloud reliance (rad-hardened chips) and next-gen propulsion/explosives manufacturing.",
    maturity: "Growth",
    finance: { tam: "$45.0B Defense", multiple: "12x - 18x EBITDA", margin: "20-45% Gross", cagr: "9.6%" },
    alphaInsights: [
      { icon: Zap, title: "The Compute Gap", desc: "AI algorithms are useless without localized Teraflops; cloud reliance presents a massive latency and EW vulnerability." },
      { icon: Activity, title: "Lead Time Reduction", desc: "A 'Deep Tech' multiple is applied to companies that compress industrial supply chains via automated or 3D manufacturing." }
    ],
    orientationDesc: "The \"brain\" of the platform. AI cannot rely on the cloud in a war zone because of latency and signal jamming.",
    orientationSegments: "Rad-hardened chips, Edge GPUs, Ruggedized Servers, and Thermal Management systems.",
    orientationThesis: "Hardware that allows AI to run locally on a drone or tank without \"calling home.\"",
    orientationPlayers: [
      { name: "NVIDIA (defense)", signal: "CUDA dominance applied to tactical edge computing" },
      { name: "Mercury Systems", signal: "Legacy incumbent facing pressure from agile startups" },
      { name: "Aethero", signal: "Rad-hardened commercial edge chips for space" }
    ],
    archetype: "Deep-Tech Enablers",
    segments: [
      { 
        name: "Edge AI Hardware", 
        conviction: "high",
        maturity: "Growth",
        desc: "Ruggedized chips/GPUs for high-heat or radiation environments.", 
        examples: "Aethero, Mercury Systems, TakeMe2Space",
        tags: ["Deep Tech", "Dual-Use"],
        companies: [
          { name: "NVIDIA (Defense)", tier: "neoprime", tags: ["DUAL"], desc: "Edge GPUs for autonomous systems.", moat: "CUDA ecosystem dominance.", signal: "The undisputed compute layer for AI.", hq: "US", stage: "Public", emp: "25K+", founded: "1993", ownership: "Public (NVDA)", itar: "Dual-Use Open" },
          { name: "Aethero", tier: "challenger", tags: ["DUAL"], desc: "Space-rated edge computing.", moat: "Rad-hardened commercial off-the-shelf components.", signal: "Enabling AI directly on satellites.", hq: "US", stage: "Seed", emp: "25+", founded: "2022", ownership: "Venture-Backed", itar: "ITAR Compliant" },
          { name: "Mercury Systems", tier: "legacy", tags: [], desc: "Legacy aerospace microelectronics.", moat: "Massive installed base on US platforms.", signal: "Facing pressure from agile startups.", hq: "US", stage: "Public", emp: "2K+", founded: "1981", ownership: "Public (MRCY)", itar: "ITAR Restricted" },
          { name: "Groq", tier: "neoprime", tags: ["DUAL"], desc: "Ultra-fast LPU inference engines.", moat: "Lowest latency for real-time AI processing.", signal: "Massive commercial hype bleeding into Defense.", hq: "US", stage: "Series D", emp: "300+", founded: "2016", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "TakeMe2Space", tier: "challenger", tags: ["Onshore JV Required", "Deep Tech", "DUAL"], desc: "Orbital infrastructure and compute-focused satellites.", moat: "Indigenous vertical integration of compute-native satellite buses in LEO.", signal: "Direct play on the 'Compute Gap' for orbital edge AI.", hq: "IN", stage: "Early Stage", emp: "50+", founded: "2023", ownership: "Private", itar: "Sovereign Restricted" },
          { name: "GITAI", tier: "challenger", tags: ["DFARS Qualifying", "FOCI Mitigated"], desc: "Robotic solutions for space station support and exploration.", moat: "Space-rated robotic manipulators and inchworm arms running trials inside the ISS commercial modules.", signal: "Operating U.S. subsidiary under FOCI mitigation to bid directly on DARPA/NASA contracts.", hq: "JP", stage: "Series B", emp: "50+", founded: "2016", ownership: "Venture-Backed", itar: "ITAR Restricted" }
        ]
      },
      { 
        name: "Energetics & Propulsion", 
        conviction: "high",
        maturity: "Mainstream",
        desc: "New fuel types and modular manufacturing of explosives.", 
        examples: "Mach Industries, X-Bow, EDGE Group",
        tags: ["Deep Tech", "Attritable Mass"],
        companies: [
          { name: "Ursa Major", tier: "neoprime", tags: ["DUAL"], desc: "Independent rocket engine manufacturer.", moat: "Scalable 3D printing of solid/liquid motors.", signal: "Solving the US solid rocket motor shortage.", hq: "US", stage: "Series D", emp: "300+", founded: "2015", ownership: "Venture-Backed", itar: "ITAR Restricted" },
          { name: "Mach Industries", tier: "challenger", tags: [], desc: "Hydrogen-powered energetics.", moat: "Field-synthesizable fuel bypassing logistics lines.", signal: "High-risk, high-reward deep tech.", hq: "US", stage: "Series A", emp: "50+", founded: "2022", ownership: "Venture-Backed", itar: "ITAR Restricted" },
          { name: "X-Bow Systems", tier: "challenger", tags: [], desc: "3D printed solid rocket motors.", moat: "Rapid prototyping of solid propellant.", signal: "Major MOD/Global Defense contracts for solid rocket motors.", hq: "US", stage: "Series B", emp: "100+", founded: "2016", ownership: "Venture-Backed", itar: "ITAR Restricted" },
          { name: "Adranos (Anduril)", tier: "neoprime", tags: ["Attritable Mass"], desc: "High-performance solid rocket motors.", moat: "ALITEC proprietary solid propellant.", signal: "Acquired by Anduril to vertically integrate munitions.", hq: "US", stage: "Acquired", emp: "100+", founded: "2015", ownership: "Anduril Ind.", itar: "ITAR Restricted" },
          { name: "EDGE Group (HALCON)", tier: "legacy", tags: ["Attritable Mass"], desc: "Precision guided munitions and smart weapons manufacturing.", moat: "End-to-end localized manufacturing of modular energetics.", signal: "Rapidly expanding production capacity to meet global munitions shortage.", hq: "AE", stage: "Mature", emp: "10K+", founded: "2019", ownership: "Sovereign", itar: "Sovereign Restricted" }
        ]
      },
      { 
        name: "Advanced Manufacturing", 
        conviction: "low",
        maturity: "Growth",
        desc: "3D printing and automated factories for rapid re-supply.", 
        examples: "Hadrian, Freeform",
        tags: ["Deep Tech", "Attritable Mass"],
        companies: [
          { name: "Hadrian", tier: "challenger", tags: ["DUAL"], desc: "Automated precision manufacturing factories.", moat: "Software-defined factories cutting lead times by 10x.", signal: "Key enabler for the 'Attritable Mass' strategy.", hq: "US", stage: "Series B", emp: "150+", founded: "2020", ownership: "Venture-Backed", itar: "ITAR Compliant" },
          { name: "Freeform", tier: "challenger", tags: ["DUAL"], desc: "AI-driven metal 3D printing.", moat: "Real-time metrology and printing control.", signal: "Unlocking complex aerospace geometries.", hq: "US", stage: "Series A", emp: "50+", founded: "2021", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "Divergent 3D", tier: "challenger", tags: ["DUAL"], desc: "Digital adaptive manufacturing.", moat: "DAPS system for vehicle chassis printing.", signal: "Strong automotive crossover.", hq: "US", stage: "Series C", emp: "200+", founded: "2014", ownership: "Venture-Backed", itar: "Dual-Use Open" }
        ]
      },
      { 
        name: "Tactical Power & Microgrids", 
        conviction: "low",
        maturity: "Emerging",
        desc: "Mobile micro-nuclear reactors and advanced power storage for edge ops.", 
        examples: "Radiant, Oklo",
        tags: ["Deep Tech", "Dual-Use"],
        companies: [
          { name: "Radiant", tier: "challenger", tags: ["DUAL", "DIU"], desc: "Portable micro-nuclear reactors.", moat: "Replaces diesel generators for forward bases.", signal: "Regulatory (NRC) risk is the main hurdle.", hq: "US", stage: "Series B", emp: "80+", founded: "2019", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "Oklo", tier: "neoprime", tags: ["DUAL"], desc: "Fast-fission clean power.", moat: "Backed by Sam Altman, liquid metal cooling.", signal: "Public (SPAC) and targeting massive dual-use TAM.", hq: "US", stage: "Public", emp: "100+", founded: "2013", ownership: "Public (OKLO)", itar: "Dual-Use Open" }
        ]
      }
    ]
  },
  {
    id: 1,
    name: "Physical Edge",
    subtitle: "Attritable Mass & Platforms",
    icon: Rocket,
    color: "bg-slate-800/50 border-slate-500",
    activeColor: "bg-slate-700/80 shadow-[0_0_20px_rgba(148,163,184,0.3)] border-slate-400",
    textColor: "text-slate-300",
    margin: "Medium (Industrial)",
    capex: "High (Factories / Supply Chain)",
    exit: "High (M&A by Legacy Primes)",
    interest: "High (Volume scale)",
    players: ["Anduril", "Saronic", "Skydio", "Firestorm", "Ghost Robotics"],
    thesis: "Moving from 'Exquisite & Expensive' ($100M jets) to 'Mass & Modular' (1,000 drones at $50k each). The focus is on automated manufacturing, low cost, and sheer volume.",
    maturity: "Maturing",
    finance: { tam: "$29.0B C-UAS Q1'26", multiple: "12x - 15x EBITDA", margin: "15-25% Gross", cagr: "13.2%" },
    alphaInsights: [
      { icon: TrendingUp, title: "Volume Economics", desc: "Reversing the historical trend; the market mandate is now 'High Volume, Low Unit Price' vs. exquisite platforms." },
      { icon: Factory, title: "Software-Defined Mfg.", desc: "The platform is merely a delivery vehicle for the software. Expendability and modular manufacturing are the core product features." },
      { icon: Shield, title: "Replicator 2.0 Inversion", desc: "Q1 2026 C-UAS Spend: $29.0B. Massive capital inversion toward fixed-site installation defense and AI interceptors." }
    ],
    orientationDesc: "Focus has dramatically shifted to C-UAS cost-efficiency (Replicator 2.0). With a $29B global spend in Q1 2026, the strategic imperative is driving down the cost-per-interception using reusable AI interceptors (e.g., Fortem, ACS) over legacy, expensive kinetic missiles.",
    orientationSegments: "Micro-drones (UAS), Unmanned Surface Vessels (USVs), Satellite Buses, Robotic Ground Vehicles, and Autonomous Precision Weapons.",
    orientationThesis: "Moving from \"Exquisite & Expensive\" (e.g., a $100M jet) to \"Mass & Modular\" (e.g., 1,000 drones at $50k each).",
    orientationPlayers: [
      { name: "Anduril (Dive-LD)", signal: "Major Australian Navy partnership for XL autonomous subs" },
      { name: "Saronic", signal: "Fastest path to scale in naval surface autonomy" },
      { name: "Allen Control Systems", signal: "U.S. Army contract for autonomous robotic gun systems" }
    ],
    archetype: "Attritable Manufacturers",
    segments: [
      { 
        name: "Aerial Systems (UAS)", 
        conviction: "high",
        maturity: "Mainstream",
        desc: "Autonomous drones for ISR and strike capabilities.", 
        examples: "Skydio, Anduril, EDGE Group, NearthLAB, VORASKY",
        tags: ["Attritable Mass", "Dual-Use"],
        companies: [
          { name: "Skydio", tier: "neoprime", tags: ["DUAL", "DIU"], desc: "AI-piloted small UAS.", moat: "Dominant US alternative to DJI.", signal: "Massive success in Ukraine combat theater.", hq: "US", stage: "Series E", emp: "800+", founded: "2014", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "Firestorm", tier: "challenger", tags: [], desc: "Modular, 3D-printed loitering munitions.", moat: "Printable at the edge (in shipping containers).", signal: "Extreme attritability.", hq: "US", stage: "Seed", emp: "30+", founded: "2022", ownership: "Venture-Backed", itar: "ITAR Restricted" },
          { name: "EDGE Group (ADASI)", tier: "legacy", tags: ["Attritable Mass", "Dual-Use"], desc: "Autonomous aerial systems and loitering munitions.", moat: "Scalable production of low-cost, combat-proven autonomous platforms.", signal: "Major export contracts signed across GCC and international markets.", hq: "AE", stage: "Mature", emp: "10K+", founded: "2019", ownership: "Sovereign", itar: "Sovereign Restricted" },
          { name: "General Atomics", tier: "legacy", tags: [], desc: "Maker of the Reaper drone.", moat: "Entrenched PORs.", signal: "Successfully transitioning with Gambit and XQ-67A autonomous programs.", hq: "US", stage: "Mature", emp: "10K+", founded: "1955", ownership: "Private", itar: "ITAR Restricted" },
          { name: "AeroVironment", tier: "legacy", tags: [], desc: "Tactical UAS and loitering munitions.", moat: "Switchblade program of record.", signal: "Benefiting massively from Ukraine demand.", hq: "US", stage: "Public", emp: "1K+", founded: "1971", ownership: "Public (AVAV)", itar: "ITAR Restricted" },
          { name: "NearthLAB", tier: "challenger", tags: ["Onshore JV Required", "Dual-Use", "Decision Advantage", "CMMC L2 Ready"], desc: "Autonomous aerial intelligence and high-speed C-UAS interceptors.", moat: "Vision-based flight algorithms engineered for GPS-denied, electronically jammed theaters.", signal: "Expanded into kinetic profiles with XAiDEN swarm attack drones and KAiDEN C-UAS interceptors.", hq: "KR", stage: "Growth", emp: "100+", founded: "2015", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "VORASKY", tier: "challenger", tags: ["Attritable Mass", "Dual-Use"], desc: "Manufacturer of unmanned aerial vehicles for defense and industrial applications.", moat: "Dual-use manufacturing serving both industrial logistics and tactical ISR.", signal: "Providing attritable physical platforms for the South Korean defense ecosystem.", hq: "KR", stage: "Growth", emp: "50+", founded: "2019", ownership: "Private", itar: "Dual-Use Open" },
          { name: "Doosan Mobility", tier: "legacy", tags: ["Onshore JV Required", "Deep Tech", "Dual-Use"], desc: "Mobile hydrogen fuel-cell power packs for endurance UAVs.", moat: "Solves the persistent battery bottleneck, drastically extending loitering times.", signal: "Demonstrated sustained >120 minute flight endurance in extreme -20°C combat conditions.", hq: "KR", stage: "Mature", emp: "100+", founded: "2016", ownership: "Corporate Subsidiary", itar: "Dual-Use Open" },
          { name: "Next Aerospace", tier: "challenger", tags: ["Attritable Mass"], desc: "Advanced composite VTOL and high-speed strike airframes.", moat: "Optimized for rapid, distributed launch deployment without runway infrastructure.", signal: "Debuted 25-kg class VTOL UAV and ultra-compact strike drones at Drone Show Korea 2026.", hq: "KR", stage: "Growth", emp: "50+", founded: "2019", ownership: "Private", itar: "ITAR Compliant" }
        ]
      },
      { 
        name: "Maritime & Subsurface (USV/UUV)", 
        conviction: "high",
        maturity: "Growth",
        desc: "Autonomous ships/subs for coastal defense and sensing.", 
        examples: "Saronic, Anduril, Planys Technologies",
        tags: ["Attritable Mass", "Dual-Use"],
        companies: [
          { name: "Saronic", tier: "challenger", tags: ["DIU"], desc: "Autonomous surface vessels (USVs) for maritime dominance.", moat: "Vertically integrated from hull design to proprietary software.", signal: "Raised $1.75B Series D led by Kleiner Perkins at $9.25B valuation (early 2026).", hq: "US", stage: "Series D", emp: "500+", founded: "2022", ownership: "Venture-Backed ($9.25B Valuation)", itar: "ITAR Compliant" },
          { name: "Anduril (Dive-LD)", tier: "neoprime", tags: ["AUKUS"], desc: "Extra-large autonomous submarines.", moat: "Extreme endurance and modular payload bays.", signal: "Major Australian Navy partnership.", hq: "US", stage: "Series F", emp: "2K+", founded: "2017", ownership: "Venture-Backed", itar: "AUKUS Aligned" },
          { name: "Saildrone", tier: "challenger", tags: ["DUAL"], desc: "Unmanned surface vehicles for ocean data.", moat: "Extreme endurance (months at sea) wind/solar powered.", signal: "Major Coast Guard and Navy ISR use.", hq: "US", stage: "Series C", emp: "200+", founded: "2012", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "Planys Technologies", tier: "challenger", tags: ["Attritable Mass", "DUAL"], desc: "Autonomous underwater robots and ROVs for sensing and inspection.", moat: "Ruggedized subsurface inspection at attritable cost.", signal: "Expanding UUV capabilities in GCC maritime domains.", hq: "AE", stage: "Growth", emp: "100+", founded: "2015", ownership: "Private", itar: "Sovereign Restricted" },
          { name: "BAE Systems (Maritime)", tier: "legacy", tags: ["AUKUS"], desc: "Naval ships, submarines, and maritime systems.", moat: "Prime contractor for the UK's nuclear submarine fleet (Astute/Dreadnought) and AUKUS Pillar 1.", signal: "Seeking to integrate autonomous UUVs and smart effectors into legacy hull designs.", hq: "UK", stage: "Public", emp: "35K+", founded: "1999", ownership: "Public (BA.L)", itar: "AUKUS Aligned" }
        ]
      },
      { 
        name: "Unmanned Ground Vehicles (UGVs)", 
        conviction: "medium",
        maturity: "Growth",
        desc: "Robotic quadrupeds and autonomous tracks for base defense and resupply.", 
        examples: "Ghost Robotics, Milrem Robotics",
        tags: ["Attritable Mass", "Dual-Use"],
        companies: [
          { name: "Ghost Robotics", tier: "challenger", tags: ["DUAL", "FOCI Mitigated"], desc: "Quadruped robotics (robot dogs).", moat: "All-terrain, highly durable physical platform.", signal: "Acquired by LIG Nex1 (Korea).", hq: "US", stage: "Acquired", emp: "100+", founded: "2015", ownership: "LIG Nex1 (KR)", itar: "ITAR Compliant" },
          { name: "Milrem Robotics", tier: "legacy", tags: ["NATO", "FOCI Mitigated"], desc: "Tracked autonomous ground vehicles.", moat: "Proven combat logistics platform.", signal: "Acquired by EDGE Group (UAE).", hq: "AE", stage: "Acquired", emp: "200+", founded: "2013", ownership: "EDGE Group (UAE)", itar: "Sovereign Restricted" },
          { name: "Hanwha Aerospace", tier: "legacy", tags: ["Onshore JV Required", "Attritable Mass"], desc: "Arion-SMET multi-purpose UGV.", moat: "Massive manufacturing scale and successful U.S. Marine Corps Oahu trials.", signal: "Aggressively establishing U.S.-based subsidiaries to bypass Buy American Act restrictions.", hq: "KR", stage: "Public", emp: "5K+", founded: "1977", ownership: "Public", itar: "ITAR Compliant" },
          { name: "Hyundai Rotem", tier: "legacy", tags: ["Onshore JV Required", "Attritable Mass"], desc: "HR-Sherpa multipurpose unmanned ground vehicle.", moat: "Heavy defense manufacturing scale backed by the broader Hyundai Motor Group ecosystem.", signal: "Aggressively competing for DAPA's TIGER 4.0 standard future ground-warfare robotic platform.", hq: "KR", stage: "Public", emp: "3K+", founded: "1977", ownership: "Public", itar: "ITAR Compliant" }
        ]
      },
      {
        name: "C-UAS Interception & Fixed-Site Defense",
        conviction: "high",
        maturity: "Emerging",
        desc: "AI-directed kinetic interceptors, reusable drone hunters, and robotic gun turrets capable of autonomous target acquisition, ballistic tracking, and homeland installation defense under Replicator 2.",
        examples: "Anduril, Fortem Technologies, Allen Control Systems",
        tags: ["Attritable Mass", "Decision Advantage"],
        companies: [
          { name: "Anduril (C-UAS)", tier: "neoprime", tags: ["Attritable Mass", "Decision Advantage"], desc: "End-to-end autonomous C-UAS ecosystem.", moat: "Vertical integration of Lattice OS with Roadrunner interceptors.", signal: "Awarded a landmark $20.0B, 10-year firm-fixed-price U.S. Army enterprise contract for its Lattice-enabled C-UAS suite (March 2026); anchors Replicator 2.0 homeland defense.", hq: "US", stage: "Series H", emp: "7,000+", founded: "2017", ownership: "Venture-Backed", itar: "ITAR Restricted" },
          { name: "Fortem Technologies", tier: "challenger", tags: ["Attritable Mass", "Dual-Use"], desc: "Autonomous radar-guided drone interceptors.", moat: "Safe kinetic capture via net for homeland defense without collateral damage.", signal: "Secured initial purchase under Replicator 2 for DroneHunter F700 systems (JIATF 401, Jan 2026); locked into fixed-site installation defense.", hq: "US", stage: "Growth", emp: "150+", founded: "2016", ownership: "Venture-Backed", itar: "ITAR Compliant" },
          { name: "Allen Control Systems", tier: "challenger", tags: ["Decision Advantage", "Attritable Mass"], desc: "Robotic gun systems for autonomous threat defeat.", moat: "Proprietary computer vision for sub-millisecond ballistic tracking and automated target engagement.", signal: "Awarded U.S. Army Bullfrog contracts; computer vision-driven robotic gun turrets replacing legacy kinetic missiles in fixed-site defense.", hq: "US", stage: "Early Stage", emp: "50+", founded: "2020", ownership: "Venture-Backed", itar: "ITAR Restricted" },
          { name: "SpearUAV", tier: "challenger", tags: ["Attritable Mass"], desc: "Encapsulated loitering munitions for infantry.", moat: "Instant tactical deployment from submarines or grunts.", signal: "Strong interest from global naval forces.", hq: "IL", stage: "Series A", emp: "50+", founded: "2017", ownership: "Private", itar: "Sovereign Restricted" },
          { name: "BONE", tier: "challenger", tags: ["Attritable Mass", "Decision Advantage", "Dual-Use"], desc: "AI-driven modular weapon stations and precision fire-control systems for robotic platforms.", moat: "Edge-AI computer vision for real-time ballistic tracking that retrofits onto low-cost platforms.", signal: "Secured 17B KRW led by Third Prime Capital; Rare US-VC bridge to ROK defense ecosystem.", hq: "KR", stage: "Series A", emp: "40+", founded: "2021", ownership: "Third Prime Capital / Private", itar: "Sovereign Restricted" },
          { name: "LIG Defense & Aerospace", tier: "legacy", tags: ["Onshore JV Required", "Attritable Mass", "Deep Tech"], desc: "Comprehensive South Korean defense prime specializing in precision-guided missiles and multi-layered air defense.", moat: "Mass industrial production of low-cost guided interceptors and tactical radars.", signal: "Undergoing active trials with ATLA (Japan) and the U.S. Marine Corps (Hawaii) for trilateral interoperability, and establishing joint ventures to supply Replicator 2.0 with attritable components.", hq: "KR", stage: "Public", emp: "2,000+", founded: "1976", ownership: "Public (KRX: 079550)", itar: "Sovereign Restricted" }
        ]
      },
      { 
        name: "Space & Hypersonics", 
        conviction: "low",
        maturity: "Emerging",
        desc: "LEO constellations and Mach 5+ delivery systems.", 
        examples: "Castelion, Varda, ExoAnalytic",
        tags: ["Deep Tech", "Attritable Mass"],
        companies: [
          { name: "Castelion", tier: "challenger", tags: [], desc: "Low-cost hypersonic glide vehicles.", moat: "Commercial off-the-shelf components for Mach 5+.", signal: "Challenging Lockheed's exquisite hypersonics.", hq: "US", stage: "Seed", emp: "30+", founded: "2023", ownership: "Venture-Backed", itar: "ITAR Restricted" },
          { name: "Varda", tier: "challenger", tags: ["DUAL"], desc: "In-space manufacturing & reentry.", moat: "High-cadence hypersonic reentry testing.", signal: "Leveraging commercial pharma to fund defense testing.", hq: "US", stage: "Series B", emp: "100+", founded: "2020", ownership: "Venture-Backed", itar: "Dual-Use Open" },
          { name: "True Anomaly", tier: "challenger", tags: ["Space Force"], desc: "Space domain awareness and security.", moat: "Jackal autonomous orbital pursuit vehicles.", signal: "Rapidly scaling for US Space Force.", hq: "US", stage: "Series B", emp: "150+", founded: "2022", ownership: "Venture-Backed", itar: "ITAR Compliant" },
          { name: "Hermeus", tier: "challenger", tags: ["DUAL"], desc: "Hypersonic aircraft.", moat: "Chimera turbine-based combined cycle engine.", signal: "Testing Quarterhorse Mach 4+ drone.", hq: "US", stage: "Series B", emp: "150+", founded: "2018", ownership: "Venture-Backed", itar: "ITAR Restricted" },
          { name: "ExoAnalytic (Anduril)", tier: "neoprime", tags: ["Deep Tech"], desc: "Commercial space domain awareness.", moat: "Global network of optical telescopes.", signal: "Acquired by Anduril for space tracking.", hq: "US", stage: "Acquired", emp: "100+", founded: "2008", ownership: "Anduril Ind.", itar: "ITAR Compliant" },
          { name: "ispace", tier: "challenger", tags: ["DFARS Qualifying", "FOCI Mitigated"], desc: "Robotic lunar landers and micro-rovers for commercial lunar transport and resource mapping.", moat: "Dual-headquarter model (JP/US) partnered with Draper to win NASA CLPS payload contracts.", signal: "Key partner for allied space logistics and lunar infrastructure.", hq: "JP", stage: "Public", emp: "200+", founded: "2010", ownership: "Public (TSE: 9348)", itar: "Dual-Use Open" }
        ]
      }
    ]
  }
];

const thesisFilters = [
  "High-Margin IP", 
  "Attritable Mass", 
  "Deep Tech", 
  "Missing Middle", 
  "Dual-Use", 
  "Decision Advantage"
];

const complianceFilters = [
  "DFARS Qualifying",
  "Onshore JV Required",
  "CMMC L2 Ready",
  "DoD IL6 / Secret",
  "FOCI Mitigated",
  "DUAL"
];

const maturityFilters = [
  "Mainstream",
  "Growth",
  "Emerging"
];

const getSortedSegments = (segments) => {
  const order = { "Mainstream": 1, "Growth": 2, "Emerging": 3 };
  return [...segments].sort((a, b) => (order[a.maturity] || 4) - (order[b.maturity] || 4));
};

const getSafeId = (str) => str ? str.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() : '';

const investorProfiles = [
  {
    id: 'vc',
    name: 'Venture Capital',
    role: 'The "Neo-Prime" Hunter',
    icon: Rocket,
    color: 'text-fuchsia-400',
    bg: 'bg-fuchsia-900/30 border-fuchsia-500/50',
    lensDescription: 'Focus on Disruption. Highlights pure-play software, autonomy, and Agentic AI workflows.',
    focus: 'High-risk, high-reward "Zero-to-One" breakthroughs.',
    thesis: 'Betting on companies that can scale from startup to "Neo-Prime" (e.g., the next Anduril or Shield AI) by owning proprietary AI and software stacks that make traditional hardware obsolete.',
    targetLayers: [5, 4],
    multiples: '15x–30x Forward Revenue',
    criticalMetric: 'R&D-to-Revenue Velocity',
    metricDesc: 'The speed at which a company can convert an initial SBIR/OTA prototype grant into a deployed tactical asset.',
    moat: 'Talent density (ex-Big Tech AI/ML engineers) and "un-cloneable" algorithmic performance in GPS-denied environments.'
  },
  {
    id: 'pe',
    name: 'Private Equity',
    role: 'The "Attritable Mass" Consolidator',
    icon: Landmark,
    color: 'text-emerald-400',
    bg: 'bg-emerald-900/30 border-emerald-500/50',
    lensDescription: 'Focus on EBITDA. Highlights attritable mass manufacturing and mature tech roll-ups.',
    focus: 'Scaling, operational efficiency, and platform roll-ups.',
    thesis: 'The shift to "Attritable Mass" requires thousands of units. PE firms are looking for "Metal-Benders 2.0"—companies with automated manufacturing and solid contract backlogs that can be rolled up into a diversified defense industrial platform.',
    targetLayers: [1, 2],
    multiples: '12x–18x EBITDA',
    criticalMetric: 'Program of Record (PoR) Probability',
    metricDesc: 'The stability and longevity of government funding lines, migrating from R&D budgets to Procurement budgets.',
    moat: 'Manufacturing scale, automated supply chain control, and absolute "cost-per-unit" dominance.'
  },
  {
    id: 'ib',
    name: 'Legacy Prime M&A',
    role: 'The "Exit & Synergy" Architect',
    icon: GitMerge,
    color: 'text-indigo-400',
    bg: 'bg-indigo-900/30 border-indigo-500/50',
    lensDescription: 'Focus on Pipeline Replenishment. Navigating technical debt and the software integration gap.',
    focus: 'M&A velocity, IPO readiness, and strategic alignment.',
    thesis: 'Identifying the "connective tissue" that the legacy Big 5 primes (Lockheed, Raytheon, Boeing, etc.) MUST acquire to stay relevant in a software-defined era.',
    targetLayers: [4, 5],
    multiples: 'Revenue vs. EBITDA Arbitrage',
    criticalMetric: 'Integration Surface Area',
    metricDesc: 'The number of distinct, legacy military platforms (e.g., F-35, Stryker, Carrier) the software is actively running on.',
    moat: 'Extreme vendor lock-in achieved through deep, mission-critical software integration.'
  },
  {
    id: 'fo',
    name: 'Sovereign / Allied Buyer',
    role: 'The "Sovereign Alpha" Steward',
    icon: Globe,
    color: 'text-blue-400',
    bg: 'bg-blue-900/30 border-blue-500/50',
    lensDescription: 'Focus on Localization. Eradicating supply chain dependencies and controlling sovereign outcomes.',
    focus: 'Long-term capital preservation, "Dual-Use" protection, and patriotic alignment.',
    thesis: 'Long-term exposure to national security with a "Dual-Use" hedge. If a drone or software company loses a military contract but wins a commercial delivery or enterprise contract, the downside is protected.',
    targetLayers: [3, 2],
    multiples: 'Long-term yield + 5x–10x MOIC',
    criticalMetric: 'Commercial TAM Multiplier',
    metricDesc: 'The realistic percentage of the core technology that is directly applicable and sellable to the private sector.',
    moat: 'Regulatory "moats" (ITAR / FedRAMP compliance) and strategic importance to the Great Power Competition.'
  }
];

const exitCategories = [
  {
    title: "Legacy Primes (Strategic - Technical Debt)",
    icon: Building,
    desc: "Buying to bridge the gap to software-defined warfare and fix massive internal R&D failures.",
    buyers: [
      {
        name: "Lockheed Martin",
        ticker: "LMT",
        gap: "AI-Driven Distributed C2 & Hypersonics",
        urgency: "High",
        targetLayers: [5, 4],
        thesis: "Facing pressure on the F-35 program's software upgrades (TR-3/Block 4). Aggressively seeking AI middleware to link legacy platforms to JADC2 requirements.",
        recentMove: "Investments via LM Ventures in autonomous edge compute."
      },
      {
        name: "RTX (Raytheon)",
        ticker: "RTX",
        gap: "C-UAS & Kinetic Interceptors",
        urgency: "Extreme",
        targetLayers: [3, 4],
        thesis: "Struggling with the cost-exchange ratio of their exquisite effectors (Patriot/Standard Missiles). Desperate for cheap C-UAS swarms and advanced radar fusion.",
        recentMove: "Focusing heavily on counter-UAS (C-UAS) software."
      },
      {
        name: "Boeing",
        ticker: "BA",
        gap: "Autonomous Flight Software",
        urgency: "Critical",
        targetLayers: [1, 5],
        thesis: "Suffering massive reputational and manufacturing technical debt. Needs to acquire software-first autonomy to compete for the Collaborative Combat Aircraft (CCA) program.",
        recentMove: "Integrating AI pilots into the MQ-28 Ghost Bat platform."
      },
      {
        name: "Northrop Grumman",
        ticker: "NOC",
        gap: "Space Networking & Edge Compute",
        urgency: "Medium",
        targetLayers: [3, 2],
        thesis: "Pivoting hard into space domain awareness and proliferated LEO architectures. Searching for optical (laser) comms and mesh networking startups.",
        recentMove: "Securing massive Space Development Agency (SDA) contracts."
      },
      {
        name: "General Dynamics",
        ticker: "GD",
        gap: "Unmanned Ground Vehicles (UGVs) & Kinetic Autonomy",
        urgency: "Medium",
        targetLayers: [1, 4],
        thesis: "The king of land systems (Stryker/Abrams) needs to robotize its fleet. Actively looking for autonomy software that can be 'brain swapped' into heavy armor, alongside robotic effectors.",
        recentMove: "Developing the 'StrykerX' tech demonstrator with hybrid AI."
      },
      {
        name: "BAE Systems",
        ticker: "BA.L",
        gap: "Autonomous Undersea Warfare & Electronic Warfare (EW)",
        urgency: "High",
        targetLayers: [5, 1],
        thesis: "Industrial anchor for AUKUS Pillar 1 (Submarines). Desperate for autonomy software to integrate into legacy maritime hulls and 6th-gen fighter programs (GCAP).",
        recentMove: "FalconWorks and Digital Intelligence divisions actively scouting '2.0' startups for GCAP integration."
      },
      {
        name: "LIG Defense & Aerospace",
        ticker: "KRX: 079550",
        gap: "Agentic Autonomy (L5) & JADC2 Interoperable Middleware (L4)",
        urgency: "High",
        targetLayers: [5, 4],
        thesis: "As a Legacy Prime, LIG lacks native, software-first Defense Tech 2.0 capabilities (L4/L5). Their strategic M&A thesis is dual-track: First, they must acquire or partner with U.S. Layer 4/5 software pure-plays to wrap their legacy Korean guided rockets in modern, open-architecture 'brains.' Second, to bypass Buy American Act (BAA) and non-DFARS trade barriers on completed systems, they must acquire U.S. physical platform shells (Layer 1) to act as compliant, onshore delivery vehicles for their high-quality domestic components.",
        recentMove: "Executed the landmark acquisition of U.S.-based Ghost Robotics (L1) to serve as their compliant onshore U.S. vehicle. To bridge their software deficit, they signed a major MOU with Palantir Technologies (L4/L5) in March 2026 to integrate data-driven targeting suites directly into their Middle East-deployed air defense systems."
      }
    ]
  },
  {
    title: "Big Tech & Neo-Primes (Strategic - Ecosystem)",
    icon: Server,
    desc: "Buying to protect cloud moats and force vertical ecosystem integration.",
    buyers: [
      {
        name: "Cloud Titans (MSFT, AMZN)",
        ticker: "Big Tech",
        gap: "Edge Sensors & Hardware Integration",
        urgency: "High",
        targetLayers: [4, 5],
        thesis: "Owning the IL6/Secret cloud isn't enough. They acquire the 'eyes and ears' (sensors/AI) to ensure battlefield data stays natively locked in their server ecosystem.",
        recentMove: "Massive strategic partnerships and investments in AI targeting."
      },
      {
        name: "Neo-Primes (Palantir, Anduril)",
        ticker: "Defense 2.0",
        gap: "Integration Surface Area",
        urgency: "Extreme",
        targetLayers: [1, 4],
        thesis: "Buying niche software to expand AIP's reach, or acquiring hardware (drones/subs) to prove their autonomy software (Lattice) can fly/drive literally anything.",
        recentMove: "Anduril aggressively acquiring hardware assets like Dive-LD."
      }
    ]
  },
  {
    title: "Sovereign Wealth & Private Equity (Financial)",
    icon: Landmark,
    desc: "Buying for cash flow consolidation (PE) or strategic IP localization (SWFs).",
    buyers: [
      {
        name: "Sovereign Wealth (e.g., PIF, Mubadala)",
        ticker: "SWF",
        gap: "Defense Localization & IP",
        urgency: "High",
        targetLayers: [1, 2, 3],
        thesis: "Acting as the 'Bridge to Scale'. Willing to pay strategic premiums for companies that agree to localize manufacturing and transfer IP to their sovereign borders.",
        recentMove: "EDGE Group (UAE) acquiring Milrem Robotics to build a national champion."
      },
      {
        name: "Mid-Market Private Equity",
        ticker: "PE",
        gap: "Fragmented Supply Chains",
        urgency: "High",
        targetLayers: [1, 2],
        thesis: "Rolling up 'attritable mass' metal-benders. Buying fragmented Layer 1/2 hardware companies to build scaled Tier 1 suppliers with 12x-15x EBITDA exits.",
        recentMove: "Aggressive roll-ups of defense electronics, PCB manufacturing, and CNC."
      }
    ]
  }
];

// --- KNOWLEDGE RIDGE EXPERTS ---
const krExperts = [
  { id: '01', role: 'Former DARPA Program Manager', domain: 'Hypersonics & Directed Energy', isLead: true },
  { id: '02', role: 'Ex-Director, J-8 (Joint Staff)', domain: 'Requirements & Capability', isLead: true },
  { id: '03', role: 'Former Chief Architect, Space Force', domain: 'Proliferated LEO & Transport', isLead: false },
  { id: '04', role: 'Retired SOCOM Commander', domain: 'Tactical Autonomy & UAS', isLead: false },
  { id: '05', role: 'Ex-DIU Portfolio Director', domain: 'Dual-Use & Commercial Tech', isLead: false },
  { id: '06', role: 'Former CISO, Dept. of Navy', domain: 'Zero-Trust & Cyber Warfare', isLead: false },
  { id: '07', role: 'Ex-Raytheon Chief Engineer', domain: 'Sensor Fusion & Radars', isLead: false },
  { id: '08', role: 'Former Pentagon Comptroller', domain: 'PoR Budgeting & Procurement', isLead: false },
  { id: '09', role: 'Retired NSA Cyber Operator', domain: 'Electronic Warfare (EW)', isLead: false },
  { id: '10', role: 'Ex-Lockheed Skunk Works Lead', domain: 'Advanced Manufacturing', isLead: false }
];

export default function DefenseTechSectorBrain() {
  const [activeLayer, setActiveLayer] = useState(layers[0]);
  const [stackViewMode, setStackViewMode] = useState('orientation');
  const [activeView, setActiveView] = useState('cover');
  const [activeCompany, setActiveCompany] = useState(null);
  const [activeInvestor, setActiveInvestor] = useState(investorProfiles[0]); 
  const [activeExitCategory, setActiveExitCategory] = useState(exitCategories[0]);
  const [activeBuyer, setActiveBuyer] = useState(exitCategories[0].buyers[0]);
  const [activeMapFilter, setActiveMapFilter] = useState('All');
  const [activeFilterType, setActiveFilterType] = useState('thesis'); // 'thesis' | 'maturity'
  const [activeGeoFilter, setActiveGeoFilter] = useState('Global');
  const [activeComplianceFilter, setActiveComplianceFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [userMandate, setUserMandate] = useState('');
  const [firmName] = useState(''); // Mock auto-populated from login
  const [isTuningOpen, setIsTuningOpen] = useState(false);
  const [isMapCompact, setIsMapCompact] = useState(false);
  const [collapsedLayers, setCollapsedLayers] = useState([]);
  const [isInquiryOpen, setIsInquiryOpen] = useState(false);
  const [krUpsellActive, setKrUpsellActive] = useState(false);
  const [isBooting, setIsBooting] = useState(false);
  const [bootStep, setBootStep] = useState(0);
  const [isAdvisoryNetworkOpen, setIsAdvisoryNetworkOpen] = useState(false);
  const [highlightedSegment, setHighlightedSegment] = useState(null); // Deep-dive state persistence
  const [mnaTab, setMnaTab] = useState('landscape'); // 'landscape' | 'casestudy'
  const [isTldrExpanded, setIsTldrExpanded] = useState(false);

  // --- AGENT STATE & REFS ---
  const [isAgentOpen, setIsAgentOpen] = useState(false);
  const [agentQuery, setAgentQuery] = useState('');
  const [chatHistory, setChatHistory] = useState([
    { role: 'agent', text: 'I am the Frontier Strategy Agent. I can explain structural theses, ecosystem positioning, and isolate specific segments. How can I help?' }
  ]);
  const chatEndRef = useRef(null);

  // --- AGENT EFFECTS ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsAgentOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (isAgentOpen) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory, isAgentOpen]);

  // --- AGENT LOGIC ENGINE ---
  const handleAgentSubmit = (e, presetQuery = null) => {
    if (e) e.preventDefault();
    const query = presetQuery || agentQuery;
    if (!query.trim()) return;

    setChatHistory(prev => [...prev, { role: 'user', text: query }]);
    setAgentQuery('');

    setTimeout(() => {
      const lowerQuery = query.toLowerCase();
      let response = null;

      // PART A: GLOBAL SCOPING (Geographic Filters)
      const geoKeywords = {
        "gcc": "GCC", "mena": "GCC", "middle east": "GCC",
        "aukus": "AUKUS",
        "nato": "NATO", "europe": "NATO",
        "israel": "Israel",
        "indo-pacific": "IndoPac", "asia": "IndoPac"
      };

      let matchedGeoKey = Object.keys(geoKeywords).find(key => lowerQuery.includes(key));
      
      if (matchedGeoKey && (lowerQuery.includes('show') || lowerQuery.includes('player') || lowerQuery.includes('map') || lowerQuery.includes('filter') || lowerQuery.includes('who'))) {
        const geoId = geoKeywords[matchedGeoKey];
        const geoLabel = geoFilters.find(f => f.id === geoId)?.label || geoId;
        
        response = {
          role: 'agent',
          text: (
            <div className="flex flex-col gap-2.5 w-full">
              <span className="text-[10px] font-black uppercase tracking-widest text-emerald-400 border-b border-slate-700/50 pb-1 mb-1 flex items-center gap-1.5">
                <Globe className="w-3 h-3" /> Geographic Scoping
              </span>
              <p className="text-xs text-slate-200 leading-relaxed">I have filtered the Target Universe to the <strong className="text-white">{geoLabel}</strong> region. You can see the matching players and their stack positions below.</p>
            </div>
          ),
          action: { 
            label: `View ${geoLabel} Map`, 
            onClick: () => { 
              setActiveGeoFilter(geoId);
              setActiveView('ecosystem');
              setSearchQuery('');
              setIsAgentOpen(false); 
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } 
          }
        };
      }

      // PART B: KNOWLEDGE BASE (Takes)
      if (!response) {
        const takes = {
          "anduril": { name: "Anduril", layer: "L4: Interoperability / L1: Edge", moat: "Lattice OS & Vertical Integration", logic: "The defining Neo-Prime. Executing a reverse-playbook by dominating the software layer and acquiring hardware shells to deploy it on." },
          "palantir": { name: "Palantir", layer: "L4: Data Fusion", moat: "AIP & Ontology", logic: "Entrenched in JADC2 and Project Maven. The benchmark for SaaS multiples (20x-30x) in the defense sector." },
          "shield ai": { name: "Shield AI", layer: "L5: Mission Intelligence", moat: "Hivemind Autonomy", logic: "Positioning to be the hardware-agnostic 'Windows of Autonomy'. Extreme high-margin software play." },
          "helsing": { name: "Helsing", layer: "L5: Mission Intelligence", moat: "European Prime Integration", logic: "The NATO/European champion for software-only AI. Crucial for allied interoperability without relying on US monopolies." },
          "saronic": { name: "Saronic", layer: "L1: Physical Edge (Maritime)", moat: "Vertically Integrated USVs", logic: "Fastest path to scale in USVs. Their integrated approach allows rapid iterative cycles compared to legacy naval shipbuilders." },
          "spacex": { name: "SpaceX / Starshield", layer: "L3: Transport Mesh", moat: "Launch Economics & LEO Scale", logic: "Effectively monopolizing military-grade proliferated LEO transport. Unmatched launch costs provide an insurmountable moat." }
        };

        let matchedEntityKey = Object.keys(takes).find(key => lowerQuery.includes(key));
        let matchedEntity = matchedEntityKey ? takes[matchedEntityKey] : null;

        if (matchedEntity) {
          let foundComp = null;
          layers.forEach(l => l.segments.forEach(s => {
             if(s.companies) {
                const comp = s.companies.find(c => c.name.toLowerCase().includes(matchedEntityKey));
                if(comp) foundComp = comp;
             }
          }));

          response = {
            role: 'agent',
            text: (
              <div className="flex flex-col gap-2.5 w-full">
                <span className="text-[10px] font-black uppercase tracking-widest text-teal-400 border-b border-slate-700/50 pb-1 mb-1 flex items-center gap-1.5">
                  <Crosshair className="w-3 h-3" /> Entity Audit: {matchedEntity.name}
                </span>
                <p className="text-xs text-slate-200 leading-relaxed"><strong className="text-slate-500 uppercase tracking-widest text-[9px] block mb-0.5">Position</strong> {matchedEntity.layer}</p>
                <p className="text-xs text-slate-200 leading-relaxed"><strong className="text-slate-500 uppercase tracking-widest text-[9px] block mb-0.5">Moat</strong> {matchedEntity.moat}</p>
                <p className="text-xs text-slate-200 leading-relaxed"><strong className="text-slate-500 uppercase tracking-widest text-[9px] block mb-0.5">Alpha Logic</strong> {matchedEntity.logic}</p>
              </div>
            ),
            action: { label: 'Open Entity Profile', onClick: () => { 
              if(foundComp) {
                setActiveCompany(foundComp);
                setIsAgentOpen(false); // Smooth UX
              } 
            } }
          };
        }
      }

      // PART C: FUZZY SEGMENT MATCHER (Smart Tokenization)
      if (!response) {
        let matchedSegment = null;
        let matchedLayerId = null;
        
        // 1. Remove conversational filler words
        const stopWords = ['show', 'me', 'the', 'segment', 'find', 'target', 'about', 'what', 'is', 'are', 'in', 'of', 'a', 'an', 'and', 'to', 'can', 'you', 'explain', 'detail', 'map'];
        const queryTokens = lowerQuery.split(/[^a-z0-9]/).filter(t => t.length > 2 && !stopWords.includes(t));
        
        let bestMatch = null;
        let highestScore = 0;

        for (const layer of layers) {
          for (const segment of layer.segments) {
            // 2. Tokenize segment name
            const segTokens = segment.name.toLowerCase().split(/[^a-z0-9]/).filter(t => t.length > 2);
            let score = 0;
            
            // 3. Calculate overlap
            for (const qt of queryTokens) {
               if (segTokens.includes(qt) || segTokens.some(st => st.includes(qt) || qt.includes(st))) {
                  score++;
               }
            }
            
            // 4. Legacy strict match bonus (maintains existing functionality for exact matches)
            const qClean = getSafeId(lowerQuery);
            const sClean = getSafeId(segment.name);
            if (qClean.includes(sClean) || sClean.includes(qClean)) score += 5;

            // 5. Keep the highest scoring match
            if (score > highestScore && score > 0) {
               highestScore = score;
               bestMatch = { segment, layerId: layer.id };
            }
          }
        }

        if (bestMatch) {
            matchedSegment = bestMatch.segment;
            matchedLayerId = bestMatch.layerId;
        }

        if (matchedSegment) {
          response = {
            role: 'agent',
            text: (
              <div className="flex flex-col gap-2.5 w-full">
                <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400 border-b border-slate-700/50 pb-1 mb-1 flex items-center gap-1.5">
                  <LayoutGrid className="w-3 h-3" /> Segment Identified
                </span>
                <p className="text-xs text-slate-200 leading-relaxed"><strong className="text-white">{matchedSegment.name}</strong></p>
                <p className="text-xs text-slate-400 leading-relaxed">{matchedSegment.desc}</p>
              </div>
            ),
            action: { 
                label: 'Locate on Ecosystem Map', 
                onClick: () => { 
                    setActiveView('ecosystem'); 
                    setActiveMapFilter('All'); // Clear filters to ensure segment is visible
                    setSearchQuery('');
                    setIsAgentOpen(false); // Smooth UX
                    setTimeout(() => {
                        const el = document.getElementById(`ecosystem-segment-${getSafeId(matchedSegment.name)}`);
                        if(el) {
                            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            const header = el.querySelector('h4');
                            if (header) {
                                // Apply a temporary highlight glow to guide the user's eyes
                                header.classList.add('text-cyan-400', 'border-cyan-500');
                                header.classList.remove('text-slate-400', 'border-slate-800/60');
                                setTimeout(() => {
                                    header.classList.remove('text-cyan-400', 'border-cyan-500');
                                    header.classList.add('text-slate-400', 'border-slate-800/60');
                                }, 3000);
                            }
                        }
                    }, 150);
                } 
            }
          };
        }
      }

      // DEFAULT FALLBACK
      if (!response) {
        response = {
          role: 'agent',
          text: 'I am scoped to the Defense Tech 2.0 Twin. Try asking about a specific Neo-Prime (like Anduril or Palantir), a segment (like Autonomous Cyber Warfare), or a geographic region (like GCC or AUKUS).'
        };
      }

      setChatHistory(prev => [...prev, response]);
    }, 600);
  };

  const councilScrollRef = useRef(null);
  
  const scrollCouncil = (direction) => {
    if (councilScrollRef.current) {
      const cardWidth = councilScrollRef.current.firstElementChild?.clientWidth || 350;
      const gap = 24; // gap-6 equals 24px
      councilScrollRef.current.scrollBy({ left: direction * (cardWidth + gap), behavior: 'smooth' });
    }
  };

  // --- DYNAMIC INTELLIGENCE COUNTERS ---
  const visibleSegmentsCount = layers.reduce((acc, layer) => {
    return acc + layer.segments.filter(segment => {
      if (activeMapFilter === 'All') return true;
      if (activeFilterType === 'thesis') return segment.tags.includes(activeMapFilter);
      return segment.maturity === activeMapFilter;
    }).length;
  }, 0);

  const visibleCompaniesCount = layers.reduce((acc, layer) => {
    return acc + layer.segments.reduce((segAcc, segment) => {
       return segAcc + (segment.companies ? segment.companies.filter(company => {
          const isGeoFilteredOut = activeGeoFilter !== 'Global' && !geoFilters.find(f => f.id === activeGeoFilter)?.codes.includes(company.hq);
          const isSearchFilteredOut = searchQuery && !company.name.toLowerCase().includes(searchQuery.toLowerCase());
          const isComplianceFilteredOut = activeComplianceFilter !== 'All' && !company.tags.includes(activeComplianceFilter) && company.itar !== activeComplianceFilter;
          return !(isGeoFilteredOut || isSearchFilteredOut || isComplianceFilteredOut);
       }).length : 0);
    }, 0);
  }, 0);

  const handleMandateSelect = (investorId) => {
    const profile = investorProfiles.find(p => p.id === investorId);
    if (profile) {
      setActiveInvestor(profile);
      setUserMandate(profile.name);
    }
  };

  const handleAccessClick = () => {
    setIsBooting(true);
    setBootStep(0);
    setTimeout(() => setBootStep(1), 500);
    setTimeout(() => setBootStep(2), 1100);
    setTimeout(() => setBootStep(3), 1700);
    setTimeout(() => {
      setIsBooting(false);
      setActiveView('exec_summary');
    }, 2400);
  };

  const navItems = [
    { id: 'exec_summary', num: '01', label: 'Strategic Synthesis', icon: FileText },
    { id: 'setup', num: '02', label: 'Strategic Drivers', icon: TrendingUp },
    { id: 'landscape', num: '03', label: 'Market Structure', icon: Layers },
    { id: 'map', num: '04', label: 'Segment Analysis', icon: LayoutGrid },
    { id: 'ecosystem', num: '05', label: 'Market Map', icon: Globe },
    { id: 'investor', num: '06', label: 'Funding Landscape', icon: PieChart },
    { id: 'mna', num: '07', label: 'Strategic M&A', icon: GitMerge },
    { id: 'execution', num: '08', label: 'Execution Roadmap', icon: Briefcase },
    { id: 'allied_case', num: '09', label: 'Allied Market Entry', icon: BookOpen }
  ];

  const getTokenStyle = (tier) => {
    switch(tier) {
      case 'neoprime': return 'bg-blue-900/40 border-blue-400 text-blue-50 shadow-[0_0_12px_rgba(59,130,246,0.3)] hover:bg-blue-800/60 hover:shadow-[0_0_20px_rgba(59,130,246,0.5)]';
      case 'challenger': return 'bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700 hover:border-slate-400';
      case 'legacy': return 'bg-transparent border-slate-700 text-slate-400 border-dashed hover:border-slate-500 hover:text-slate-300';
      default: return 'bg-slate-800 border-slate-600 text-slate-300';
    }
  };

  const getTagStyle = (tag) => {
    switch(tag) {
      case 'High-Margin IP': return 'bg-indigo-900/30 border-indigo-500/40 text-indigo-300';
      case 'Attritable Mass': return 'bg-emerald-900/30 border-emerald-500/40 text-emerald-300';
      case 'Deep Tech': return 'bg-rose-900/30 border-rose-500/40 text-rose-300';
      case 'Missing Middle': return 'bg-blue-900/30 border-blue-500/40 text-blue-300';
      case 'Dual-Use': return 'bg-teal-900/30 border-teal-500/40 text-teal-300';
      case 'Decision Advantage': return 'bg-purple-900/30 border-purple-500/40 text-purple-300';
      case 'DFARS Qualifying': return 'bg-amber-900/30 border-amber-500/40 text-amber-300';
      case 'Onshore JV Required': return 'bg-orange-900/30 border-orange-500/40 text-orange-300';
      case 'CMMC L2 Ready': return 'bg-emerald-900/30 border-emerald-500/40 text-emerald-400';
      case 'DoD IL6 / Secret': return 'bg-blue-900/40 border-blue-500/40 text-blue-300 shadow-[0_0_10px_rgba(59,130,246,0.2)]';
      case 'FOCI Mitigated': return 'bg-rose-900/30 border-rose-500/40 text-rose-300';
      default: return 'bg-slate-900 border-slate-700/50 text-slate-400';
    }
  };

  const getMaturityBadgeStyle = (maturity) => {
    switch(maturity) {
      case 'Mainstream': return 'bg-blue-900/30 border-blue-500/30 text-blue-400';
      case 'Growth': return 'bg-emerald-900/30 border-emerald-500/30 text-emerald-400';
      case 'Emerging': return 'bg-amber-900/30 border-amber-500/30 text-amber-400';
      default: return 'bg-slate-800 border-slate-700 text-slate-400';
    }
  };

  const getLayerBarColors = (layerId) => {
    switch(layerId) {
      case 5: return { tam: 'bg-indigo-600', margin: 'bg-indigo-900/40 border border-indigo-500', text: 'text-indigo-300', border: 'border-indigo-800' };
      case 4: return { tam: 'bg-blue-600', margin: 'bg-blue-900/40 border border-blue-500', text: 'text-blue-300', border: 'border-blue-800' };
      case 3: return { tam: 'bg-teal-600', margin: 'bg-teal-900/40 border border-teal-500', text: 'text-teal-300', border: 'border-teal-800' };
      case 2: return { tam: 'bg-emerald-600', margin: 'bg-emerald-900/40 border border-emerald-500', text: 'text-emerald-300', border: 'border-emerald-800' };
      case 1: return { tam: 'bg-slate-600', margin: 'bg-slate-800/40 border border-slate-500', text: 'text-slate-300', border: 'border-slate-700' };
      default: return { tam: 'bg-slate-600', margin: 'bg-slate-800/40 border border-slate-500', text: 'text-slate-300', border: 'border-slate-700' };
    }
  };

  const getHeatmapStyle = (layerId, conviction) => {
    const isAlpha = conviction === 'high';
    const isSecondary = conviction === 'medium';
    
    switch(layerId) {
      case 5: 
        if(isAlpha) return 'bg-indigo-600 text-white border-indigo-400 shadow-[0_0_15px_rgba(79,70,229,0.3)]';
        if(isSecondary) return 'bg-indigo-950/40 text-indigo-300/70 border-indigo-900/50';
        return 'bg-[#0a0f1a]/80 text-slate-500 border-slate-800/60';
      case 4:
        if(isAlpha) return 'bg-blue-600 text-white border-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.3)]';
        if(isSecondary) return 'bg-blue-950/40 text-blue-300/70 border-blue-900/50';
        return 'bg-[#0a0f1a]/80 text-slate-500 border-slate-800/60';
      case 3:
        if(isAlpha) return 'bg-teal-600 text-white border-teal-400 shadow-[0_0_15px_rgba(20,184,166,0.3)]';
        if(isSecondary) return 'bg-teal-950/40 text-teal-300/70 border-teal-900/50';
        return 'bg-[#0a0f1a]/80 text-slate-500 border-slate-800/60';
      case 2:
        if(isAlpha) return 'bg-emerald-600 text-white border-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.3)]';
        if(isSecondary) return 'bg-emerald-950/40 text-emerald-300/70 border-emerald-900/50';
        return 'bg-[#0a0f1a]/80 text-slate-500 border-slate-800/60';
      case 1:
        if(isAlpha) return 'bg-slate-600 text-white border-slate-400 shadow-[0_0_15px_rgba(100,116,139,0.3)]';
        if(isSecondary) return 'bg-slate-800/60 text-slate-300/70 border-slate-700/50';
        return 'bg-[#0a0f1a]/80 text-slate-500 border-slate-800/60';
      default:
        return 'bg-[#0a0f1a]/80 text-slate-500 border-slate-800/60';
    }
  };

  const renderStrategicLensModal = () => {
    if (!isTuningOpen) return null;
    return (
      <div className="fixed inset-0 z-[250] flex justify-end bg-[#0a0f1a]/80 backdrop-blur-sm animate-fade-in" onClick={() => setIsTuningOpen(false)}>
        <div className="bg-[#0B1120] border-l border-slate-800/80 w-full max-w-[420px] h-full shadow-[-20px_0_60px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden relative" onClick={e => e.stopPropagation()}>
           {/* Header */}
           <div className="p-6 border-b border-slate-800/60 flex justify-between items-start shrink-0">
             <div className="flex gap-3">
               <SlidersHorizontal className="w-6 h-6 text-teal-400 mt-1 shrink-0" />
               <div>
                 <h2 className="text-xl font-bold text-white mb-1">Strategic Lens</h2>
                 <p className="text-sm text-slate-400 leading-snug">Configure the twin's valuation logic and highlighting.</p>
               </div>
             </div>
             <button onClick={() => setIsTuningOpen(false)} className="p-2 bg-slate-800/40 hover:bg-slate-700 rounded-xl text-slate-400 transition-colors shrink-0">
               <X className="w-5 h-5" />
             </button>
           </div>
           
           {/* Body */}
           <div className="p-6 space-y-4 bg-[#0a0f1a]/40 flex-1 overflow-y-auto custom-scrollbar">
             {investorProfiles.map(profile => {
               const ProfileIcon = profile.icon;
               const isSelected = activeInvestor?.id === profile.id;
               return (
                 <button
                   key={profile.id}
                   onClick={() => handleMandateSelect(profile.id)}
                   className={`w-full text-left p-4 rounded-xl border transition-all duration-200 flex items-start gap-4 group ${
                     isSelected
                       ? 'bg-slate-800/90 border-slate-600 shadow-[0_0_20px_rgba(255,255,255,0.05)]'
                       : 'bg-[#151c2d] border-transparent hover:border-slate-700 hover:bg-slate-800/50'
                   }`}
                 >
                    <div className={`p-3 rounded-full bg-slate-900/80 shrink-0 border border-slate-800 transition-colors group-hover:border-slate-700`}>
                      <ProfileIcon className={`w-5 h-5 ${profile.color}`} />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-[15px] mb-1">{profile.name}</h4>
                      <p className="text-[13px] text-slate-400 leading-relaxed pr-2">{profile.lensDescription}</p>
                    </div>
                 </button>
               );
             })}
           </div>

           {/* Footer */}
           <div className="p-6 bg-[#0B1120] border-t border-slate-800/60 shrink-0">
             <button
               onClick={() => {
                 if (!userMandate) setUserMandate(activeInvestor.name);
                 setIsTuningOpen(false);
               }}
               className="w-full py-3.5 bg-[#14b8a6] hover:bg-[#0d9488] text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(20,184,166,0.3)] hover:shadow-[0_0_30px_rgba(20,184,166,0.5)] text-[15px] tracking-wide"
             >
               <CheckCircle2 className="w-5 h-5" /> APPLY LENS CONFIGURATION
             </button>
           </div>
        </div>
      </div>
    );
  };

  const renderContextWidget = () => (
    <div className="absolute top-3 right-3 md:top-4 md:right-4 z-[90] flex flex-col items-end">
      {!userMandate && (
        <button 
          onClick={() => setIsTuningOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0B1120]/90 border border-slate-700/60 rounded-full text-slate-400 hover:text-teal-400 hover:border-teal-500/50 transition-all shadow-[0_0_15px_rgba(0,0,0,0.5)] backdrop-blur-md text-[9px] sm:text-[10px] font-mono tracking-widest uppercase group relative"
        >
          <SlidersHorizontal className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" /> Strategic Lens
        </button>
      )}

      {userMandate && (
        <div 
          onClick={() => setIsTuningOpen(true)}
          className="flex items-center gap-2 lg:gap-3 bg-[#0B1120] border border-slate-700/80 px-3 lg:px-4 py-1.5 rounded-full shadow-[0_0_20px_rgba(0,0,0,0.4)] cursor-pointer hover:border-slate-500 transition-colors group relative"
        >
          <Fingerprint className="w-3.5 h-3.5 text-teal-400 shrink-0" />
          <div className="flex items-center gap-1.5 lg:gap-2 text-[9px] lg:text-[10px] font-mono tracking-widest overflow-hidden">
            <span className="text-slate-400 uppercase shrink-0">LENS:</span>
            <span className="text-teal-400 font-bold truncate max-w-[150px] lg:max-w-[250px] uppercase">{userMandate}</span>
          </div>
          <div className="flex items-center gap-2 pl-1 shrink-0 border-l border-slate-700">
            <SlidersHorizontal className="w-3 h-3 text-slate-500 group-hover:text-teal-400 transition-colors" />
          </div>
        </div>
      )}
    </div>
  );

  if (activeView === 'cover') {
    return (
      <div className="min-h-screen bg-[#0a0f1a] text-slate-200 font-sans flex flex-col relative overflow-x-hidden selection:bg-blue-500/30">
        
        {/* Agentic Boot Sequence Overlay */}
        {isBooting && (
          <div className="absolute inset-0 z-[200] bg-[#0a0f1a] flex flex-col items-center justify-center p-8 transition-opacity duration-500">
            <div className="flex flex-col items-center max-w-sm w-full animate-fade-in-up">
              
              {/* Brand Signature */}
              <div className="flex items-center gap-5 mb-12">
                <div className="p-3.5 rounded-2xl bg-[#212736] border border-slate-700/50 shadow-[0_0_30px_rgba(99,102,241,0.15)]">
                  <Command size={36} className="text-indigo-400" strokeWidth={2} />
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-4xl font-black tracking-[0.25em] text-white uppercase leading-none">FRONTIER</span>
                  <span className="text-[10px] text-slate-500 font-mono tracking-widest uppercase mt-2">Strategy &amp; Agents</span>
                </div>
              </div>

              {/* Minimalist Progress Indicator */}
              <div className="w-full h-[2px] bg-slate-800 mb-8 relative overflow-hidden rounded-full">
                <div 
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-indigo-600 to-cyan-400 transition-all duration-300 ease-out"
                  style={{ width: `${(bootStep / 3) * 100}%` }}
                />
              </div>

              {/* Elegant Advisory Status Text */}
              <div className="h-6 flex items-center justify-center">
                <span className="text-xs md:text-sm font-mono text-slate-400 tracking-[0.2em] uppercase transition-opacity duration-300">
                  {bootStep === 0 && "Verifying Strategic Mandate..."}
                  {bootStep === 1 && "Aggregating Operational Stack..."}
                  {bootStep === 2 && "Synthesizing Target Universe..."}
                  {bootStep >= 3 && <span className="text-indigo-400 font-bold">Opening Strategic Review...</span>}
                </span>
              </div>
              
            </div>
          </div>
        )}

        {/* Subtle Background Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-[120px] pointer-events-none" />

        {renderContextWidget()}
        {renderStrategicLensModal()}
        
        {/* Main Content Area - Flex-1 pushes footer down safely */}
        <div className="flex-1 flex flex-col items-center justify-center p-6 w-full max-w-5xl mx-auto relative z-10 pt-16">
          <div className="flex flex-col items-center animate-fade-in-up w-full -mt-12 lg:-mt-24">
            
            {/* Clean Eyebrow: Report Type & Date */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900/50 border border-slate-700/50 text-[10px] font-mono text-[#14b8a6] font-bold tracking-widest uppercase mb-8 shadow-sm">
              <Target className="w-3.5 h-3.5" /> June 2026 | Strategic Outlook
            </div>

            {/* Power Title Stack - Adjusted scaling to prevent word wrapping */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5rem] font-black text-white uppercase tracking-tighter leading-[1.05] mb-6 flex flex-col items-center text-center w-full">
              <span>Defense Tech 2.0</span>
              <span>The Architecture Of</span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#14b8a6] to-[#3b82f6]">Multidomain Warfare</span>
            </h1>
            
            {/* Description */}
            <p className="text-base md:text-lg text-slate-400 font-normal max-w-2xl leading-relaxed mb-10 text-center">
              An architectural audit of the emerging Neo-Prime ecosystem and the structural shift from exquisite hardware to software-defined attritable mass.
            </p>

            {/* Validation Tag (Floating Gatekeeper style) */}
            <div className="flex items-center gap-4 mb-10 opacity-80 hover:opacity-100 transition-opacity cursor-default bg-[#0d1322] border border-slate-800/60 px-5 py-2.5 rounded-full shadow-sm">
              <div className="flex -space-x-3">
                 <img src="/images/profile_martin.jpg" alt="Dr. Martin Crilly" className="w-10 h-10 rounded-full border-2 border-amber-500/50 object-cover relative z-20 shadow-[0_0_15px_rgba(245,158,11,0.3)]" />
                 <img src="/images/profile_vivan.jpg" alt="Vivan A." className="w-8 h-8 rounded-full border-2 border-[#0a0f1a] object-cover relative z-10" />
                 <img src="/images/profile_frank.jpg" alt="Frank R. Carus" className="w-8 h-8 rounded-full border-2 border-[#0a0f1a] object-cover relative z-10" />
                 <img src="/images/profile_gethin.jpg" alt="Gethin Lewis" className="w-8 h-8 rounded-full border-2 border-[#0a0f1a] object-cover relative z-10" />
              </div>
              <span className="text-[11px] sm:text-xs text-slate-400 font-medium tracking-wide">
                Expert Validated by the <strong className="text-slate-200">Frontier Intelligence Council</strong>
              </span>
            </div>

            {/* Action Area */}
            <div className="flex flex-col sm:flex-row items-center gap-4 w-full justify-center">
              <button 
                onClick={() => {
                  if (!userMandate) setUserMandate(activeInvestor.name);
                  handleAccessClick();
                }}
                className="group px-8 py-3.5 bg-gradient-to-r from-[#14b8a6] to-[#2563eb] hover:from-[#0d9488] hover:to-[#1d4ed8] text-white font-bold rounded-xl flex items-center gap-3 transition-all shadow-[0_0_30px_rgba(59,130,246,0.3)] hover:shadow-[0_0_40px_rgba(59,130,246,0.5)] text-[15px] tracking-wide w-full sm:w-auto justify-center"
              >
                <Power className="w-5 h-5" />
                <span>INITIALIZE DEFENSE TWIN</span>
              </button>

              <button 
                onClick={() => setIsTuningOpen(true)}
                className="group px-8 py-3.5 bg-[#151c2d] hover:bg-[#1e293b] border border-slate-700/60 text-slate-200 font-bold rounded-xl flex items-center gap-3 transition-all shadow-lg hover:border-slate-500 text-[15px] tracking-wide w-full sm:w-auto justify-center"
              >
                <SlidersHorizontal className="w-5 h-5 text-[#14b8a6]" />
                <span>SELECT STRATEGIC LENS</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Minimalist Footer - Now pushed safely to the bottom */}
        <div className="w-full flex flex-col items-center gap-5 px-4 pb-8 opacity-90 hover:opacity-100 transition-opacity relative z-10 shrink-0">
          <a 
            href="https://strategyandagents.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-3 cursor-pointer"
          >
            <Command size={22} className="text-[#14b8a6]" strokeWidth={2.5} />
            <span className="text-2xl font-black tracking-[0.25em] text-white uppercase">FRONTIER</span>
          </a>
          
          <div className="flex flex-col items-center gap-2.5">
            <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
              <span className="hover:text-slate-200 transition-colors cursor-pointer">Strategy &amp; Deal Advisory</span>
              <span className="hidden sm:inline text-slate-700">|</span>
              <span className="text-[#14b8a6] border border-[#14b8a6]/30 bg-[#14b8a6]/10 px-2 py-1 rounded shadow-sm">Agentic Leverage</span>
              <span className="hidden sm:inline text-slate-700">|</span>
              <span className="hover:text-slate-200 transition-colors cursor-pointer">TMT &amp; Deep Tech Focus</span>
            </div>

            <div className="w-12 h-px bg-slate-800/80 my-1 rounded-full"></div>

            <div className="text-[9px] text-slate-600 font-mono uppercase tracking-widest text-center">
              © 2026 Frontier | Strategy &amp; Agents. All Rights Reserved.
            </div>
          </div>
        </div>

      </div>
    );
  }

  // Derived datasets for custom visualisations in Executive Summary
  const executiveSegmentsData = [
    { name: "L5: Mission Intelligence", tam: 38.8, margin: 85, tags: ["High-Margin IP", "Decision Advantage"] },
    { name: "L4: Interoperability Fabric", tam: 15.0, margin: 80, tags: ["High-Margin IP", "Missing Middle"] },
    { name: "L3: Transport Mesh", tam: 12.0, margin: 50, tags: ["Deep Tech", "Dual-Use"] },
    { name: "L2: Compute & Energy", tam: 25.0, margin: 40, tags: ["Deep Tech"] },
    { name: "L1: Physical Edge", tam: 110.0, margin: 20, tags: ["Attritable Mass"] },
  ];
  
  const executiveFundingData = [
    { year: '2022', seed: 2.5, series: 1.5, growth: 1.0 },
    { year: '2023', seed: 1.8, series: 2.2, growth: 1.5 },
    { year: '2024', seed: 1.5, series: 3.1, growth: 2.5 },
    { year: '2025', seed: 1.2, series: 2.8, growth: 4.2 },
    { year: '2026E', seed: 1.0, series: 2.5, growth: 14.3 }
  ];

  const executiveMAData = [
    { year: '2021', vol: 12, val: 45 },
    { year: '2022', vol: 18, val: 60 },
    { year: '2023', vol: 15, val: 55 },
    { year: '2024', vol: 24, val: 110 },
    { year: '2025', vol: 35, val: 180 },
    { year: '2026E', vol: 42, val: 240 },
  ];

  // Aggregated Scatter Data with Deep Linking and Embedded Intelligence
  const executiveScatterNeoPrimes = [
    { name: 'Anduril', x: 7, y: 8.8, size: 30, layer: 'L4', segment: 'Data Fusion & OS', alpha: 'OS for autonomous systems and sensor fusion.', multiple: 'SaaS Premium: 20x-30x', company: { name: 'Anduril (Lattice)', tier: 'neoprime', tags: ['DIU', 'AUKUS'], desc: 'OS for autonomous systems and sensor fusion.', moat: 'Closed-loop sensor-to-shooter capabilities via Lattice OS.', signal: 'Closed historic $5B Series H at $61B valuation (May 2026); awarded $20B 10-yr US Army Enterprise contract.', hq: 'US', stage: 'Series H', emp: '7,000+', founded: '2017', ownership: 'Venture-Backed ($61B Valuation)', itar: 'ITAR Restricted' } },
    { name: 'Palantir', x: 8.5, y: 9, size: 40, layer: 'L4', segment: 'Data Fusion & OS', alpha: 'Entrenched AI targeting and JADC2 infrastructure.', multiple: 'SaaS Premium: 20x-30x', company: { name: 'Palantir (AIP)', tier: 'neoprime', tags: ['DUAL'], desc: 'AI Platform for defense targeting chains.', moat: 'Entrenched in Project Maven and JADC2.', signal: 'Strategic integration with GDLS combat vehicles.', hq: 'US', stage: 'Public', emp: '3K+', founded: '2003', ownership: 'Public (PLTR)', itar: 'FedRAMP High' } },
    { name: 'Shield AI', x: 6, y: 6.5, size: 25, layer: 'L5', segment: 'Targeting & Computer Vision', alpha: 'Hardware-agnostic AI pilot (Hivemind).', multiple: 'SaaS Premium: 20x-30x', company: { name: 'Shield AI', tier: 'neoprime', tags: ['DUAL', 'DIU'], desc: 'AI pilot (Hivemind) for aircraft, drones, and tactical swarms.', moat: 'Hardware-agnostic autonomy stack with edge swarming capabilities.', signal: 'Closed $2B transaction (including $1.5B Series G at $12.7B valuation led by Advent International) in March 2026.', hq: 'US', stage: 'Series G', emp: '1,000+', founded: '2015', ownership: 'Venture-Backed ($12.7B Valuation)', itar: 'ITAR Restricted' } },
    { name: 'SpaceX', x: 9, y: 7, size: 50, layer: 'L3', segment: 'Resilient Communications', alpha: 'Monopolizing military-grade proliferated LEO.', multiple: 'Infrastructure: 15x-20x', company: { name: 'Starshield (SpaceX)', tier: 'neoprime', tags: ['DUAL'], desc: 'Military-grade proliferated LEO comms.', moat: 'Unmatched launch economics and scale.', signal: 'Effectively a monopoly in LEO transport.', hq: 'US', stage: 'Private', emp: '10K+', founded: '2002', ownership: 'Private', itar: 'ITAR Restricted' } },
    { name: 'Helsing', x: 6.5, y: 8.5, size: 28, layer: 'L5', segment: 'Agentic AI & Command and Control', alpha: 'Software-only AI for European platforms.', multiple: 'SaaS Premium: 20x-30x', company: { name: 'Helsing', tier: 'neoprime', tags: ['NATO', 'DUAL'], desc: 'Software-only AI company for defense platforms.', moat: 'Deep integration with European prime contractors.', signal: 'Series C valued at €5B+.', hq: 'DE', stage: 'Series C', emp: '300+', founded: '2021', ownership: 'Venture-Backed', itar: 'NATO Sovereign' } },
    { name: 'Skydio', x: 7.5, y: 6, size: 35, layer: 'L1', segment: 'Aerial Systems (UAS)', alpha: 'Dominant US alternative to DJI.', multiple: 'Hardware+SaaS: 10x-15x', company: { name: 'Skydio', tier: 'neoprime', tags: ['DUAL', 'DIU'], desc: 'AI-piloted small UAS.', moat: 'Dominant US alternative to DJI.', signal: 'Massive success in Ukraine combat theater.', hq: 'US', stage: 'Series E', emp: '800+', founded: '2014', ownership: 'Venture-Backed', itar: 'Dual-Use Open' } },
    { name: 'Scale AI', x: 8, y: 8.5, size: 40, layer: 'L5', segment: 'Agentic AI & Command and Control', alpha: 'Foundational LLMs and defense data refinery.', multiple: 'SaaS Premium: 20x-30x', company: { name: 'Scale AI', tier: 'neoprime', tags: ['DUAL', 'DIU'], desc: 'LLMs and data platforms for national security.', moat: 'Donovan platform and massive federal data contracts.', signal: '$14B valuation and broad AI dominance.', hq: 'US', stage: 'Series F', emp: '1K+', founded: '2016', ownership: 'Venture-Backed', itar: 'Dual-Use Open' } },
    { name: 'Applied Intuition', x: 8.2, y: 7.8, size: 35, layer: 'L4', segment: 'Digital Twins & Simulation', alpha: 'The Digital Twin OS for autonomous armor and ground systems.', multiple: 'SaaS Premium: 20x-30x', company: { name: 'Applied Intuition', tier: 'neoprime', tags: ['DUAL'], desc: 'Vehicle software and simulation platform.', moat: 'Dominant in commercial autonomous driving.', signal: 'Strategic partnership with General Dynamics (GDLS).', hq: 'US', stage: 'Series E', emp: '500+', founded: '2017', ownership: 'Venture-Backed', itar: 'Dual-Use Open' } },
    { name: 'Ursa Major', x: 7.2, y: 5.5, size: 28, layer: 'L2', segment: 'Energetics & Propulsion', alpha: 'Vertically integrated propulsion challenging the legacy motor monopoly.', multiple: 'Deep Tech: 15x-20x', company: { name: 'Ursa Major', tier: 'neoprime', tags: ['DUAL'], desc: 'Independent rocket engine manufacturer.', moat: 'Scalable 3D printing of solid/liquid motors.', signal: 'Solving the US solid rocket motor shortage.', hq: 'US', stage: 'Series D', emp: '300+', founded: '2015', ownership: 'Venture-Backed', itar: 'ITAR Restricted' } }
  ];

  const executiveScatterChallengers = [
    { name: 'HawkEye 360', x: 7.5, y: 4.5, size: 20, layer: 'L3', segment: 'Spectrum & Electronic Warfare', alpha: 'Space-based RF mapping and dark-ship tracking.', multiple: 'Infrastructure: 12x-18x', company: { name: 'HawkEye 360', tier: 'challenger', tags: ['DUAL'], desc: 'Space-based RF mapping.', moat: 'Constellation dedicated to RF signal geolocation.', signal: 'Critical for dark-ship tracking.', hq: 'US', stage: 'Series D', emp: '150+', founded: '2015', ownership: 'Venture-Backed', itar: 'Dual-Use Open' } },
    { name: 'Saronic', x: 5.5, y: 5.5, size: 18, layer: 'L1', segment: 'Maritime & Subsurface (USV/UUV)', alpha: 'Fastest path to scale in naval surface autonomy.', multiple: 'Hardware: 10x-15x', company: { name: 'Saronic', tier: 'challenger', tags: ['DIU'], desc: 'Autonomous surface vessels (USVs) for maritime dominance.', moat: 'Vertically integrated from hull design to proprietary software.', signal: 'Raised $1.75B Series D led by Kleiner Perkins at $9.25B valuation (early 2026).', hq: 'US', stage: 'Series D', emp: '500+', founded: '2022', ownership: 'Venture-Backed ($9.25B Valuation)', itar: 'ITAR Compliant' } },
    { name: 'Allen Control Systems', x: 4.0, y: 7.0, size: 16, layer: 'L1', segment: 'Autonomous Precision Weapons', alpha: 'Computer vision-driven robotic gun turrets replacing manual air defense.', multiple: 'Hardware+SaaS: 15x-20x', company: { name: 'Allen Control Systems', tier: 'challenger', tags: ['Decision Advantage', 'Attritable Mass'], desc: 'Robotic gun systems for autonomous threat defeat.', moat: 'Proprietary computer vision for sub-millisecond ballistic tracking and automated target engagement.', signal: 'Awarded U.S. Army contract for the Bullfrog autonomous weapon system.', hq: 'US', stage: 'Early Stage', emp: '50+', founded: '2020', ownership: 'Venture-Backed', itar: 'ITAR Restricted' } },
    { name: 'Epirus', x: 6.5, y: 6, size: 22, layer: 'L3', segment: 'Spectrum & Electronic Warfare', alpha: 'Solid-state directed energy swarm-defeat.', multiple: 'Deep Tech: 15x-20x', company: { name: 'Epirus', tier: 'neoprime', tags: ['DIU'], desc: 'High-power microwave (HPM) directed energy.', moat: 'Solid-state architecture allowing swarm-defeat.', signal: 'Transitioning to major Program of Record.', hq: 'US', stage: 'Series C', emp: '200+', founded: '2018', ownership: 'Venture-Backed', itar: 'ITAR Restricted' } },
    { name: 'Varda', x: 4.5, y: 5, size: 15, layer: 'L1', segment: 'Space & Hypersonics', alpha: 'In-space manufacturing and reentry testing.', multiple: 'Deep Tech: 15x-20x', company: { name: 'Varda', tier: 'challenger', tags: ['DUAL'], desc: 'In-space manufacturing & reentry.', moat: 'High-cadence hypersonic reentry testing.', signal: 'Leveraging commercial pharma to fund defense testing.', hq: 'US', stage: 'Series B', emp: '100+', founded: '2020', ownership: 'Venture-Backed', itar: 'Dual-Use Open' } },
    { name: 'Shift5', x: 6.8, y: 4, size: 16, layer: 'L5', segment: 'Predictive Maintenance & Logistics AI', alpha: 'Serial bus observability for legacy fleets.', multiple: 'SaaS Premium: 20x-30x', company: { name: 'Shift5', tier: 'challenger', tags: ['DUAL'], desc: 'Observability for onboard fleet systems.', moat: 'Direct access to serial bus data on legacy vehicles.', signal: 'High commercial dual-use in rail/aviation.', hq: 'US', stage: 'Series B', emp: '150+', founded: '2019', ownership: 'Venture-Backed', itar: 'Dual-Use Open' } },
    { name: 'Harmattan AI', x: 3.5, y: 7.5, size: 15, layer: 'L5', segment: 'Agentic AI & Command and Control', alpha: 'Localized GCC cognitive processing.', multiple: 'SaaS Premium: 20x-30x', company: { name: 'Harmattan AI', tier: 'challenger', tags: ['Decision Advantage', 'DUAL'], desc: 'Agentic AI and cognitive decision systems.', moat: 'Localized GCC cognitive processing and decision advantage.', signal: 'Emerging AI leader in the UAE defense ecosystem.', hq: 'AE', stage: 'Early Stage', emp: '50+', founded: '2023', ownership: 'Private', itar: 'Sovereign Restricted' } },
    { name: 'Planys Tech', x: 6.5, y: 5, size: 18, layer: 'L1', segment: 'Maritime & Subsurface (USV/UUV)', alpha: 'Ruggedized subsurface sensing at attritable cost.', multiple: 'Hardware: 10x-15x', company: { name: 'Planys Technologies', tier: 'challenger', tags: ['Attritable Mass', 'DUAL'], desc: 'Autonomous underwater robots and ROVs for sensing and inspection.', moat: 'Ruggedized subsurface inspection at attritable cost.', signal: 'Expanding UUV capabilities in GCC maritime domains.', hq: 'AE', stage: 'Growth', emp: '100+', founded: '2015', ownership: 'Private', itar: 'Sovereign Restricted' } },
    { name: 'TakeMe2Space', x: 4.2, y: 6.8, size: 16, layer: 'L2', segment: 'Edge AI Hardware', alpha: 'Indigenous compute-focused LEO satellites.', multiple: 'Deep Tech: 15x-20x', company: { name: 'TakeMe2Space', tier: 'challenger', tags: ['Deep Tech', 'DUAL'], desc: 'Orbital infrastructure and compute-focused satellites.', moat: 'Indigenous vertical integration of compute-native satellite buses in LEO.', signal: 'Direct play on the "Compute Gap" for orbital edge AI.', hq: 'IN', stage: 'Early Stage', emp: '50+', founded: '2023', ownership: 'Private', itar: 'Sovereign Restricted' } },
    { name: 'Hadrian', x: 6.2, y: 7.2, size: 25, layer: 'L2', segment: 'Advanced Manufacturing', alpha: 'Software-defined factories solving the AUKUS munitions bottleneck.', multiple: 'Deep Tech: 15x-20x', company: { name: 'Hadrian', tier: 'challenger', tags: ['DUAL'], desc: 'Automated precision manufacturing factories.', moat: 'Software-defined factories cutting lead times by 10x.', signal: 'Key enabler for the "Attritable Mass" strategy.', hq: 'US', stage: 'Series B', emp: '150+', founded: '2020', ownership: 'Venture-Backed', itar: 'ITAR Compliant' } },
    { name: 'TurbineOne', x: 3.5, y: 8.2, size: 20, layer: 'L5', segment: 'Targeting & Computer Vision', alpha: 'No-code ML deployment for the non-technical frontline operator.', multiple: 'SaaS Premium: 20x-30x', company: { name: 'TurbineOne', tier: 'challenger', tags: ['DIU'], desc: 'Frontline ML for threat recognition.', moat: 'No-code model training at the tactical edge.', signal: 'Strong OTA growth.', hq: 'US', stage: 'Series A', emp: '30+', founded: '2021', ownership: 'Venture-Backed', itar: 'ITAR Compliant' } },
    { name: 'AnySignal', x: 3.2, y: 8.0, size: 18, layer: 'L3', segment: 'Resilient Communications', alpha: 'API-first software-defined radios disrupting legacy comms.', multiple: 'Hardware+SaaS: 15x-20x', company: { name: 'AnySignal', tier: 'challenger', tags: ['DUAL'], desc: 'Software-defined radio platform.', moat: 'Replaces fragmented legacy radios with API-first hardware.', signal: 'Fastest growing space-comms startup.', hq: 'US', stage: 'Seed', emp: '30+', founded: '2022', ownership: 'Venture-Backed', itar: 'Dual-Use Open' } },
    { name: 'Shift Dynamics', x: 4.8, y: 7.5, size: 18, layer: 'L5', segment: 'Targeting & Computer Vision', alpha: 'The "AI Brain" for small UAS and FPV drone ecosystems.', multiple: 'SaaS Premium: 20x-30x', company: { name: 'Shift Dynamics', tier: 'challenger', tags: ['High-Margin IP', 'Decision Advantage'], desc: 'Integrating AI, computer vision, and path-planning into tactical systems.', moat: "The 'AI Brain' for small UAS and FPV drone ecosystems.", signal: 'Strategic subsidiary of Red Cat, targeting Army SRR requirements.', hq: 'US', stage: 'Subsidiary', emp: '50+', founded: '2023', ownership: 'Public (RCAT)', itar: 'ITAR Compliant' } },
    { name: 'Pablo Air', x: 6.0, y: 6.5, size: 22, layer: 'L4', segment: 'Data Fusion & Operating Systems', alpha: 'Tech anchor for DAPA MUM-T combat integration drills.', multiple: 'SaaS Premium: 20x-30x', company: { name: 'Pablo Air', tier: 'challenger', tags: ['Attritable Mass', 'Missing Middle'], desc: 'Swarm intelligence and cross-domain orchestration software (PAMNet).', moat: 'Swarm-as-a-Service, allowing a single operator to command massive drone fleets.', signal: "Primary tech anchor for DAPA's MUM-T (Manned-Unmanned Teaming) combat integration drills.", hq: 'KR', stage: 'Series B', emp: '150+', founded: '2018', ownership: 'Private', itar: 'Dual-Use Open' } },
    { name: 'NearthLAB', x: 7.0, y: 5.0, size: 20, layer: 'L1', segment: 'Aerial Systems (UAS)', alpha: 'Vision-based flight algorithms engineered for GPS-denied, electronically jammed theaters.', multiple: 'Hardware+SaaS: 15x-20x', company: { name: 'NearthLAB', tier: 'challenger', tags: ['Dual-Use', 'Decision Advantage'], desc: 'Autonomous aerial intelligence and high-speed C-UAS interceptors.', moat: 'Vision-based flight algorithms engineered for GPS-denied, electronically jammed theaters.', signal: 'Expanded into kinetic profiles with XAiDEN swarm attack drones and KAiDEN C-UAS interceptors.', hq: 'KR', stage: 'Growth', emp: '100+', founded: '2015', ownership: 'Venture-Backed', itar: 'Dual-Use Open' } },
    { name: 'TORIS SQUARE', x: 4.5, y: 6.2, size: 16, layer: 'L3', segment: 'Counter-UAS & Directed Energy', alpha: 'Radar-based anti-drone solution platforms.', multiple: 'Hardware+SaaS: 15x-20x', company: { name: 'TORIS SQUARE', tier: 'challenger', tags: ['Decision Advantage', 'Dual-Use'], desc: 'Radar-based anti-drone solution platforms.', moat: 'Software-defined distributed radar for the C-UAS detection phase.', signal: 'Sensing foundation for kinetic effectors in the Indo-Pacific.', hq: 'KR', stage: 'Growth', emp: '50+', founded: '2020', ownership: 'Private', itar: 'Dual-Use Open' } },
    { name: 'VORASKY', x: 6.5, y: 4.8, size: 18, layer: 'L1', segment: 'Aerial Systems (UAS)', alpha: 'Manufacturer of unmanned aerial vehicles for defense and industrial applications.', multiple: 'Hardware: 10x-15x', company: { name: 'VORASKY', tier: 'challenger', tags: ['Attritable Mass', 'Dual-Use'], desc: 'Manufacturer of unmanned aerial vehicles for defense and industrial applications.', moat: 'Dual-use manufacturing serving both industrial logistics and tactical ISR.', signal: 'Providing attritable physical platforms for the South Korean defense ecosystem.', hq: 'KR', stage: 'Growth', emp: '50+', founded: '2019', ownership: 'Private', itar: 'Dual-Use Open' } },
    { name: 'BONE', x: 4.8, y: 6.8, size: 18, layer: 'L1', segment: 'Autonomous Precision Weapons', alpha: 'Software-defined robotic turrets enabling high-volume attritable strike capabilities.', multiple: 'Hardware+SaaS: 15x-20x', company: { name: "BONE", tier: "challenger", tags: ["Attritable Mass", "Decision Advantage", "Dual-Use"], desc: "AI-driven modular weapon stations and precision fire-control systems for robotic platforms.", moat: "Edge-AI computer vision for real-time ballistic tracking that retrofits onto low-cost platforms.", signal: "Secured 17B KRW led by Third Prime Capital; Rare US-VC bridge to ROK defense ecosystem.", hq: "KR", stage: "Series A", emp: "40+", founded: "2021", ownership: "Third Prime Capital / Private", itar: "Sovereign Restricted" } },
    { name: 'GenGen AI', x: 5.8, y: 7.8, size: 18, layer: 'L5', segment: 'Targeting & Computer Vision', alpha: 'Synthetic data solving the vision bottleneck for autonomous edge platforms.', multiple: 'SaaS Premium: 20x-30x', company: { name: 'GenGen AI', tier: 'challenger', tags: ['High-Margin IP', 'Dual-Use'], desc: 'Generative AI & Sim2Real synthetic video generation for defense vision.', moat: 'Overcomes data scarcity by creating hyper-realistic multi-sensor training environments.', signal: 'KAI acquired a 10% equity stake to accelerate AI pilot development via GenGenStudio.', hq: 'KR', stage: 'Seed', emp: '30+', founded: '2020', ownership: 'Venture/Strategic-Backed', itar: 'Dual-Use Open' } },
    { name: 'Next Aerospace', x: 6.8, y: 4.2, size: 16, layer: 'L1', segment: 'Aerial Systems (UAS)', alpha: 'Advanced composite VTOL airframes for runway-independent kinetic strike.', multiple: 'Hardware: 10x-15x', company: { name: 'Next Aerospace', tier: 'challenger', tags: ['Attritable Mass'], desc: 'Advanced composite VTOL and high-speed strike airframes.', moat: 'Optimized for rapid, distributed launch deployment without runway infrastructure.', signal: 'Debuted 25-kg class VTOL UAV and ultra-compact strike drones at Drone Show Korea 2026.', hq: 'KR', stage: 'Growth', emp: '50+', founded: '2019', ownership: 'Private', itar: 'ITAR Compliant' } },
    { name: 'Synspective', x: 6.2, y: 5.2, size: 18, layer: 'L3', segment: 'Spectrum & Electronic Warfare', alpha: 'SAR constellations providing allied dark-vessel detection.', multiple: 'Infrastructure: 12x-18x', company: { name: "Synspective", tier: "challenger", tags: ["DUAL", "Deep Tech"], desc: "StriX SAR (Synthetic Aperture Radar) Satellite constellation.", moat: "Small 100kg-class SAR satellites with proprietary deep-learning engine for Dark Vessel Detection.", signal: "High M&A interest for allied space domain situational awareness.", hq: "JP", stage: "Series C", emp: "150+", founded: "2018", ownership: "Venture-Backed", itar: "DFARS Qualifying" } }
  ];

  const executiveScatterLegacy = [
    { name: 'Lockheed', x: 9, y: 2, size: 45, layer: 'MULTI', segment: 'Legacy Prime', alpha: 'Bridging F-35 tech debt via CVC.', multiple: 'EBITDA: 10x-15x', company: { name: 'Lockheed Martin', tier: 'legacy', tags: [], desc: 'Global security and aerospace prime contractor.', moat: 'Massive incumbent PoRs (F-35, Black Hawk).', signal: 'Aggressively seeking AI middleware to link legacy platforms to JADC2 requirements.', hq: 'US', stage: 'Public', emp: '116K+', founded: '1995', ownership: 'Public (LMT)', itar: 'ITAR Restricted' } },
    { name: 'Northrop', x: 8.0, y: 3.5, size: 40, layer: 'MULTI', segment: 'Legacy Prime', alpha: 'Pivoting to Space Networking.', multiple: 'EBITDA: 10x-15x', company: { name: 'Northrop Grumman', tier: 'legacy', tags: [], desc: 'Aerospace and defense technology prime.', moat: 'B-21 Raider and space systems dominance.', signal: 'Securing massive Space Development Agency (SDA) contracts.', hq: 'US', stage: 'Public', emp: '100K+', founded: '1994', ownership: 'Public (NOC)', itar: 'ITAR Restricted' } },
    { name: 'RTX', x: 9.5, y: 1.5, size: 45, layer: 'MULTI', segment: 'Legacy Prime', alpha: 'Desperate for cheap C-UAS software.', multiple: 'EBITDA: 10x-15x', company: { name: 'RTX (Raytheon)', tier: 'legacy', tags: [], desc: 'Aerospace and defense conglomerate.', moat: 'Patriot and Standard Missile effectors.', signal: 'Focusing heavily on counter-UAS (C-UAS) software acquisitions.', hq: 'US', stage: 'Public', emp: '185K+', founded: '1922', ownership: 'Public (RTX)', itar: 'ITAR Restricted' } },
    { name: 'Gen Dynamics', x: 8.5, y: 2.8, size: 35, layer: 'MULTI', segment: 'Legacy Prime', alpha: 'Robotizing land systems (Stryker).', multiple: 'EBITDA: 10x-15x', company: { name: 'General Dynamics', tier: 'legacy', tags: [], desc: 'Aerospace and defense corporation.', moat: 'Abrams tanks and nuclear submarine production.', signal: 'Developing the StrykerX tech demonstrator with hybrid AI.', hq: 'US', stage: 'Public', emp: '100K+', founded: '1899', ownership: 'Public (GD)', itar: 'ITAR Restricted' } },
    { name: 'Boeing', x: 9.2, y: 1, size: 35, layer: 'MULTI', segment: 'Legacy Prime', alpha: 'Acquiring autonomy for CCA program.', multiple: 'EBITDA: 10x-15x', company: { name: 'Boeing Defense', tier: 'legacy', tags: [], desc: 'Defense, space, and security prime.', moat: 'Massive legacy aviation platforms (F-15, Apache).', signal: 'Integrating AI pilots into the MQ-28 Ghost Bat platform.', hq: 'US', stage: 'Public', emp: '140K+', founded: '1916', ownership: 'Public (BA)', itar: 'ITAR Restricted' } },
    { name: 'AeroVironment', x: 8.8, y: 4.5, size: 32, layer: 'L1', segment: 'Aerial Systems (UAS)', alpha: 'The incumbent leader in battle-tested loitering munitions.', multiple: 'EBITDA: 10x-15x', company: { name: 'AeroVironment', tier: 'legacy', tags: [], desc: 'Tactical UAS and loitering munitions.', moat: 'Switchblade program of record.', signal: 'Benefiting massively from Ukraine demand.', hq: 'US', stage: 'Public', emp: '1K+', founded: '1971', ownership: 'Public (AVAV)', itar: 'ITAR Restricted' } },
    { name: 'BAE Systems', x: 8.2, y: 2.5, size: 38, layer: 'MULTI', segment: 'Legacy Prime', alpha: 'AUKUS Submarine Anchor & GCAP Prime.', multiple: 'EBITDA: 10x-15x', company: { name: 'BAE Systems', tier: 'legacy', tags: ['AUKUS', 'NATO'], desc: 'Multinational arms, security, and aerospace company.', moat: 'AUKUS Pillar 1 prime and massive European defense footprint.', signal: 'Acquiring autonomy software for GCAP and future submarine programs.', hq: 'UK', stage: 'Public', emp: '93K+', founded: '1999', ownership: 'Public (BA.L)', itar: 'AUKUS Aligned' } },
    { name: 'Doosan Mobility', x: 8.5, y: 3.8, size: 28, layer: 'L1', segment: 'Aerial Systems (UAS)', alpha: 'Hydrogen fuel-cell dominance expanding tactical UAV loitering times.', multiple: 'EBITDA: 10x-15x', company: { name: 'Doosan Mobility', tier: 'legacy', tags: ['Deep Tech', 'Dual-Use'], desc: 'Mobile hydrogen fuel-cell power packs for endurance UAVs.', moat: 'Solves the persistent battery bottleneck, drastically extending loitering times.', signal: 'Demonstrated sustained >120 minute flight endurance in extreme -20°C combat conditions.', hq: 'KR', stage: 'Mature', emp: '100+', founded: '2016', ownership: 'Corporate Subsidiary', itar: 'Dual-Use Open' } },
    { name: 'Fujitsu', x: 8.4, y: 2.1, size: 30, layer: 'L5', segment: 'Agentic AI & Command and Control', alpha: 'Leading Japan’s sovereign Multi-AI Agent initiative for ATLA.', multiple: 'EBITDA: 10x-15x', company: { name: "Fujitsu (ATLA)", tier: "legacy", tags: ["Decision Advantage", "DUAL"], desc: "Developing Defense Multi-AI Agents for ATLA.", moat: "Japan's first specialized open innovation defense program for automated operational support.", signal: "Validates active sovereign transition to agentic, autonomous multi-agent systems.", hq: "JP", stage: "Public", emp: "100K+", founded: "1935", ownership: "Public", itar: "DFARS Qualifying" } },
    { name: 'Hanwha Aero', x: 8.6, y: 3.1, size: 34, layer: 'L1', segment: 'Unmanned Ground Vehicles (UGVs)', alpha: 'Bypassing BAA via onshore JV to deploy Arion-SMET.', multiple: 'EBITDA: 10x-15x', company: { name: 'Hanwha Aerospace', tier: 'legacy', tags: ['Onshore JV Required', 'Attritable Mass'], desc: 'Arion-SMET multi-purpose UGV.', moat: 'Massive manufacturing scale and successful U.S. Marine Corps Oahu trials.', signal: 'Aggressively establishing U.S.-based subsidiaries to bypass Buy American Act restrictions.', hq: 'KR', stage: 'Public', emp: '5K+', founded: '1977', ownership: 'Public', itar: 'ITAR Compliant' } }
  ];

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-slate-200 font-sans selection:bg-blue-500/30 flex flex-col md:flex-row relative">
      
      {/* Sidebar Navigation */}
      {isSidebarOpen && (
        <aside className="w-full md:w-72 shrink-0 md:sticky md:top-0 md:h-screen border-b md:border-b-0 md:border-r border-slate-800 bg-[#060913] p-6 flex flex-col gap-8 z-40 md:overflow-y-auto custom-scrollbar">
          <div className="flex items-start justify-between gap-2">
            <button 
              onClick={() => setActiveView('cover')}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity text-left cursor-pointer group"
              title="Return to Cover Page"
            >
              <Shield className="w-8 h-8 text-blue-500 shrink-0 group-hover:scale-105 transition-transform" />
              <h1 className="text-xl font-bold tracking-tight text-white leading-tight">Defense Tech 2.0<br/><span className="text-blue-400">Landscape</span></h1>
            </button>
            <button 
              onClick={() => setIsSidebarOpen(false)} 
              className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-800/80 transition-colors hidden md:block"
              title="Hide Sidebar"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button 
              onClick={() => setIsSidebarOpen(false)} 
              className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-800/80 transition-colors md:hidden"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex flex-col gap-1.5 w-full mt-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveView(item.id)}
                  className={`group flex items-center gap-3 text-left px-3 py-3 rounded-lg transition-all duration-200 ${
                    isActive 
                      ? 'bg-blue-500/10 text-blue-400 font-semibold' 
                      : 'text-slate-400 hover:bg-slate-800/40 hover:text-slate-200'
                  }`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-blue-400' : 'text-slate-500 group-hover:text-slate-300'}`} /> 
                  <div className="flex-1 flex items-center">
                    <span className="text-[10px] font-mono opacity-50 mr-2">{item.num}.</span> 
                    <span className="text-[13px] tracking-wide">{item.label}</span>
                  </div>
                </button>
              );
            })}
          </nav>

          {/* Sidebar Footer with system-tag legal strip */}
          <div className="mt-auto pt-6 border-t border-slate-800/60 hidden md:flex flex-col gap-4">
            <a 
              href="https://strategyandagents.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer w-fit"
            >
              <div className="p-1.5 rounded-lg bg-[#212736] border border-slate-700/50 shadow-[0_0_10px_rgba(99,102,241,0.15)]">
                <Command size={16} className="text-indigo-400" strokeWidth={2.5} />
              </div>
              <span className="text-lg font-black tracking-[0.2em] text-white uppercase">FRONTIER</span>
            </a>
            <div className="text-[9px] text-slate-500 font-mono tracking-widest uppercase flex flex-col gap-1.5">
              <span className="text-[9px] text-slate-700 tracking-tighter mt-1">[ © 2026 FRONTIER // ALL RIGHTS RESERVED ]</span>
            </div>
          </div>
        </aside>
      )}

      {/* Main Content Area */}
      <main className="flex-1 p-6 md:p-10 lg:p-12 overflow-x-hidden w-full relative">
        
        {/* Toggle to Re-open Sidebar */}
        {!isSidebarOpen && (
          <button 
            onClick={() => setIsSidebarOpen(true)}
            className="absolute top-3 left-3 md:top-4 md:left-4 z-50 p-2 bg-[#060913] border border-slate-700/60 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-all shadow-lg flex items-center justify-center group"
            title="Show Navigation"
          >
            <Menu className="w-4 h-4 group-hover:scale-110 transition-transform" />
          </button>
        )}
        
        {renderContextWidget()}

        {/* Strategic Lens Modal */}
        {renderStrategicLensModal()}

        {/* Add top padding buffer to prevent overlap with floating absolute widgets */}
        <div className={`transition-all pt-8 md:pt-4`}>
          
          {/* VIEW: Executive Summary (Unified Dashboard) */}
          {activeView === 'exec_summary' && (
            <div className="max-w-6xl mx-auto space-y-16 pb-12 animate-fade-in">
              
              {/* Header */}
              <div className="border-b border-slate-800 pb-6">
                <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-3">
                  01 // Strategic Synthesis
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-8 h-8 text-blue-400 shrink-0" />
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-white tracking-tight">
                    Defense Tech 2.0: <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">The Alpha Thesis</span>
                  </h2>
                </div>
                <p className="text-slate-400 text-lg max-w-4xl leading-relaxed">
                  Exploiting the structural gap between legacy technical debt and Neo-Prime software dominance.
                </p>
              </div>

              {/* Validation Ribbon */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gradient-to-r from-amber-950/30 to-transparent border border-amber-500/20 rounded-xl p-4 shadow-[0_0_15px_rgba(245,158,11,0.05)] mt-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <div className="text-amber-500 font-bold text-sm tracking-wider uppercase mb-0.5">Sovereign-Checked & Validated</div>
                    <div className="text-slate-400 text-xs">Synthesis ground-truth verified by former MOD/Global Defense leaders via the <span className="text-slate-200 font-semibold">Knowledge Ridge Expert Network</span>.</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0 pt-2 sm:pt-0 sm:pl-4 sm:border-l border-amber-500/20 w-full sm:w-auto">
                   <div className="flex -space-x-2">
                     <img src="/images/profile_martin.jpg" alt="Dr. Martin Crilly" className="w-8 h-8 rounded-full border-2 border-amber-500/50 object-cover relative z-20 shadow-[0_0_10px_rgba(245,158,11,0.3)]" title="Dr. Martin Crilly - Architectural Authority | Dual-Use Orchestration" />
                     <img src="/images/profile_vivan.jpg" alt="Vivan A." className="w-7 h-7 rounded-full border-2 border-[#0a0f1a] object-cover relative z-10" title="Vivan A. - Physical AI | Big Tech & Autonomy" />
                     <img src="/images/profile_frank.jpg" alt="Frank R. Carus" className="w-7 h-7 rounded-full border-2 border-[#0a0f1a] object-cover relative z-10" title="Frank R. Carus - Aerospace & Platforms | Ex-VP F-35 Program" />
                     <img src="/images/profile_gethin.jpg" alt="Gethin Lewis" className="w-7 h-7 rounded-full border-2 border-[#0a0f1a] object-cover relative z-10" title="Gethin Lewis - Prime Integration | NATO/UK Procurement" />
                   </div>
                   <button 
                     onClick={() => document.getElementById('strategic-council-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                     className="text-[10px] font-mono text-amber-500/60 hover:text-amber-400 uppercase tracking-widest transition-colors flex items-center gap-1 cursor-pointer group"
                   >
                     Council Active <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                   </button>
                </div>
              </div>

              {/* 1. Definition (Migration Spectrum) */}
              <section className="space-y-6">
                <div>
                  <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-2">DEFINITION</div>
                  <h2 className="text-3xl font-bold text-slate-100">The Realignment: Migration of Value</h2>
                  <p className="text-slate-400 mt-2">A structural shift away from closed, hardware-centric monopolies toward open, software-defined ecosystems.</p>
                </div>
                
                <div className="relative flex flex-col md:flex-row rounded-2xl border border-slate-800 bg-[#0f172a] overflow-hidden shadow-xl">
                  
                  {/* Continuous Top Progress Bar */}
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-slate-700 via-slate-500 to-cyan-400 z-30" />

                  {/* Step 1: Legacy */}
                  <div className="flex-1 p-6 md:p-8 relative bg-slate-950/60 grayscale hover:grayscale-0 transition-all duration-500 border-b md:border-b-0 md:border-r border-slate-800/80">
                    <div className="text-slate-600 mb-5"><Landmark className="w-8 h-8" /></div>
                    <h3 className="text-xl font-bold text-slate-400 mb-4">Legacy Defense 1.0</h3>
                    <ul className="space-y-3 text-slate-500 text-sm">
                      <li className="flex items-start gap-2"><span className="text-slate-600 font-bold mt-0.5">&rarr;</span> Exquisite, hardware-first platforms</li>
                      <li className="flex items-start gap-2"><span className="text-slate-600 font-bold mt-0.5">&rarr;</span> Decades-long procurement cycles</li>
                      <li className="flex items-start gap-2"><span className="text-slate-600 font-bold mt-0.5">&rarr;</span> Cost-plus contracting (low incentive for speed)</li>
                      <li className="flex items-start gap-2"><span className="text-slate-600 font-bold mt-0.5">&rarr;</span> Absolute vendor lock-in by top Primes</li>
                    </ul>
                  </div>

                  {/* Connector 1 (Desktop) */}
                  <div className="hidden md:flex absolute left-1/3 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-slate-900 rounded-full border-[3px] border-[#0f172a] items-center justify-center text-slate-600 shadow-sm">
                    <ChevronRight className="w-5 h-5 ml-0.5" />
                  </div>

                  {/* Connector 1 (Mobile) */}
                  <div className="md:hidden absolute top-[33.33%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-8 h-8 bg-slate-900 rounded-full border-2 border-[#0f172a] flex items-center justify-center text-slate-600">
                    <ChevronDown className="w-4 h-4 mt-0.5" />
                  </div>

                  {/* Step 2: Next Gen */}
                  <div className="flex-1 p-6 md:p-8 relative bg-slate-900/30 border-b md:border-b-0 md:border-r border-slate-800/80 hover:bg-slate-800/30 transition-colors duration-500">
                    <div className="text-slate-400 mb-5"><Network className="w-8 h-8" /></div>
                    <h3 className="text-xl font-bold text-slate-200 mb-4">Next Gen Defense</h3>
                    <ul className="space-y-3 text-slate-400 text-sm">
                      <li className="flex items-start gap-2"><span className="text-slate-500 font-bold mt-0.5">&rarr;</span> Software-enabled legacy hardware</li>
                      <li className="flex items-start gap-2"><span className="text-slate-500 font-bold mt-0.5">&rarr;</span> Commercial Off-The-Shelf (COTS) integration</li>
                      <li className="flex items-start gap-2"><span className="text-slate-500 font-bold mt-0.5">&rarr;</span> Fixed-price capability adoption</li>
                      <li className="flex items-start gap-2"><span className="text-slate-500 font-bold mt-0.5">&rarr;</span> Collaborative Combat Aircraft (CCA) teaming</li>
                    </ul>
                  </div>

                  {/* Connector 2 (Desktop) */}
                  <div className="hidden md:flex absolute left-2/3 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-blue-900/80 rounded-full border-[3px] border-[#0f172a] items-center justify-center text-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.3)] backdrop-blur-sm">
                    <ChevronRight className="w-5 h-5 ml-0.5" />
                  </div>

                  {/* Connector 2 (Mobile) */}
                  <div className="md:hidden absolute top-[66.66%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-8 h-8 bg-blue-900/80 rounded-full border-2 border-[#0f172a] flex items-center justify-center text-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.3)]">
                    <ChevronDown className="w-4 h-4 mt-0.5" />
                  </div>

                  {/* Step 3: Defense Tech 2.0 */}
                  <div className="flex-1 p-6 md:p-8 relative bg-gradient-to-br from-blue-900/10 to-cyan-900/10 overflow-hidden group">
                    <div className="absolute -right-12 -top-12 w-40 h-40 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none group-hover:bg-cyan-500/20 transition-colors duration-700"></div>
                    <div className="text-cyan-400 mb-5 relative z-10 drop-shadow-[0_0_8px_rgba(34,211,238,0.5)]"><Zap className="w-8 h-8" /></div>
                    <h3 className="text-xl font-bold text-white mb-4 relative z-10">Defense Tech 2.0</h3>
                    <ul className="space-y-3 text-slate-200 text-sm font-medium relative z-10">
                      <li className="flex items-start gap-2"><span className="text-cyan-400 font-bold mt-0.5">&rarr;</span> AI-native &amp; API-first architectures</li>
                      <li className="flex items-start gap-2"><span className="text-cyan-400 font-bold mt-0.5">&rarr;</span> Attritable mass (High volume, low unit cost)</li>
                      <li className="flex items-start gap-2"><span className="text-cyan-400 font-bold mt-0.5">&rarr;</span> Dual-use DNA bridging commercial & federal</li>
                      <li className="flex items-start gap-2"><span className="text-cyan-400 font-bold mt-0.5">&rarr;</span> Agile DevOps iteration (Months, not years)</li>
                    </ul>
                  </div>

                </div>
              </section>

              {/* 2. Drivers */}
              <section className="space-y-6">
                <div>
                  <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-2">MACRO DRIVERS</div>
                  <h2 className="text-3xl font-bold text-slate-100">The Catalysts: Shifts in Global Procurement Logic</h2>
                  <p className="text-slate-400 mt-2">Macroeconomic, geopolitical, and technological forces accelerating the procurement and deployment of modern systems.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-[#0f172a] border border-slate-800 p-6 rounded-xl shadow-md hover:border-slate-700 transition-colors">
                    <BrainCircuit className="w-7 h-7 text-indigo-400 mb-4" />
                    <h4 className="text-lg font-bold text-white mb-2">The AI-First Mandate</h4>
                    <p className="text-sm text-slate-400 leading-relaxed">Historic MOD/Global Defense strategy memorandums are mandating 'AI-first' operational concepts and forcing 30-day commercial deployment standards.</p>
                  </div>
                  
                  <div className="bg-[#0f172a] border border-slate-800 p-6 rounded-xl shadow-md hover:border-slate-700 transition-colors">
                    <Factory className="w-7 h-7 text-emerald-400 mb-4" />
                    <h4 className="text-lg font-bold text-white mb-2">Attritable Mass</h4>
                    <p className="text-sm text-slate-400 leading-relaxed">The mandate to field thousands of autonomous, low-cost systems is shifting capital toward software-augmented manufacturing and high-volume production.</p>
                  </div>
                  
                  <div className="bg-[#0f172a] border border-slate-800 p-6 rounded-xl shadow-md hover:border-slate-700 transition-colors">
                    <Globe className="w-7 h-7 text-rose-400 mb-4" />
                    <h4 className="text-lg font-bold text-white mb-2">Geopolitical Realignment</h4>
                    <p className="text-sm text-slate-400 leading-relaxed">A permanent shift toward near-peer deterrence is driving a ~5% baseline growth in defense spending and forcing the insourcing of sovereign supply chains.</p>
                  </div>

                  <div className="bg-[#0f172a] border border-slate-800 p-6 rounded-xl shadow-md hover:border-slate-700 transition-colors">
                    <Award className="w-7 h-7 text-amber-400 mb-4" />
                    <h4 className="text-lg font-bold text-white mb-2">The Neo-Prime Inflection</h4>
                    <p className="text-sm text-slate-400 leading-relaxed">The shift from experimental R&D to enterprise-scale adoption, evidenced by MOD/Global Defense actively scaling software-first firms as Tier 1 Strategic Suppliers.</p>
                  </div>
                </div>
              </section>

              {/* 3. Stack */}
              <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-2">OPERATIONAL STACK</div>
                  <h2 className="text-3xl font-bold text-slate-100 mb-4">The Architecture: Disaggregating the Battlefield</h2>
                  <p className="text-slate-400 mb-6 leading-relaxed">To overcome the integration challenges of the past, the Defense Tech 2.0 ecosystem has moved toward a disaggregated, five-layer technology stack. This architecture allows hardware and software to evolve independently at their own necessary speeds.</p>
                  
                  <div className="space-y-4 text-sm">
                    {layers.map(layer => {
                      const Icon = layer.icon;
                      return (
                        <div key={layer.id} className="flex items-start gap-4">
                          <div className={`mt-1 ${layer.textColor} p-1.5 rounded-lg bg-slate-900 border border-slate-800`}><Icon className="w-4 h-4" /></div>
                          <div>
                            <strong className="text-slate-200 block mb-0.5 text-[15px]">L{layer.id}: {layer.name}</strong>
                            <span className="text-slate-400 leading-relaxed text-sm block">{layer.orientationSegments}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col gap-3 p-6 md:p-8 bg-[#0f172a] border border-slate-800 rounded-xl shadow-lg">
                  {layers.map(layer => {
                    const Icon = layer.icon;
                    return (
                      <div key={layer.id} className={`${layer.activeColor} border rounded-xl p-4 md:p-5 transition-transform hover:-translate-y-1 cursor-default flex items-center gap-4 text-left shadow-sm hover:shadow-md`}>
                        <div className={`p-3 rounded-lg bg-slate-950/50 ${layer.textColor} shrink-0 hidden sm:block`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="text-[10px] font-mono text-slate-400 mb-1 tracking-wider uppercase">Layer {layer.id}</div>
                          <h4 className="font-bold text-white text-base md:text-lg tracking-wide leading-tight">{layer.name}</h4>
                          <p className="text-xs text-slate-300 mt-1 font-mono">{layer.subtitle}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* 4. Segments (Actionable Taxonomy) */}
              <section id="segment-dynamics-section" className="space-y-4 md:space-y-5 scroll-mt-24">
                <div>
                  <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-2">SEGMENT DYNAMICS</div>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-100">Alpha Segments: Isolating High-Margin Taxonomy</h2>
                  <p className="text-slate-400 mt-1.5 md:mt-2 text-sm md:text-base leading-relaxed">Evaluating the stack through the lens of <strong className="text-white">High-Margin IP</strong> vs. <strong className="text-white">Attritable Mass</strong>. The operational architecture dictates the alpha: Layer 5 commands pure software multiples, while Layer 1 drives absolute hardware volume.</p>
                </div>
                
                {/* Heat-Map Legend (Moved to Top & Right-Aligned) */}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[9px] md:text-[10px] font-mono text-slate-500 bg-slate-900/30 py-2.5 px-4 rounded-lg border border-slate-800/50 w-fit ml-auto mb-2">
                  <div className="flex items-center gap-2"><div className="w-3 h-3 bg-blue-600 rounded-sm"></div> High Conviction (Primary Alpha)</div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 bg-blue-900/50 border border-blue-500/50 rounded-sm"></div> Medium Conviction (Secondary)</div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 bg-[#0a0f1a] border border-slate-700 rounded-sm"></div> Low Conviction (Marginal / Commoditized)</div>
                </div>
                
                <div className="flex flex-col gap-2">
                  {layers.map((layer) => {
                    const Icon = layer.icon;
                    return (
                      <div key={layer.id} className="flex flex-col lg:flex-row items-stretch gap-1.5 md:gap-2 bg-[#0f172a] border border-slate-800 rounded-xl p-2 shadow-md hover:border-slate-700 transition-colors">
                        
                        {/* Left: Enhanced Taxonomy Anchor */}
                        <div className={`w-full lg:w-4/12 shrink-0 flex flex-col justify-center p-3 md:p-4 rounded-lg border ${layer.activeColor} relative shadow-sm`}>
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`p-2 md:p-2.5 rounded-md bg-slate-950/50 ${layer.textColor} shrink-0 hidden sm:block`}>
                              <Icon className="w-5 h-5 md:w-6 md:h-6" />
                            </div>
                            <div>
                              <div className="text-[9px] md:text-[10px] font-mono text-slate-400 mb-0.5 tracking-wider uppercase">Layer {layer.id}</div>
                              <h4 className="font-bold text-white text-[14px] md:text-base tracking-wide leading-tight">{layer.name}</h4>
                            </div>
                          </div>
                          
                          {/* Financial Pills embedded in Stack Block */}
                          <div className="flex gap-1.5 md:gap-2 mt-1 pt-2 md:pt-3 border-t border-slate-800/40">
                            <div className="flex items-center justify-between gap-1.5 bg-slate-950/40 border border-slate-800/50 px-2 py-1 md:py-1.5 rounded-md flex-1 shadow-inner">
                              <div className="text-[7px] md:text-[8px] font-mono text-slate-400 uppercase tracking-widest"><Globe className="w-2 h-2 inline mr-1" />TAM:</div>
                              <div className="text-[10px] md:text-xs font-bold text-white whitespace-nowrap">{layer.finance.tam}</div>
                            </div>
                            <div className="flex items-center justify-between gap-1.5 bg-slate-950/40 border border-slate-800/50 px-2 py-1 md:py-1.5 rounded-md flex-1 shadow-inner">
                              <div className="text-[7px] md:text-[8px] font-mono text-slate-400 uppercase tracking-widest"><TrendingUp className="w-2 h-2 inline mr-1" />Margin:</div>
                              <div className="text-[10px] md:text-xs font-bold text-white whitespace-nowrap">{layer.finance.margin}</div>
                            </div>
                          </div>
                        </div>

                        {/* Right: Intensity Matrix (Heat-Map) with Alpha Popovers */}
                        <div className="w-full lg:w-8/12 grid grid-cols-2 lg:flex lg:flex-nowrap gap-1.5 md:gap-2">
                          {getSortedSegments(layer.segments).map((seg, sIdx) => {
                            const heatStyle = getHeatmapStyle(layer.id, seg.conviction);
                            const verdict = seg.conviction === 'high' ? 'HIGH CONVICTION' : seg.conviction === 'medium' ? 'MEDIUM CONVICTION' : 'LOW CONVICTION';
                            const verdictColor = seg.conviction === 'high' ? 'text-emerald-400' : seg.conviction === 'medium' ? 'text-amber-400' : 'text-rose-400';
                            
                            return (
                              <div 
                                key={sIdx} 
                                className={`group/segbox relative lg:flex-1 flex items-center justify-center text-center px-2 py-3 md:py-2 rounded-lg border transition-all cursor-pointer min-h-[70px] lg:min-h-[80px] overflow-hidden ${heatStyle} hover:border-slate-500`}
                                onClick={() => {
                                   setHighlightedSegment(seg.name);
                                   document.getElementById('scatter-plot-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }}
                              >
                                {/* Default State */}
                                <span className="text-[10px] md:text-[11px] font-bold leading-tight relative z-10 group-hover/segbox:opacity-0 group-hover/segbox:scale-95 transition-all duration-300 px-1">
                                  {seg.name}
                                </span>
                                
                                {/* Hover State (Internal Smart Tile) */}
                                <div className="absolute inset-0 bg-[#0B1120] flex flex-col justify-center items-center p-2 opacity-0 translate-y-4 group-hover/segbox:opacity-100 group-hover/segbox:translate-y-0 transition-all duration-300 z-20">
                                   <span className={`text-[8px] md:text-[9px] font-mono font-bold tracking-widest mb-1 ${verdictColor}`}>
                                      [ {verdict} ]
                                   </span>
                                   <span className="text-[9px] md:text-[10px] text-slate-300 font-medium leading-snug line-clamp-3 px-1">
                                      {renderWithJargon(seg.desc)}
                                   </span>
                                </div>

                                {/* Deep-Dive Audit Button */}
                                <button 
                                   className="absolute top-1 right-1 p-1 opacity-0 group-hover/segbox:opacity-100 transition-opacity hover:bg-slate-700/50 rounded z-30"
                                   onClick={(e) => { 
                                      e.stopPropagation(); 
                                      setActiveLayer(layer);
                                      setActiveView('landscape'); 
                                   }}
                                   title="Launch Segment Audit"
                                >
                                   <Search className="w-3 h-3 text-slate-400 hover:text-white" />
                                </button>
                              </div>
                            );
                          })}
                        </div>

                      </div>
                    );
                  })}
                </div>
                
                {/* Detailed Intelligence Anchor */}
                <div className="mt-4 md:mt-6 flex justify-center">
                  <button 
                    onClick={() => setActiveView('map')}
                    className="group px-5 py-2.5 md:px-6 md:py-3 bg-[#0f172a] hover:bg-slate-800 border border-slate-700 hover:border-slate-500 text-slate-300 font-semibold rounded-full transition-all flex items-center gap-2 md:gap-3 shadow-md"
                  >
                    <LayoutGrid className="w-3.5 h-3.5 md:w-4 md:h-4 text-blue-400" />
                    <span className="text-xs md:text-sm">Explore Full Multidomain Taxonomy &amp; Target Universe</span>
                    <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4 text-slate-500 group-hover:text-slate-300 group-hover:translate-x-1 transition-all" />
                  </button>
                </div>
              </section>

              {/* 5. Market Map (Scatter) */}
              <section id="scatter-plot-section" className="space-y-6 relative scroll-mt-24">
                <div>
                  <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-2">COMPETITIVE PEERS</div>
                  <h2 className="text-3xl font-bold text-slate-100">The Landscape: Neo-Primes vs. Technical Debt</h2>
                  <p className="text-slate-400 mt-2 leading-relaxed">The competitive landscape is transitioning from a monopoly of Legacy incumbents to a tri-polar market. While <strong className="text-white">Neo-Primes</strong> disrupt through software-first architectures, <strong className="text-white">Growth Challengers</strong> represent the critical M&A 'connective tissue' required by Legacy Primes to bridge their technical debt. <strong className="text-blue-400">Click any asset to launch a full audit, or hover to view its strategic Alpha Vector.</strong></p>
                </div>
                
                <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-4 md:p-6 shadow-md relative">
                  
                  {/* Target Lock UI (State Persistence Clear) */}
                  {highlightedSegment && (
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 animate-fade-in pointer-events-auto">
                      <button 
                        onClick={() => setHighlightedSegment(null)}
                        className="flex items-center gap-2 px-4 py-1.5 bg-rose-900/90 border border-rose-500 text-rose-100 rounded-full text-[10px] font-mono tracking-widest uppercase shadow-[0_0_20px_rgba(225,29,72,0.4)] hover:bg-rose-800 hover:text-white transition-all"
                      >
                        <Target className="w-3.5 h-3.5" /> Target Lock: {highlightedSegment} <X className="w-3.5 h-3.5 ml-1" />
                      </button>
                    </div>
                  )}

                  <div className="flex w-full h-[450px] md:h-[500px] mt-2">
                    {/* Y-Axis Label */}
                    <div className="w-8 md:w-12 flex items-center justify-center shrink-0">
                      <div className="text-[10px] text-slate-400 font-mono uppercase tracking-widest whitespace-nowrap -rotate-90">
                        Disruptive Potential (Software/AI Focus) &rarr;
                      </div>
                    </div>
                    
                    <div className="flex-1 flex flex-col">
                      {/* Plot Area */}
                      <div className="relative flex-1 bg-slate-900/50 rounded-lg border border-slate-700/60 font-sans">
                        <div className="absolute inset-0 overflow-hidden rounded-lg z-0 pointer-events-none">
                          {/* Background Quadrants & Valuation Annotations */}
                          <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-fuchsia-500/5 border-l border-b border-fuchsia-500/20" />
                          
                          <div className="absolute top-4 right-4 text-right">
                            <div className="text-fuchsia-400/50 font-bold uppercase tracking-widest text-xs">Hyper-Growth Quadrant</div>
                            <div className="text-fuchsia-300/40 font-mono text-[10px] mt-1">Premium SaaS Multiples (20x-30x)</div>
                          </div>
                          
                          <div className="absolute bottom-4 right-4 text-right">
                            <div className="text-slate-500/50 font-bold uppercase tracking-widest text-xs">Legacy Quadrant</div>
                            <div className="text-slate-500/40 font-mono text-[10px] mt-1">EBITDA Multiples (10x-15x)</div>
                          </div>

                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center mt-8">
                            <div className="text-cyan-400/30 font-bold uppercase tracking-widest text-sm border border-cyan-500/20 px-4 py-2 rounded-full bg-cyan-900/20 backdrop-blur-sm shadow-[0_0_15px_rgba(34,211,238,0.1)]">The Tactical Edge (M&A Zone)</div>
                          </div>
                          
                          {/* Axis Lines & Grids */}
                          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-700/50" />
                          <div className="absolute top-1/2 left-0 right-0 h-px bg-slate-700/50" />
                        </div>

                        {/* Data Points - Neo Primes */}
                        {executiveScatterNeoPrimes.map((p, i) => {
                          const isDimmed = highlightedSegment && p.segment !== highlightedSegment;
                          return (
                          <div 
                            key={`np-${i}`} 
                            className={`absolute flex flex-col items-center -translate-x-1/2 -translate-y-1/2 group cursor-pointer transition-all duration-500 ${isDimmed ? 'opacity-10 grayscale scale-75 z-0' : 'z-20 hover:z-50'}`}
                            style={{ left: `${p.x * 10}%`, bottom: `${p.y * 10}%` }}
                            onClick={() => setActiveCompany(p.company)}
                          >
                            <div 
                              className="rounded-full bg-fuchsia-500/80 border border-fuchsia-300 shadow-[0_0_10px_rgba(217,70,239,0.5)] transition-transform group-hover:scale-125"
                              style={{ width: p.size, height: p.size }}
                            />
                            <span className="absolute top-full mt-1 text-[10px] text-slate-300 font-medium whitespace-nowrap bg-slate-900/80 px-1.5 py-0.5 rounded shadow group-hover:text-white transition-colors pointer-events-none">
                              {p.name}
                            </span>
                            {/* Rich Tooltip (Alpha Vector) */}
                            <div className={`absolute left-1/2 -translate-x-1/2 w-64 p-4 bg-[#0B1120] rounded-xl border border-slate-700 shadow-[0_20px_50px_rgba(0,0,0,0.9)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none group-hover:pointer-events-auto text-left ${p.y > 6 ? 'top-full mt-6' : 'bottom-full mb-6'}`}>
                               <div className="flex justify-between items-start mb-2 border-b border-slate-800 pb-2">
                                  <h5 className="font-bold text-white text-sm">{p.name}</h5>
                                  <span className="text-[9px] font-mono bg-slate-800 px-1.5 py-0.5 rounded text-slate-400 border border-slate-700">{p.company.hq}</span>
                               </div>
                               <div 
                                  className="text-[10px] font-mono uppercase tracking-widest mb-1.5 text-fuchsia-400 hover:text-white cursor-pointer inline-flex items-center gap-1 transition-colors"
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setHighlightedSegment(p.segment);
                                    document.getElementById('segment-dynamics-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  }}
                                  title="View Segment Thesis"
                               >
                                  [ THESIS: {p.segment} ] <ArrowRight className="w-3 h-3" />
                               </div>
                               <p className="text-xs text-slate-300 mb-3 leading-relaxed italic">"{renderWithJargon(p.alpha)}"</p>
                               <div className="flex justify-between items-center pt-2 border-t border-slate-800">
                                  <span className="text-[9px] text-slate-500 uppercase tracking-widest">Target Exit</span>
                                  <span className="text-[10px] font-bold text-slate-200">{renderWithJargon(p.multiple)}</span>
                               </div>
                            </div>
                          </div>
                        )})}

                        {/* Data Points - Growth Challengers */}
                        {executiveScatterChallengers.map((p, i) => {
                          const isDimmed = highlightedSegment && p.segment !== highlightedSegment;
                          return (
                          <div 
                            key={`gc-${i}`} 
                            className={`absolute flex flex-col items-center -translate-x-1/2 -translate-y-1/2 group cursor-pointer transition-all duration-500 ${isDimmed ? 'opacity-10 grayscale scale-75 z-0' : 'z-10 hover:z-50'}`}
                            style={{ left: `${p.x * 10}%`, bottom: `${p.y * 10}%` }}
                            onClick={() => setActiveCompany(p.company)}
                          >
                            <div 
                              className="rounded-full bg-cyan-500/80 border border-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.4)] transition-transform group-hover:scale-125"
                              style={{ width: p.size, height: p.size }}
                            />
                            <span className="absolute top-full mt-1 text-[10px] text-slate-300 font-medium whitespace-nowrap bg-slate-900/80 px-1.5 py-0.5 rounded shadow group-hover:text-white transition-colors pointer-events-none">
                              {p.name}
                            </span>
                            {/* Rich Tooltip (Alpha Vector) */}
                            <div className={`absolute left-1/2 -translate-x-1/2 w-64 p-4 bg-[#0B1120] rounded-xl border border-slate-700 shadow-[0_20px_50px_rgba(0,0,0,0.9)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none group-hover:pointer-events-auto text-left ${p.y > 6 ? 'top-full mt-6' : 'bottom-full mb-6'}`}>
                               <div className="flex justify-between items-start mb-2 border-b border-slate-800 pb-2">
                                  <h5 className="font-bold text-white text-sm">{p.name}</h5>
                                  <span className="text-[9px] font-mono bg-slate-800 px-1.5 py-0.5 rounded text-slate-400 border border-slate-700">{p.company.hq}</span>
                               </div>
                               <div 
                                  className="text-[10px] font-mono uppercase tracking-widest mb-1.5 text-cyan-400 hover:text-white cursor-pointer inline-flex items-center gap-1 transition-colors"
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setHighlightedSegment(p.segment);
                                    document.getElementById('segment-dynamics-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  }}
                                  title="View Segment Thesis"
                               >
                                  [ THESIS: {p.segment} ] <ArrowRight className="w-3 h-3" />
                               </div>
                               <p className="text-xs text-slate-300 mb-3 leading-relaxed italic">"{renderWithJargon(p.alpha)}"</p>
                               <div className="flex justify-between items-center pt-2 border-t border-slate-800">
                                  <span className="text-[9px] text-slate-500 uppercase tracking-widest">Target Exit</span>
                                  <span className="text-[10px] font-bold text-slate-200">{renderWithJargon(p.multiple)}</span>
                               </div>
                            </div>
                          </div>
                        )})}

                        {/* Data Points - Legacy */}
                        {executiveScatterLegacy.map((p, i) => {
                          const isDimmed = highlightedSegment && p.segment !== highlightedSegment;
                          return (
                          <div 
                            key={`l-${i}`} 
                            className={`absolute flex flex-col items-center -translate-x-1/2 -translate-y-1/2 group cursor-pointer transition-all duration-500 ${isDimmed ? 'opacity-10 grayscale scale-75 z-0' : 'z-10 hover:z-50'}`}
                            style={{ left: `${p.x * 10}%`, bottom: `${p.y * 10}%` }}
                            onClick={() => setActiveCompany(p.company)}
                          >
                            <div 
                              className="rounded-full bg-slate-600/90 border border-slate-400 shadow-[0_0_10px_rgba(148,163,184,0.3)] transition-transform group-hover:scale-110"
                              style={{ width: p.size, height: p.size }}
                            />
                            <span className="absolute bottom-full mb-1 text-[10px] text-slate-400 font-medium whitespace-nowrap bg-slate-900/80 px-1.5 py-0.5 rounded shadow group-hover:text-slate-200 transition-colors pointer-events-none">
                              {p.name}
                            </span>
                            {/* Rich Tooltip (Alpha Vector) */}
                            <div className={`absolute left-1/2 -translate-x-1/2 w-64 p-4 bg-[#0B1120] rounded-xl border border-slate-700 shadow-[0_20px_50px_rgba(0,0,0,0.9)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none group-hover:pointer-events-auto text-left ${p.y > 6 ? 'top-full mt-6' : 'bottom-full mb-6'}`}>
                               <div className="flex justify-between items-start mb-2 border-b border-slate-800 pb-2">
                                  <h5 className="font-bold text-white text-sm">{p.name}</h5>
                                  <span className="text-[9px] font-mono bg-slate-800 px-1.5 py-0.5 rounded text-slate-400 border border-slate-700">{p.company.hq}</span>
                               </div>
                               <div 
                                  className="text-[10px] font-mono uppercase tracking-widest mb-1.5 text-slate-400 hover:text-white cursor-pointer inline-flex items-center gap-1 transition-colors"
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setHighlightedSegment(p.segment);
                                    document.getElementById('segment-dynamics-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  }}
                                  title="View Segment Thesis"
                               >
                                  [ THESIS: {p.segment} ] <ArrowRight className="w-3 h-3" />
                               </div>
                               <p className="text-xs text-slate-300 mb-3 leading-relaxed italic">"{renderWithJargon(p.alpha)}"</p>
                               <div className="flex justify-between items-center pt-2 border-t border-slate-800">
                                  <span className="text-[9px] text-slate-500 uppercase tracking-widest">Target Exit</span>
                                  <span className="text-[10px] font-bold text-slate-200">{renderWithJargon(p.multiple)}</span>
                               </div>
                            </div>
                          </div>
                        )})}

                        {/* Custom Legend */}
                        <div className="absolute top-4 left-4 bg-slate-900/80 border border-slate-700 p-3 rounded-lg text-xs flex flex-col gap-2 shadow-lg backdrop-blur-sm z-30">
                          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-fuchsia-500/80 border border-fuchsia-300" /> <span className="text-slate-300">Neo-Primes</span></div>
                          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-cyan-500/80 border border-cyan-300" /> <span className="text-slate-300">Growth Challengers</span></div>
                          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-slate-600/90 border border-slate-400" /> <span className="text-slate-300">Legacy Primes</span></div>
                          <div className="border-t border-slate-700/60 pt-2 mt-1">
                            <span className="text-[9px] font-mono text-slate-500 uppercase tracking-widest leading-tight block">
                              * Bubble size indicates<br/>relative valuation scale
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* X-Axis Label */}
                      <div className="h-10 flex items-center justify-center shrink-0 pt-3">
                        <div className="text-[10px] text-slate-400 font-mono uppercase tracking-widest whitespace-nowrap">
                          Platform Maturity &amp; Govt Adoption &rarr;
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Competitive Archetype Summary Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    <div className="bg-fuchsia-900/10 border border-fuchsia-500/20 rounded-xl p-4 hover:bg-fuchsia-900/20 transition-colors">
                      <h4 className="text-fuchsia-400 font-bold text-sm mb-1">The Disrupters (Neo-Primes)</h4>
                      <p className="text-xs text-slate-400 leading-relaxed">Focus on winning new Programs of Record (PoRs) via software agility. Capitalizing on AI mandates to out-compete legacy timelines.</p>
                    </div>
                    <div className="bg-cyan-900/10 border border-cyan-500/20 rounded-xl p-4 hover:bg-cyan-900/20 transition-colors">
                      <h4 className="text-cyan-400 font-bold text-sm mb-1">The Specialists (Challengers)</h4>
                      <p className="text-xs text-slate-400 leading-relaxed">Focus on owning high-moat tactical niches (EW, PNT, 3D Mfg). Represent the primary M&A targets for incumbents.</p>
                    </div>
                    <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 hover:bg-slate-800/60 transition-colors">
                      <h4 className="text-slate-300 font-bold text-sm mb-1">The Incumbents (Legacy)</h4>
                      <p className="text-xs text-slate-400 leading-relaxed">Masters of scale re-tooling through M&A and CVC arms. Acquiring software capabilities to bridge their technical debt.</p>
                    </div>
                  </div>

                </div>
              </section>

              {/* 6 & 7. Financials Grid */}
              <section className="space-y-6">
                <div>
                  <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-2">FINANCIAL LANDSCAPE</div>
                  <h2 className="text-3xl font-bold text-slate-100">Capital Velocity: Funding &amp; Exit Logic</h2>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  
                  {/* Funding Chart */}
                  <div className="flex flex-col h-full">
                    <div className="mb-6 flex-shrink-0">
                        <h3 className="text-2xl font-bold text-slate-200">VC Funding Trends</h3>
                        <p className="text-slate-400 mt-2 text-sm leading-relaxed">Capital deployment stabilized in 2025/2026, shifting heavily towards Growth and Late Stage rounds as breakout category leaders emerged from the 2022 early-stage boom.</p>
                    </div>
                    <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6 shadow-md flex-grow flex flex-col justify-end relative">
                     <div className="absolute top-6 left-6 text-[10px] font-mono text-slate-500 uppercase tracking-widest">Capital Invested ($B)</div>
                     {/* Bars Container */}
                     <div className="flex items-end justify-between w-full h-[220px] mt-6 border-b border-slate-700 pb-2 relative z-10 px-2 md:px-6">
                        {executiveFundingData.map(d => {
                          const total = d.seed + d.series + d.growth;
                          return (
                            <div key={d.year} className="flex flex-col items-center gap-2 group w-1/6 max-w-[40px]">
                              {/* Stack */}
                              <div className="flex flex-col justify-end w-full h-[180px] gap-0.5">
                                {/* Growth */}
                                <div className="w-full bg-fuchsia-600 hover:brightness-110 transition-all cursor-crosshair rounded-t-sm" style={{ height: `${(d.growth / 9.0) * 100}%` }} title={`Growth/Late Stage: $${d.growth}B`} />
                                {/* Series */}
                                <div className="w-full bg-cyan-600 hover:brightness-110 transition-all cursor-crosshair" style={{ height: `${(d.series / 9.0) * 100}%` }} title={`Series B/C: $${d.series}B`} />
                                {/* Seed */}
                                <div className="w-full bg-emerald-600 hover:brightness-110 transition-all cursor-crosshair rounded-b-sm" style={{ height: `${(d.seed / 9.0) * 100}%` }} title={`Seed/Series A: $${d.seed}B`} />
                              </div>
                              <span className="text-[10px] font-mono text-slate-400">{d.year}</span>
                              
                              {/* Tooltip on hover */}
                              <div className="absolute bottom-[230px] opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900 border border-slate-700 rounded p-2 text-[10px] shadow-xl pointer-events-none whitespace-nowrap z-50 flex flex-col gap-1">
                                <strong className="text-white border-b border-slate-700 pb-1 mb-1">{d.year} Total: ${total.toFixed(1)}B</strong>
                                <span className="text-fuchsia-400">Growth: ${d.growth}B</span>
                                <span className="text-cyan-400">Series B/C: ${d.series}B</span>
                                <span className="text-emerald-400">Seed: ${d.seed}B</span>
                              </div>
                            </div>
                          );
                        })}
                     </div>
                     {/* Legend */}
                     <div className="flex flex-wrap justify-center gap-4 text-[10px] font-mono text-slate-500 mt-4">
                       <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-emerald-600 rounded-sm" /> Seed/Series A</div>
                       <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-cyan-600 rounded-sm" /> Series B/C</div>
                       <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-fuchsia-600 rounded-sm" /> Growth/Late Stage</div>
                     </div>
                    </div>
                  </div>

                  {/* M&A Chart */}
                  <div className="flex flex-col h-full">
                    <div className="mb-6 flex-shrink-0">
                        <h3 className="text-2xl font-bold text-slate-200">Strategic M&A Activity</h3>
                        <p className="text-slate-400 mt-2 text-sm leading-relaxed">Legacy primes are aggressively utilizing M&A to inject software and autonomy into their portfolios, driving a spike in deal volume and transaction size.</p>
                    </div>
                    <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6 shadow-md flex-grow flex flex-col justify-end relative">
                       <div className="absolute top-6 left-6 text-[10px] font-mono text-yellow-500 uppercase tracking-widest">Number of Deals</div>
                       <div className="absolute top-6 right-6 text-[10px] font-mono text-blue-400 uppercase tracking-widest">Value ($M)</div>
                       
                       <div className="w-full h-[220px] mt-6 border-b border-slate-700 pb-2 relative z-10 pt-4 px-2 md:px-6">
                          <div className="relative w-full h-full">
                            <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full overflow-visible preserve-3d" preserveAspectRatio="none">
                              {/* Y-axis grid lines (mock) */}
                              <line x1="0" y1="25" x2="100" y2="25" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
                              <line x1="0" y1="50" x2="100" y2="50" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
                              <line x1="0" y1="75" x2="100" y2="75" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />

                              {/* Deal Volume Line (Solid Gold) - max 50 */}
                              <path 
                                d={`M ${executiveMAData.map((d, i) => `${(i / 5) * 100} ${100 - (d.vol / 50) * 100}`).join(' L ')}`} 
                                fill="none" 
                                stroke="#fbbf24" 
                                strokeWidth="2.5" 
                                vectorEffect="non-scaling-stroke"
                              />
                              {/* Deal Value Line (Dashed Blue) - max 300 */}
                              <path 
                                d={`M ${executiveMAData.map((d, i) => `${(i / 5) * 100} ${100 - (d.val / 300) * 100}`).join(' L ')}`} 
                                fill="none" 
                                stroke="#3b82f6" 
                                strokeWidth="2.5" 
                                strokeDasharray="4,4" 
                                vectorEffect="non-scaling-stroke"
                              />
                            </svg>

                            {/* HTML Points Overlay (Remains perfectly round) */}
                            {executiveMAData.map((d, i) => (
                              <React.Fragment key={i}>
                                <div 
                                  className="absolute w-2.5 h-2.5 rounded-full bg-[#fbbf24] border-[1.5px] border-[#0f172a] shadow-[0_0_8px_rgba(251,191,36,0.6)] cursor-pointer hover:scale-150 transition-transform z-20"
                                  style={{ left: `${(i / 5) * 100}%`, top: `${100 - (d.vol / 50) * 100}%`, transform: 'translate(-50%, -50%)' }}
                                  title={`${d.year} Volume: ${d.vol} Deals`}
                                />
                                <div 
                                  className="absolute w-2.5 h-2.5 rounded-full bg-[#3b82f6] border-[1.5px] border-[#0f172a] shadow-[0_0_8px_rgba(59,130,246,0.6)] cursor-pointer hover:scale-150 transition-transform z-20"
                                  style={{ left: `${(i / 5) * 100}%`, top: `${100 - (d.val / 300) * 100}%`, transform: 'translate(-50%, -50%)' }}
                                  title={`${d.year} Value: $${d.val}M`}
                                />
                              </React.Fragment>
                            ))}
                          </div>
                          
                          {/* X Axis Labels overlay */}
                          <div className="absolute bottom-0 left-0 w-full flex justify-between px-2 md:px-6 translate-y-6">
                             {executiveMAData.map((d) => (
                               <div key={d.year} className="text-[10px] font-mono text-slate-400 w-8 text-center -translate-x-1/2 first:translate-x-0 last:-translate-x-full relative">
                                 {d.year}
                               </div>
                             ))}
                          </div>
                       </div>

                       {/* Legend */}
                       <div className="flex flex-wrap justify-center gap-6 text-[10px] font-mono text-slate-500 mt-10">
                         <div className="flex items-center gap-1.5"><div className="w-4 h-1 bg-yellow-400" /> Deal Volume (Count)</div>
                         <div className="flex items-center gap-1.5"><div className="w-4 h-1 border-b-2 border-dashed border-blue-500" /> Avg Deal Size ($M)</div>
                       </div>
                    </div>
                  </div>

                </div>
              </section>

              {/* 8. Thesis */}
              <section className="space-y-6">
                <div>
                  <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-2">THESIS</div>
                  <h2 className="text-3xl font-bold text-slate-100">The Mandate: High-Conviction Deployment</h2>
                  <p className="text-slate-400 mt-2">Actionable framework for capital allocation in the 2026-2030 cycle.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-[#111827] border border-slate-800 rounded-2xl p-8 border-l-4 border-l-indigo-500 hover:border-slate-700 transition-colors relative overflow-hidden group shadow-[0_4px_20px_rgba(0,0,0,0.3)]">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-indigo-900/20 to-transparent pointer-events-none" />
                    <h3 className="text-2xl font-bold text-indigo-400 mb-3 relative z-10">01. Prioritize Dual-Use Architectures</h3>
                    <p className="text-slate-300 leading-relaxed relative z-10 text-[15px]">
                      {renderWithJargon("Pure-play defense startups face the 'Valley of Death' in DoD procurement. The optimal risk-adjusted profile belongs to companies generating >40% commercial revenue (e.g., enterprise cyber, commercial space logistics, industrial autonomy) with hardened, spin-off federal capabilities. This ensures burn rate sustainability while awaiting large Programs of Record.")}
                    </p>
                  </div>

                  <div className="bg-[#111827] border border-slate-800 rounded-2xl p-8 border-l-4 border-l-blue-500 hover:border-slate-700 transition-colors relative overflow-hidden group shadow-[0_4px_20px_rgba(0,0,0,0.3)]">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-900/20 to-transparent pointer-events-none" />
                    <h3 className="text-2xl font-bold text-blue-400 mb-3 relative z-10">02. Software Over Hardware (Layer 2 Dominance)</h3>
                    <p className="text-slate-300 leading-relaxed relative z-10 text-[15px]">
                      {renderWithJargon("Hardware is rapidly commoditizing. The deepest economic moats reside in the OS and Data layers—specifically software that commands attritable hardware, fuses multi-modal sensor data, or translates legacy systems into interoperable networks. Target businesses with >70% gross margins indicative of software scale.")}
                    </p>
                  </div>

                  <div className="bg-[#111827] border border-slate-800 rounded-2xl p-8 border-l-4 border-l-teal-500 hover:border-slate-700 transition-colors relative overflow-hidden group shadow-[0_4px_20px_rgba(0,0,0,0.3)]">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-teal-900/20 to-transparent pointer-events-none" />
                    <h3 className="text-2xl font-bold text-teal-400 mb-3 relative z-10">03. The Defense Prime M&A Premium</h3>
                    <p className="text-slate-300 leading-relaxed relative z-10 text-[15px]">
                      {renderWithJargon("Do not underwrite based solely on independent IPOs. Legacy primes are no longer just competing with each other—they are losing Tier 1 Enterprise contracts to venture-backed Neoprimes. This shifts M&A from a 'nice to have' to an 'existential imperative,' justifying the 20x-30x valuation multiples for startups that bridge their structural software deficits.")}
                    </p>
                  </div>

                  <div className="bg-[#111827] border border-slate-800 rounded-2xl p-8 border-l-4 border-l-emerald-500 hover:border-slate-700 transition-colors relative overflow-hidden group shadow-[0_4px_20px_rgba(0,0,0,0.3)]">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-emerald-900/20 to-transparent pointer-events-none" />
                    <h3 className="text-2xl font-bold text-emerald-400 mb-3 relative z-10">04. Geopolitical Friend-Shoring Tech</h3>
                    <p className="text-slate-300 leading-relaxed relative z-10 text-[15px]">
                      {renderWithJargon("AUKUS and NATO interoperability standards are creating a unified allied procurement block. Supply chain tech, critical minerals processing, and secure microelectronics that decouple allied forces from adversarial dependencies represent a highly defensible, structurally mandated growth vector.")}
                    </p>
                  </div>

                  <div className="bg-[#111827] border border-slate-800 rounded-2xl p-8 border-l-4 border-l-slate-500 hover:border-slate-700 transition-colors relative overflow-hidden group shadow-[0_4px_20px_rgba(0,0,0,0.3)] md:col-span-2">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-slate-800/40 to-transparent pointer-events-none" />
                    <h3 className="text-2xl font-bold text-slate-300 mb-3 relative z-10">05. The Sovereign Localization Premium</h3>
                    <p className="text-slate-300 leading-relaxed relative z-10 text-[15px]">
                      {renderWithJargon("GCC and NATO-aligned nations are now paying a strategic premium for companies willing to localize manufacturing and IP transfer. This represents a massive 'alpha' driver for investors targeting non-US markets, where sovereign government subsidies (e.g., EDGE Group or PIF-backed ventures) can significantly de-risk the initial CAPEX and guarantee long-term PoRs.")}
                    </p>
                  </div>
                </div>
              </section>

              <div className="flex justify-center pt-8">
                <button 
                  onClick={() => setActiveView('setup')}
                  className="bg-slate-800 hover:bg-slate-700 text-white font-semibold py-3 px-8 rounded-full flex items-center gap-3 transition-all hover:scale-105 shadow-lg border border-slate-700"
                >
                  Dive Deeper: Strategic Drivers <ArrowRight className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              {/* Intelligence Grounding */}
              <div className="mt-10 pt-12 border-t border-slate-800">
                <div className="flex flex-col items-center text-center mb-10">
                  <div className="inline-flex items-center gap-2 text-amber-500/80 text-xs font-mono font-bold tracking-widest uppercase mb-3 bg-amber-950/20 px-3 py-1.5 rounded-lg border border-amber-500/20">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Intelligence Grounding
                  </div>
                  <h3 className="text-3xl font-serif font-bold text-white mb-4">Human-in-the-Loop Validation</h3>
                  <p className="text-slate-400 max-w-3xl leading-relaxed">
                    Agentic sourcing provides unmatched breadth, but defense investing requires deep political and operational judgment. This baseline report was ground-truth validated by our <span className="text-slate-200 font-semibold">Lead Defense Curators</span>. An additional roster of pre-vetted domain specialists is on standby via the <span className="text-slate-200 font-semibold">Knowledge Ridge Expert Network</span> for bespoke deep-dives.
                  </p>
                </div>

                <div className="mb-8" id="strategic-council-section">
                  <h4 className="text-sm font-bold text-indigo-400/90 uppercase tracking-widest mb-6 border-b border-slate-800/60 pb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Frontier Strategic Council
                  </h4>
                  
                  {/* VIP Grid Carousel */}
                  <div className="relative group/carousel mb-8">
                    <div 
                      ref={councilScrollRef}
                      className="flex overflow-x-auto snap-x snap-mandatory gap-6 pt-4 pb-6 -mt-4 -mb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden scroll-smooth"
                    >
                    
                    {/* Curator 1: Dr. Martin Crilly */}
                    <div className="w-full md:w-[calc(50%-12px)] xl:w-[calc(33.333%-16px)] shrink-0 snap-start bg-gradient-to-br from-amber-900/20 to-[#111827] border border-amber-500/40 rounded-xl p-6 flex flex-col relative overflow-hidden group shadow-[0_0_25px_rgba(245,158,11,0.08)] hover:border-amber-500/70 transition-colors transform lg:-translate-y-2">
                      <div className="absolute top-0 right-0 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
                      <div className="flex items-start justify-between mb-4 relative z-10">
                        <img src="/images/profile_martin.jpg" alt="Dr. Martin Crilly" className="w-20 h-20 rounded-full border-2 border-amber-500/60 shadow-lg object-cover" />
                        <span className="px-2.5 py-1 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-[9px] font-mono font-bold uppercase tracking-widest rounded whitespace-nowrap">
                          Architectural Auth
                        </span>
                      </div>
                      <div className="relative z-10 flex-1 flex flex-col">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-xl font-bold text-white">Dr. Martin Crilly</h3>
                          <a href="https://www.linkedin.com/in/martincrilly/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-amber-400 transition-colors"><Linkedin className="w-4 h-4" /></a>
                        </div>
                        <div className="text-xs font-semibold text-amber-200/80 mb-4">Former CTO &amp; Advisor (BAE) | CAIO @ Emirates</div>
                        
                        <div className="mb-4">
                          <div className="text-[9px] font-mono text-amber-500/70 uppercase tracking-widest mb-1.5">Strategic Pedigree</div>
                          <div className="flex flex-wrap gap-1.5">
                            <span className="px-2 py-1 bg-amber-900/20 border border-amber-500/30 rounded text-[10px] font-bold text-amber-300">Emirates</span>
                            <span className="px-2 py-1 bg-amber-900/20 border border-amber-500/30 rounded text-[10px] font-bold text-amber-300">dnata</span>
                            <span className="px-2 py-1 bg-slate-800/80 border border-slate-700 rounded text-[10px] font-semibold text-slate-300">BAE Systems</span>
                          </div>
                        </div>

                        <p className="text-[13px] text-slate-400 leading-relaxed mt-auto">
                          Advising Ministries and C-suites on $12bn+ of defense technology investments. Validates sovereign M&A strategy, legacy prime integration, and dual-use technological adoption for global operations.
                        </p>
                      </div>
                    </div>

                    {/* Curator 2: Vivan A. */}
                    <div className="w-full md:w-[calc(50%-12px)] xl:w-[calc(33.333%-16px)] shrink-0 snap-start bg-gradient-to-br from-cyan-900/20 to-[#111827] border border-cyan-500/30 rounded-xl p-6 flex flex-col relative overflow-hidden group shadow-[0_0_20px_rgba(8,145,178,0.05)] hover:border-cyan-500/60 transition-colors">
                      <div className="absolute top-0 right-0 w-48 h-48 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
                      <div className="flex items-start justify-between mb-4 relative z-10">
                        <img src="/images/profile_vivan.jpg" alt="Vivan A." className="w-16 h-16 rounded-full border-2 border-cyan-500/50 shadow-md object-cover" />
                        <span className="px-2.5 py-1 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-[9px] font-mono font-bold uppercase tracking-widest rounded whitespace-nowrap">
                          Physical AI
                        </span>
                      </div>
                      <div className="relative z-10 flex-1 flex flex-col">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-bold text-white">Vivan A.</h3>
                          <a href="https://www.linkedin.com/in/vivan-amin/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-cyan-400 transition-colors"><Linkedin className="w-3.5 h-3.5" /></a>
                        </div>
                        <div className="text-xs font-semibold text-cyan-200/80 mb-4">Director, Physical AI @ Microsoft Research</div>
                        
                        <div className="mb-4">
                          <div className="text-[9px] font-mono text-cyan-500/70 uppercase tracking-widest mb-1.5">Strategic Pedigree</div>
                          <div className="flex flex-wrap gap-1.5">
                            <span className="px-2 py-1 bg-cyan-900/20 border border-cyan-500/30 rounded text-[10px] font-bold text-cyan-300">Microsoft</span>
                            <span className="px-2 py-1 bg-slate-800/80 border border-slate-700 rounded text-[10px] font-semibold text-slate-300">Lockheed Martin</span>
                            <span className="px-2 py-1 bg-slate-800/80 border border-slate-700 rounded text-[10px] font-semibold text-slate-300">Boeing</span>
                            <span className="px-2 py-1 bg-slate-800/80 border border-slate-700 rounded text-[10px] font-semibold text-slate-300">Honeywell</span>
                          </div>
                        </div>

                        <p className="text-[13px] text-slate-400 leading-relaxed mt-auto">
                          Turns frontier robotics and autonomous systems research into products that work in the real world. Defines the next generation of Responsible AI principles for agentic memory and Physical AI.
                        </p>
                      </div>
                    </div>

                    {/* Curator 3: Frank R. Carus */}
                    <div className="w-full md:w-[calc(50%-12px)] xl:w-[calc(33.333%-16px)] shrink-0 snap-start bg-gradient-to-br from-emerald-900/20 to-[#111827] border border-emerald-500/30 rounded-xl p-6 flex flex-col relative overflow-hidden group shadow-[0_0_20px_rgba(16,185,129,0.05)] hover:border-emerald-500/60 transition-colors">
                      <div className="absolute top-0 right-0 w-48 h-48 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />
                      <div className="flex items-start justify-between mb-4 relative z-10">
                        <img src="/images/profile_frank.jpg" alt="Frank R. Carus" className="w-16 h-16 rounded-full border-2 border-emerald-500/50 shadow-md object-cover" />
                        <span className="px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[9px] font-mono font-bold uppercase tracking-widest rounded whitespace-nowrap">
                          Aerospace
                        </span>
                      </div>
                      <div className="relative z-10 flex-1 flex flex-col">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-bold text-white">Frank R. Carus</h3>
                          <a href="https://www.linkedin.com/in/frank-r-carus-95b98a66/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-emerald-400 transition-colors"><Linkedin className="w-3.5 h-3.5" /></a>
                        </div>
                        <div className="text-xs font-semibold text-emerald-200/80 mb-4">Ex-VP F-35 Program @ Northrop Grumman</div>
                        
                        <div className="mb-4">
                          <div className="text-[9px] font-mono text-emerald-500/70 uppercase tracking-widest mb-1.5">Strategic Pedigree</div>
                          <div className="flex flex-wrap gap-1.5">
                            <span className="px-2 py-1 bg-emerald-900/20 border border-emerald-500/30 rounded text-[10px] font-bold text-emerald-300">Northrop Grumman</span>
                            <span className="px-2 py-1 bg-slate-800/80 border border-slate-700 rounded text-[10px] font-semibold text-slate-300">F-35 Lightning II</span>
                            <span className="px-2 py-1 bg-slate-800/80 border border-slate-700 rounded text-[10px] font-semibold text-slate-300">Global Supply Chain</span>
                          </div>
                        </div>

                        <p className="text-[13px] text-slate-400 leading-relaxed mt-auto">
                          Aerospace Executive with 26+ years at Northrop Grumman. Expertise in air, space, and missile platforms, validating massive integration programs and tier-1 supplier networks.
                        </p>
                      </div>
                    </div>

                    {/* Curator 4: Gethin Lewis */}
                    <div className="w-full md:w-[calc(50%-12px)] xl:w-[calc(33.333%-16px)] shrink-0 snap-start bg-gradient-to-br from-rose-900/20 to-[#111827] border border-rose-500/30 rounded-xl p-6 flex flex-col relative overflow-hidden group shadow-[0_0_20px_rgba(225,29,72,0.05)] hover:border-rose-500/60 transition-colors">
                      <div className="absolute top-0 right-0 w-48 h-48 bg-rose-500/5 rounded-full blur-3xl pointer-events-none" />
                      <div className="flex items-start justify-between mb-4 relative z-10">
                        <img src="/images/profile_gethin.jpg" alt="Gethin Lewis" className="w-16 h-16 rounded-full border-2 border-rose-500/50 shadow-md object-cover" />
                        <span className="px-2.5 py-1 bg-rose-500/10 border border-rose-500/30 text-rose-400 text-[9px] font-mono font-bold uppercase tracking-widest rounded whitespace-nowrap">
                          Prime Integration
                        </span>
                      </div>
                      <div className="relative z-10 flex-1 flex flex-col">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-bold text-white">Gethin Lewis</h3>
                        </div>
                        <div className="text-xs font-semibold text-rose-200/80 mb-4">Defence Exec | AI &amp; Autonomous Systems</div>
                        
                        <div className="mb-4">
                          <div className="text-[9px] font-mono text-rose-500/70 uppercase tracking-widest mb-1.5">Strategic Pedigree</div>
                          <div className="flex flex-wrap gap-1.5">
                            <span className="px-2 py-1 bg-rose-900/20 border border-rose-500/30 rounded text-[10px] font-bold text-rose-300">Helsing</span>
                            <span className="px-2 py-1 bg-slate-800/80 border border-slate-700 rounded text-[10px] font-semibold text-slate-300">Lockheed Martin</span>
                            <span className="px-2 py-1 bg-slate-800/80 border border-slate-700 rounded text-[10px] font-semibold text-slate-300">BAE Systems</span>
                          </div>
                        </div>

                        <p className="text-[13px] text-slate-400 leading-relaxed mt-auto">
                          Closes the gap between proven technology and prime contracts. Shapes propositions, secures new business, and actively navigates complex UK/European defence procurement channels.
                        </p>
                      </div>
                    </div>
                    
                    </div>
                    
                    {/* Carousel Controls */}
                    <button 
                      onClick={() => scrollCouncil(-1)}
                      className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-5 z-20 p-2.5 bg-[#0B1120] border border-slate-700/80 text-slate-300 rounded-full shadow-[0_0_20px_rgba(0,0,0,0.8)] opacity-0 group-hover/carousel:opacity-100 transition-all duration-300 hover:bg-slate-800 hover:text-white hover:scale-110 hidden md:flex items-center justify-center focus:outline-none"
                      title="Previous Expert"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => scrollCouncil(1)}
                      className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-5 z-20 p-2.5 bg-[#0B1120] border border-slate-700/80 text-slate-300 rounded-full shadow-[0_0_20px_rgba(0,0,0,0.8)] opacity-0 group-hover/carousel:opacity-100 transition-all duration-300 hover:bg-slate-800 hover:text-white hover:scale-110 hidden md:flex items-center justify-center focus:outline-none"
                      title="Next Expert"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="mt-4">
                    <button 
                      onClick={() => setIsAdvisoryNetworkOpen(!isAdvisoryNetworkOpen)}
                      className="w-full flex items-center justify-between text-sm font-bold text-slate-500 uppercase tracking-widest mb-4 border-b border-slate-800/60 pb-2 hover:text-slate-300 transition-colors group"
                    >
                      <span className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4" /> Global Defense Advisory Panel
                      </span>
                      <div className="p-1 rounded-lg bg-slate-900/50 group-hover:bg-slate-800 transition-colors">
                        {isAdvisoryNetworkOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </div>
                    </button>
                    
                    {isAdvisoryNetworkOpen && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 animate-fade-in">
                        {krExperts.map((expert) => (
                          <div key={expert.id} className="bg-[#111827]/50 border border-slate-800/80 rounded-xl p-4 flex flex-col hover:border-blue-500/30 transition-colors group/expert opacity-80 hover:opacity-100">
                            <div className="flex justify-between items-start mb-3 border-b border-slate-800/60 pb-2">
                              <span className="text-[10px] font-mono text-slate-500">EXPERT {expert.id}</span>
                              <UserCheck className="w-3.5 h-3.5 text-slate-600 group-hover/expert:text-blue-400 transition-colors" />
                            </div>
                            <div className="font-bold text-sm text-slate-300 mb-1 leading-snug">{expert.role}</div>
                            <div className="text-xs text-slate-500 mt-auto pt-2 font-medium">{expert.domain}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* VIEW: Paradigm Shift (Macro Themes) */}
          {activeView === 'setup' && (
            <div className="max-w-6xl mx-auto space-y-12 pb-12">
              
              <div className="border-b border-slate-800 pb-6">
                <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-3">
                  02 // Strategic Drivers
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <TrendingUp className="w-8 h-8 text-blue-400 shrink-0" />
                  <h2 className="text-3xl font-serif font-bold text-white">The Paradigm Shift: From Exquisite Hardware to Attritable Mass</h2>
                </div>
                <p className="text-slate-400 text-lg max-w-4xl">
                  The 2026 defense landscape is shaped by a permanent realignment of global security priorities. Governments have shifted from episodic responses to conflicts toward a "permanent strategy" where military capability is the center of economic policy.
                </p>
              </div>

              {/* NEW SECTION: Market Definition (from PDF) */}
              <div className="pt-2">
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-6 flex items-center gap-2">
                  <BookOpen className="w-4 h-4"/> Market Definition: The 3 Tiers of Defense Tech
                </h3>
                
                <p className="text-slate-300 leading-relaxed mb-8">
                  The taxonomy of the defense sector has historically been opaque, often conflating total military spending with technology procurement. In 2026, it is critical for investors to differentiate between three distinct tiers of the market, as they carry vastly different growth and valuation profiles.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Tier 1 */}
                  <div className="bg-[#111827] border border-slate-800 rounded-xl p-6 hover:border-slate-600 transition-colors flex flex-col">
                    <div className="text-xs font-mono text-slate-500 uppercase tracking-widest mb-2">Tier 1</div>
                    <h4 className="text-lg font-bold text-white mb-3">Defense Tech (General)</h4>
                    <p className="text-sm text-slate-400 leading-relaxed mb-6 flex-grow">
                      {renderWithJargon("The broad spectrum of products built to deliver military capabilities. Projected to reach $542.72B in 2026 (7.1% CAGR). However, this includes non-tech services and sustainment activities that dilute the true growth profile.")}
                    </p>
                    <div className="mt-auto pt-4 border-t border-slate-800 space-y-2.5">
                      <div className="flex justify-between items-center"><span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">Valuation</span> <span className="text-xs font-bold text-slate-300">10x-15x Earnings</span></div>
                      <div className="flex justify-between items-center"><span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">Cycle Time</span> <span className="text-xs font-bold text-slate-300">Decades (10-20 yrs)</span></div>
                      <div className="flex justify-between items-center"><span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">Production</span> <span className="text-xs font-bold text-slate-300 truncate ml-2">Bespoke / Low-volume</span></div>
                    </div>
                  </div>
                  
                  {/* Tier 2 */}
                  <div className="bg-[#111827] border border-slate-800 rounded-xl p-6 hover:border-slate-600 transition-colors flex flex-col">
                    <div className="text-xs font-mono text-slate-500 uppercase tracking-widest mb-2">Tier 2</div>
                    <h4 className="text-lg font-bold text-white mb-3">Next Gen Defense</h4>
                    <p className="text-sm text-slate-400 leading-relaxed mb-6 flex-grow">
                      {renderWithJargon("A transitional phase where legacy platforms are retrofitted with advanced sensors, autonomy, and AI (e.g., NGAD, CCA). Focused on creating a hybrid of manned and unmanned systems, but growth remains tethered to legacy hardware lifecycles.")}
                    </p>
                    <div className="mt-auto pt-4 border-t border-slate-800 space-y-2.5">
                      <div className="flex justify-between items-center"><span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">Valuation</span> <span className="text-xs font-bold text-slate-300">15x-20x Earnings</span></div>
                      <div className="flex justify-between items-center"><span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">Cycle Time</span> <span className="text-xs font-bold text-slate-300">5-10 years</span></div>
                      <div className="flex justify-between items-center"><span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">Production</span> <span className="text-xs font-bold text-slate-300 truncate ml-2">Advanced Mfg</span></div>
                    </div>
                  </div>
                  
                  {/* Tier 3 */}
                  <div className="bg-blue-900/10 border border-blue-500/30 rounded-xl p-6 relative overflow-hidden shadow-[0_0_20px_rgba(59,130,246,0.05)] flex flex-col">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-bl-full -z-10" />
                    <div className="text-xs font-mono text-blue-400 uppercase tracking-widest mb-2 font-bold">Tier 3 (Alpha Target)</div>
                    <h4 className="text-lg font-bold text-white mb-3">Defense Tech 2.0</h4>
                    <p className="text-sm text-slate-300 leading-relaxed mb-6 flex-grow">
                      {renderWithJargon("Software-first and AI-native from inception. The physical platform is a commodity; value resides in the software 'brain'. Driven by Neo-Primes using MOSA. Core sub-segments are expanding at 11.4% annually.")}
                    </p>
                    <div className="mt-auto pt-4 border-t border-blue-500/20 space-y-2.5">
                      <div className="flex justify-between items-center"><span className="text-[10px] font-mono text-blue-400/70 uppercase tracking-wider">Valuation</span> <span className="text-xs font-bold text-blue-300">25x-30x (Tech-like)</span></div>
                      <div className="flex justify-between items-center"><span className="text-[10px] font-mono text-blue-400/70 uppercase tracking-wider">Cycle Time</span> <span className="text-xs font-bold text-blue-100">Weeks (Agile/DevOps)</span></div>
                      <div className="flex justify-between items-center"><span className="text-[10px] font-mono text-blue-400/70 uppercase tracking-wider">Production</span> <span className="text-xs font-bold text-blue-100 truncate ml-2" title="Enterprise Scale / $B+ Production Programs">Enterprise Scale / $B+ Production Programs</span></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-10">
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-6 flex items-center gap-2">
                  <Globe className="w-4 h-4"/> 2026 Macro Trends
                </h3>
                <div className="space-y-6">
                  {/* Trend 1 */}
                  <div className="bg-[#111827] border border-slate-800 rounded-2xl p-6 md:p-8 relative overflow-hidden group hover:border-indigo-900/50 transition-colors">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-bl-full -z-10 transition-transform group-hover:scale-110" />
                    <div className="flex flex-col md:flex-row gap-6 md:gap-10">
                      <div className="md:w-1/3 shrink-0">
                        <BrainCircuit className="w-8 h-8 text-indigo-400 mb-4" />
                        <h4 className="text-xl font-bold text-white mb-3">The AI-First Mandate &amp; "Wartime" Bureaucracy</h4>
                        <p className="text-sm text-slate-400 leading-relaxed">
                          {renderWithJargon("On January 9, 2026, the Pentagon issued a historic AI Strategy memorandum mandating an 'AI-first' approach across all components. This goes beyond existing workflows, requiring the reimagining of whole operational concepts with AI as the foundation.")}
                        </p>
                      </div>
                      <div className="md:w-2/3 flex flex-col justify-center space-y-5 border-t md:border-t-0 md:border-l border-slate-800 pt-5 md:pt-0 md:pl-8">
                        <div className="flex items-start gap-3">
                          <div className="mt-1 text-indigo-500"><Shield className="w-4 h-4"/></div>
                          <div>
                            <div className="text-white font-semibold text-sm">The "Wartime CDAO" Footing</div>
                            <div className="text-slate-400 text-sm mt-1">{renderWithJargon("The Chief Digital and AI Office transitioned to a wartime footing, establishing a monthly 'Barrier Removal Board' with authority to aggressively waive non-statutory blockers like slow ATOs and data-sharing hurdles.")}</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="mt-1 text-indigo-500"><Zap className="w-4 h-4"/></div>
                          <div>
                            <div className="text-white font-semibold text-sm">30-Day Deployment Standard</div>
                            <div className="text-slate-400 text-sm mt-1">{renderWithJargon("A massive new procurement criterion mandating that the latest commercial AI models must be available to military users within 30 days of public release, forcing commercial-grade DevOps.")}</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="mt-1 text-indigo-500"><TrendingUp className="w-4 h-4"/></div>
                          <div>
                            <div className="text-indigo-300 font-semibold text-sm">Investment Alpha: The "Software Plumbing"</div>
                            <div className="text-slate-400 text-sm mt-1">{renderWithJargon("This shift creates significant demand and premium multiples for companies capable of building the required APIs and containerized stacks to integrate sensitive data without brittle point solutions.")}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Trend 2 */}
                  <div className="bg-[#111827] border border-slate-800 rounded-2xl p-6 md:p-8 relative overflow-hidden group hover:border-emerald-900/50 transition-colors">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-bl-full -z-10 transition-transform group-hover:scale-110" />
                    <div className="flex flex-col md:flex-row gap-6 md:gap-10">
                      <div className="md:w-1/3 shrink-0">
                        <Factory className="w-8 h-8 text-emerald-400 mb-4" />
                        <h4 className="text-xl font-bold text-white mb-3">Attritable Mass &amp; Scalable Producibility</h4>
                        <p className="text-sm text-slate-400 leading-relaxed">
                          {renderWithJargon("Recent conflicts have proven that 'exquisite' systems are too scarce and vulnerable. Western MODs are now prioritizing 'attritable mass': common, affordable, modular form factors that can tolerate high losses without degrading the mission network.")}
                        </p>
                      </div>
                      <div className="md:w-2/3 flex flex-col justify-center space-y-5 border-t md:border-t-0 md:border-l border-slate-800 pt-5 md:pt-0 md:pl-8">
                        <div className="flex items-start gap-3">
                          <div className="mt-1 text-emerald-500"><Cpu className="w-4 h-4"/></div>
                          <div>
                            <div className="text-white font-semibold text-sm">Software-Augmented Manufacturing</div>
                            <div className="text-slate-400 text-sm mt-1">{renderWithJargon("Companies like Hadrian and Machina Labs are receiving significant venture backing to build automated, AI-robotic factories capable of slashing drone production times from weeks to hours.")}</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="mt-1 text-emerald-500"><Target className="w-4 h-4"/></div>
                          <div>
                            <div className="text-emerald-300 font-semibold text-sm">Investment Alpha: Execution over Invention</div>
                            <div className="text-slate-400 text-sm mt-1">{renderWithJargon("In 2026, 'execution, not invention, will determine returns.' Capital is aggressively flowing toward companies that can convert industrial facilities into repeatable, high-speed output.")}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Trend 3 */}
                  <div className="bg-[#111827] border border-slate-800 rounded-2xl p-6 md:p-8 relative overflow-hidden group hover:border-rose-900/50 transition-colors">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/5 rounded-bl-full -z-10 transition-transform group-hover:scale-110" />
                    <div className="flex flex-col md:flex-row gap-6 md:gap-10">
                      <div className="md:w-1/3 shrink-0">
                        <Globe className="w-8 h-8 text-rose-400 mb-4" />
                        <h4 className="text-xl font-bold text-white mb-3">Geopolitical Realignment &amp; Sovereignty</h4>
                        <p className="text-sm text-slate-400 leading-relaxed">
                          {renderWithJargon("Geopolitics continues to extend the defense spending cycle beyond immediate conflicts. Structural priorities across the U.S., Europe, and Asia are driving a ~5% baseline growth in defense spending through 2026.")}
                        </p>
                      </div>
                      <div className="md:w-2/3 flex flex-col justify-center space-y-5 border-t md:border-t-0 md:border-l border-slate-800 pt-5 md:pt-0 md:pl-8">
                        <div className="flex items-start gap-3">
                          <div className="mt-1 text-rose-500"><Landmark className="w-4 h-4"/></div>
                          <div>
                            <div className="text-white font-semibold text-sm">Europe's "Strategic Autonomy"</div>
                            <div className="text-slate-400 text-sm mt-1">{renderWithJargon("As the U.S. shifts resources, regional governments are allocating nearly €800 billion toward defense by 2030, explicitly driven by NATO guidance targeting 5% of GDP by 2035.")}</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="mt-1 text-rose-500"><Radio className="w-4 h-4"/></div>
                          <div>
                            <div className="text-white font-semibold text-sm">Arctic Blind Spots (FROSTY Program)</div>
                            <div className="text-slate-400 text-sm mt-1">{renderWithJargon("Escalating tensions in the North have led to rapid deployment of the 'FROSTY' program, creating massive demand for cold-weather sensing and high-latitude ISR capabilities.")}</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="mt-1 text-rose-500"><MapPin className="w-4 h-4"/></div>
                          <div>
                            <div className="text-white font-semibold text-sm">Asia-Pacific Rearmament &amp; Insourcing</div>
                            <div className="text-slate-400 text-sm mt-1">{renderWithJargon("Nations are recognizing the fatal risk of outsourced supply chains in a peer-conflict scenario, moving quickly to develop, fund, and protect their own sovereign critical technologies.")}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Trend 4 */}
                  <div className="bg-[#111827] border border-slate-800 rounded-2xl p-6 md:p-8 relative overflow-hidden group hover:border-amber-900/50 transition-colors">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-bl-full -z-10 transition-transform group-hover:scale-110" />
                    <div className="flex flex-col md:flex-row gap-6 md:gap-10">
                      <div className="md:w-1/3 shrink-0">
                        <Award className="w-8 h-8 text-amber-400 mb-4" />
                        <h4 className="text-xl font-bold text-white mb-3">The Neo-Prime Production Inflection</h4>
                        <p className="text-sm text-slate-400 leading-relaxed">
                          {renderWithJargon("The MOD/Global Defense is no longer just experimenting with startups; it is treating software-first firms as Tier 1 Strategic Suppliers. The era of 'innovation theater' has been replaced by Institutional Validation at scale.")}
                        </p>
                      </div>
                      <div className="md:w-2/3 flex flex-col justify-center space-y-5 border-t md:border-t-0 md:border-l border-slate-800 pt-5 md:pt-0 md:pl-8">
                        <div className="flex items-start gap-3">
                          <div className="mt-1 text-amber-500"><Rocket className="w-4 h-4"/></div>
                          <div>
                            <div className="text-white font-semibold text-sm">The $25B "Enterprise" Signal</div>
                            <div className="text-slate-400 text-sm mt-1">{renderWithJargon("The Eglin AFB Enterprise contract explicitly targets autonomous, high-volume munitions, representing the first structural break in the production monopoly traditionally held by the Big 5.")}</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="mt-1 text-amber-500"><Scale className="w-4 h-4"/></div>
                          <div>
                            <div className="text-amber-300 font-semibold text-sm">Investment Alpha: Shifting the Risk</div>
                            <div className="text-slate-400 text-sm mt-1">{renderWithJargon("For investors, this means the 'Neo-Prime' category is no longer a speculative venture bet; it is a Strategic Infrastructure bet. Risk underwriting has shifted from 'Can they win a contract?' to 'Can they scale the supply chain?'")}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-12">
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-6 flex items-center gap-2">
                  <Activity className="w-4 h-4"/> Industrial Response: The Divergence
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-[#111827] border border-slate-800 rounded-2xl p-8 relative overflow-hidden group hover:border-slate-700 transition-colors">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-slate-800/20 rounded-bl-full -z-10" />
                    <h3 className="text-slate-500 font-mono text-sm tracking-widest mb-2">DEFENSE 1.0</h3>
                    <h2 className="text-2xl font-serif font-bold text-white mb-6">Legacy Primes Pivot</h2>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <Briefcase className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
                        <div>
                          <div className="text-white font-medium">Strategic Incubation &amp; CVC</div>
                          <div className="text-slate-400 text-sm">{renderWithJargon("Firms like Lockheed and BAE are spinning up internal incubators (Launchpad, AI Fight Club) and deploying massive CVC funds to de-risk startup adoption.")}</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Network className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
                        <div>
                          <div className="text-white font-medium">Open Architectures (MOSA)</div>
                          <div className="text-slate-400 text-sm">Acknowledging the limits of proprietary silos, primes are creating "app store" models to easily integrate third-party software on legacy platforms.</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-[#1e293b] to-[#0f172a] border border-blue-500/30 rounded-2xl p-8 relative overflow-hidden shadow-[0_0_30px_rgba(59,130,246,0.1)]">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-bl-full blur-2xl" />
                    <h3 className="text-blue-400 font-mono text-sm tracking-widest mb-2 flex items-center gap-2"><Zap className="w-4 h-4"/> DEFENSE 2.0</h3>
                    <h2 className="text-2xl font-serif font-bold text-white mb-6">Venture-Fueled Neoprimes</h2>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <BrainCircuit className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                        <div>
                          <div className="text-white font-medium">Shift from R&D to Mega-PORs</div>
                          <div className="text-blue-200/70 text-sm">{renderWithJargon("Backed by massive private capital, Neoprimes are vertically scaling into mainstream production readiness. The Eglin AFB Enterprise contract proves autonomous munitions have shifted from emerging tech to scalable $B+ supply chains.")}</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <TrendingUp className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                        <div>
                          <div className="text-white font-medium">High-Margin IP Multiples</div>
                          <div className="text-blue-200/70 text-sm">Trades on Forward Revenue (10x-20x) due to scalable software margins, forcing legacy primes into a massive M&A technology race.</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Strategic Synthesis Bridge */}
              <div className="mt-12 bg-blue-900/10 border-l-4 border-blue-500 rounded-r-2xl border-y border-r border-slate-800/50 p-6 md:p-8 shadow-sm">
                <h3 className="text-xl font-serif font-bold text-white mb-3">Strategic Synthesis: The Architecture Mandate</h3>
                <p className="text-blue-200/90 leading-relaxed text-lg">
                  The 2026 catalysts—specifically the <strong className="text-white">30-Day Deployment Standard</strong> and the requirement for <strong className="text-white">Attritable Mass</strong>—are physically impossible to achieve using Legacy 1.0 hardware cycles. 
                </p>
                <p className="text-blue-200/90 leading-relaxed text-lg mt-3">
                  To meet these mandates and unlock the 25x-30x valuation multiples of Defense Tech 2.0, the industry has transitioned to a disaggregated architecture—the <strong className="text-white">5-Layer Operational Stack</strong>.
                </p>
              </div>

              <div className="flex justify-center pt-8 pb-2">
                <button 
                  onClick={() => setActiveView('landscape')}
                  className="bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 px-8 rounded-full flex items-center gap-2 transition-all hover:scale-105 shadow-[0_0_20px_rgba(37,99,235,0.4)] text-lg"
                >
                  Explore Market Structure <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* VIEW: Operational Stack */}
          {activeView === 'landscape' && (
            <div className="max-w-6xl mx-auto space-y-8 pb-12">
              
              <div className="border-b border-slate-800 pb-6">
                <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-3">
                  03 // Market Structure
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <Layers className="w-8 h-8 text-blue-400 shrink-0" />
                  <h2 className="text-3xl font-serif font-bold text-white">The Architecture: The Five-Layer Defense Tech Stack of the Future</h2>
                </div>
                <p className="text-slate-400 text-lg max-w-4xl leading-relaxed">
                  To overcome the integration challenges of the past, the Defense Tech 2.0 ecosystem has moved toward a disaggregated, five-layer technology stack. This architecture allows hardware and software to evolve independently at their own necessary speeds.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                
                <div className="col-span-1 lg:col-span-5 flex flex-col gap-3">
                  <h2 className="text-sm uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-2">
                    <Activity className="w-4 h-4" /> Operational Stack
                  </h2>
                  
                  <div className="flex gap-4">
                    <div className="hidden lg:flex flex-col items-center pt-2 select-none pointer-events-none">
                      <div className="text-[10px] font-mono text-indigo-400 font-bold tracking-[0.2em] whitespace-nowrap [writing-mode:vertical-rl] rotate-180 mb-3 opacity-90">
                        VALUATION MULTIPLE ARBITRAGE →
                      </div>
                      <div className="w-1.5 h-full bg-gradient-to-t from-slate-700 via-blue-500/50 to-indigo-400 rounded-full opacity-60"></div>
                      <div className="text-[10px] font-mono text-slate-500 font-bold tracking-[0.2em] whitespace-nowrap [writing-mode:vertical-rl] rotate-180 mt-3 opacity-70">
                        ← ABSOLUTE PROCUREMENT SPEND
                      </div>
                    </div>

                    <div className="flex-1 flex flex-col gap-3">
                      {layers.map((layer) => {
                        const Icon = layer.icon;
                        const isActive = activeLayer.id === layer.id;
                        
                        return (
                          <button
                            key={layer.id}
                            onClick={() => setActiveLayer(layer)}
                            className={`relative w-full text-left p-4 rounded-xl border transition-all duration-300 group
                              ${isActive ? layer.activeColor : `${layer.color} hover:bg-slate-800/60 hover:border-slate-500`}
                            `}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`p-3 rounded-lg bg-slate-950/50 ${isActive ? layer.textColor : 'text-slate-400'}`}>
                                <Icon className="w-6 h-6" />
                              </div>
                              <div>
                                <div className="text-xs font-mono text-slate-500 mb-1">LAYER {layer.id}</div>
                                <h3 className={`font-bold text-lg leading-tight ${isActive ? 'text-white' : 'text-slate-300'}`}>
                                  {layer.name}
                                </h3>
                                <p className="text-sm text-slate-400 mt-1">{layer.subtitle}</p>
                              </div>
                            </div>
                            
                            {isActive && (
                              <div className="hidden lg:block absolute -right-8 top-1/2 w-8 border-t-2 border-dashed border-slate-600" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="col-span-1 lg:col-span-7">
                  <div className="flex justify-between items-end mb-4">
                    <h2 className="text-sm uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
                      <Crosshair className="w-4 h-4" /> Layer Definition &amp; Scope
                    </h2>
                    
                    <div className="flex bg-[#0a0f1a] p-1 rounded-xl border border-slate-700 shadow-inner">
                      <button 
                        onClick={() => setStackViewMode('orientation')} 
                        className={`px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
                          stackViewMode === 'orientation' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-300'
                        }`}
                      >
                        Orientation
                      </button>
                      <button 
                        onClick={() => setStackViewMode('financial')} 
                        className={`px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${
                          stackViewMode === 'financial' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-300'
                        }`}
                      >
                        <PieChart className="w-3.5 h-3.5" /> Alpha
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-[#111827] border border-slate-800 rounded-2xl relative">
                    <div className={`absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl opacity-10 transition-colors duration-500 pointer-events-none ${activeLayer.color.split(' ')[0]}`} />

                    <div className="relative z-10 p-6 md:p-8">
                      <div className="mb-6">
                        <h2 className="text-4xl font-bold text-white mb-6">
                          {activeLayer.name}
                        </h2>
                        
                        <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-8">
                          {[
                            { label: 'MATURITY', val: activeLayer.maturity },
                            { label: 'MARKET SCALE', val: activeLayer.finance.tam },
                            { label: 'TARGET MULTIPLE', val: activeLayer.finance.multiple, highlight: true },
                            { label: 'MARGIN', val: activeLayer.finance.margin },
                            { label: 'CAGR', val: activeLayer.finance.cagr }
                          ].map((stat, i) => (
                            <div key={i} className="flex items-center gap-2.5 bg-[#0a0f1a] border border-slate-700/60 px-3.5 py-2 rounded-lg shadow-inner">
                              <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">{stat.label}</span>
                              <span className={`text-sm font-bold ${stat.highlight ? activeLayer.textColor : 'text-slate-200'}`}>
                                {stat.val}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {stackViewMode === 'orientation' ? (
                        <div className="animate-fade-in">
                          <div className="bg-[#0a0f1a] p-5 md:p-6 rounded-xl border-l-4 border-slate-700 shadow-inner mb-6">
                            <p className="text-[15px] md:text-lg text-slate-300 leading-relaxed font-serif">
                              {renderWithJargon(activeLayer.orientationDesc)}
                            </p>
                          </div>

                          <div className="flex flex-col gap-4 mb-2">
                            <div className="bg-slate-900/40 border border-slate-800/60 p-5 rounded-xl transition-colors hover:border-slate-700">
                              <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                                <LayoutGrid className="w-4 h-4 text-blue-400"/> Segments
                              </h4>
                              <p className="text-[15px] text-slate-200 font-medium leading-relaxed">
                                {renderWithJargon(activeLayer.orientationSegments)}
                              </p>
                            </div>
                            <div className="bg-slate-900/40 border border-slate-800/60 p-5 rounded-xl transition-colors hover:border-slate-700">
                              <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-emerald-400"/> Investment Thesis
                              </h4>
                              <p className="text-[15px] text-slate-200 font-medium leading-relaxed">
                                {renderWithJargon(activeLayer.orientationThesis)}
                              </p>
                            </div>
                            <div className="bg-slate-900/40 border border-slate-800/60 p-5 rounded-xl transition-colors hover:border-slate-700">
                              <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                                <Building className="w-4 h-4 text-purple-400"/> Key Players &amp; Market Signals
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {activeLayer.orientationPlayers.map((p, i) => (
                                  <div key={i} className="group/player relative inline-block">
                                    <div className="px-3 py-1.5 bg-slate-800 border border-slate-600 hover:border-slate-400 rounded-lg text-[13px] font-bold text-slate-200 cursor-help transition-colors">
                                      {p.name}
                                    </div>
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-[#0a0f1a] text-slate-300 text-xs rounded-xl border border-slate-700 shadow-2xl opacity-0 invisible group-hover/player:opacity-100 group-hover/player:visible transition-all z-20 pointer-events-none">
                                      <div className="font-mono font-bold text-[10px] text-blue-400 mb-1 uppercase tracking-wider flex items-center gap-1.5"><Activity className="w-3 h-3" /> Market Signal</div>
                                      <p className="leading-relaxed">{renderWithJargon(p.signal)}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="animate-fade-in space-y-4">
                          {activeLayer.alphaInsights.map((insight, i) => {
                            const InsightIcon = insight.icon;
                            return (
                              <div key={i} className="bg-slate-900/60 border border-slate-700/50 p-6 rounded-xl flex gap-4 hover:border-slate-600 transition-colors">
                                <div className={`p-2.5 rounded-lg shrink-0 h-fit bg-slate-950/50 border border-slate-800 ${activeLayer.textColor}`}>
                                  <InsightIcon className="w-5 h-5" />
                                </div>
                                <div>
                                  <h4 className="text-base font-bold text-white mb-2">{insight.title}</h4>
                                  <p className="text-sm text-slate-300 leading-relaxed">{renderWithJargon(insight.desc)}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>

              <div className="space-y-6 pt-4">
                <div className="p-6 md:p-8 bg-blue-900/10 border-l-4 border-blue-500 rounded-r-2xl border-y border-r border-slate-800/50 shadow-sm">
                  <p className="text-blue-200/90 font-serif italic text-xl md:text-2xl leading-relaxed text-center md:text-left">
                    "In this framework, the value is migrating away from the 'outer shell' (the vehicle) and toward the 'brains' and 'nervous system' (the software and sensors)."
                  </p>
                </div>

                <div className="bg-[#111827] border border-slate-800/80 rounded-2xl p-6 md:p-8">
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2 mb-1">
                      <Target className="w-5 h-5 text-rose-400" /> Why the "Middle" is the 2026 Hotspot
                    </h3>
                    <p className="text-slate-400 text-[15px]">For PE and VC firms, Layers 4 and 5 are currently the "Alpha" targets.</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div className="bg-[#0a0f1a] border border-slate-800 rounded-xl p-5 hover:border-slate-700 transition-colors">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-lg bg-blue-900/20 text-blue-400"><Globe className="w-4 h-4" /></div>
                        <h4 className="text-white font-bold text-sm">Low ITAR Friction</h4>
                      </div>
                      <p className="text-sm text-slate-400 leading-relaxed">Software is easier to export to allies than physical missiles, significantly easing market expansion.</p>
                    </div>
                    
                    <div className="bg-[#0a0f1a] border border-slate-800 rounded-xl p-5 hover:border-slate-700 transition-colors">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-lg bg-emerald-900/20 text-emerald-400"><Zap className="w-4 h-4" /></div>
                        <h4 className="text-white font-bold text-sm">Instant Scalability</h4>
                      </div>
                      <p className="text-sm text-slate-400 leading-relaxed">You can push a software update to 10,000 drones instantly; you cannot "update" a physical tank in the field as easily.</p>
                    </div>

                    <div className="bg-[#0a0f1a] border border-slate-800 rounded-xl p-5 hover:border-slate-700 transition-colors">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-lg bg-purple-900/20 text-purple-400"><Briefcase className="w-4 h-4" /></div>
                        <h4 className="text-white font-bold text-sm">Dual-Use Markets</h4>
                      </div>
                      <p className="text-sm text-slate-400 leading-relaxed">Mission Intelligence often has direct commercial parallels in autonomous trucking or industrial robotics.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* VIEW: Segment Analysis */}
          {activeView === 'map' && (
            <div className="max-w-7xl mx-auto space-y-10 pb-12">
              
              <div className="border-b border-slate-800 pb-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                  <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-3">
                    04 // Segment Analysis
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5 mb-3">
                    <div className="flex items-center gap-3">
                      <LayoutGrid className="w-8 h-8 text-blue-400 shrink-0" />
                      <h2 className="text-3xl font-serif font-bold text-white">Multidomain Taxonomy</h2>
                    </div>
                    <div className="px-3 py-1.5 bg-indigo-950/40 border border-indigo-500/30 text-indigo-400 text-[10px] sm:text-xs font-mono font-bold uppercase tracking-widest rounded-lg whitespace-nowrap shadow-inner w-fit">
                      [ {visibleSegmentsCount} SEGMENTS IDENTIFIED ]
                    </div>
                  </div>
                  <p className="text-slate-400 text-lg max-w-3xl mb-8">
                    Isolate high-conviction targets by filtering the multidomain stack through core Defense Tech 2.0 investment theses or map them by industry maturity.
                  </p>
                  
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest mr-2 flex items-center gap-1.5"><Filter className="w-3.5 h-3.5" /> Lens:</span>
                      <button
                        onClick={() => { setActiveFilterType('thesis'); setActiveMapFilter('All'); }}
                        className={`px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
                          activeFilterType === 'thesis' ? 'bg-slate-700 text-white shadow-sm' : 'bg-slate-900 text-slate-500 hover:text-slate-300'
                        }`}
                      >
                        Investment Thesis
                      </button>
                      <button
                        onClick={() => { setActiveFilterType('maturity'); setActiveMapFilter('All'); }}
                        className={`px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
                          activeFilterType === 'maturity' ? 'bg-slate-700 text-white shadow-sm' : 'bg-slate-900 text-slate-500 hover:text-slate-300'
                        }`}
                      >
                        Industry Maturity
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        onClick={() => setActiveMapFilter('All')}
                        className={`flex items-center gap-2 text-xs font-mono tracking-widest uppercase transition-all px-4 py-2 rounded-full border ${
                          activeMapFilter === 'All' 
                            ? 'bg-blue-600 border-blue-500 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]' 
                            : 'bg-slate-900/50 border-slate-800 text-slate-500 hover:text-slate-300 hover:border-slate-600'
                        }`}
                      >
                        All Segments
                      </button>
                      
                      {activeFilterType === 'thesis' && thesisFilters.map((thesis) => (
                        <div key={thesis} className="group/filter relative inline-block cursor-help">
                          <button
                            onClick={() => setActiveMapFilter(thesis)}
                            className={`flex items-center gap-2 text-xs font-mono tracking-widest uppercase transition-all px-4 py-2 rounded-full border ${
                              activeMapFilter === thesis 
                                ? 'bg-blue-600 border-blue-500 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]' 
                                : 'bg-slate-900/50 border-slate-800 text-slate-500 hover:text-slate-300 hover:border-slate-600'
                            }`}
                          >
                            {thesis}
                          </button>
                          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 p-3 bg-[#0B1120] text-slate-300 text-[11px] leading-relaxed rounded-xl border border-slate-700 shadow-[0_15px_40px_rgba(0,0,0,0.8)] opacity-0 invisible group-hover/filter:opacity-100 group-hover/filter:visible transition-all z-[100] pointer-events-none font-sans normal-case text-left tracking-normal whitespace-normal">
                            <span className="font-bold text-cyan-400 block mb-1">{thesis}</span>
                            <span className="text-slate-400">{thesisDict[thesis]}</span>
                          </div>
                        </div>
                      ))}

                      {activeFilterType === 'maturity' && maturityFilters.map((maturity) => (
                        <div key={maturity} className="group/filter relative inline-block cursor-pointer">
                          <button
                            onClick={() => setActiveMapFilter(maturity)}
                            className={`flex items-center gap-2 text-xs font-mono tracking-widest uppercase transition-all px-4 py-2 rounded-full border ${
                              activeMapFilter === maturity 
                                ? 'bg-blue-600 border-blue-500 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]' 
                                : 'bg-slate-900/50 border-slate-800 text-slate-500 hover:text-slate-300 hover:border-slate-600'
                            }`}
                          >
                            {maturity}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center bg-[#0a0f1a] p-1 rounded-xl border border-slate-700 shadow-inner shrink-0">
                  <button 
                    onClick={() => setIsMapCompact(false)}
                    className={`p-2 rounded-lg transition-colors ${!isMapCompact ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                    title="Detailed View"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => setIsMapCompact(true)}
                    className={`p-2 rounded-lg transition-colors ${isMapCompact ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                    title="Compact Matrix View"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-12">
                {layers.map((layer) => {
                  const Icon = layer.icon;
                  const isLayerCollapsed = collapsedLayers.includes(layer.id);
                  
                  const hasVisibleSegments = activeMapFilter === 'All' || 
                    (activeFilterType === 'thesis' 
                      ? layer.segments.some(seg => seg.tags.includes(activeMapFilter))
                      : layer.segments.some(seg => seg.maturity === activeMapFilter));

                  if (!hasVisibleSegments && activeMapFilter !== 'All') return null;

                  return (
                    <div key={layer.id} className="relative">
                      <div 
                        className="flex items-center justify-between mb-6 cursor-pointer group/layer select-none"
                        onClick={() => {
                          setCollapsedLayers(prev => 
                            prev.includes(layer.id) ? prev.filter(id => id !== layer.id) : [...prev, layer.id]
                          );
                        }}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-xl bg-slate-900 border ${layer.color.split(' ')[1]}`}>
                            <Icon className={`w-6 h-6 ${layer.textColor}`} />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-white flex items-center gap-3 transition-colors group-hover/layer:text-blue-300">
                              Layer {layer.id}: {layer.name}
                            </h3>
                            <p className="text-slate-400 text-sm mt-1">{layer.subtitle}</p>
                          </div>
                        </div>
                        <div className="p-2 rounded-lg bg-slate-900/50 border border-slate-800 text-slate-500 group-hover/layer:text-white group-hover/layer:bg-slate-800 transition-colors">
                          {isLayerCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                      </div>
                      
                      {!isLayerCollapsed && (
                        isMapCompact ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pl-0 md:pl-16">
                            {getSortedSegments(layer.segments).map((segment, idx) => {
                              const isHighlighted = activeMapFilter === 'All' || 
                                (activeFilterType === 'thesis' 
                                  ? segment.tags.includes(activeMapFilter)
                                  : segment.maturity === activeMapFilter);
                              
                              return (
                                <div 
                                  key={idx} 
                                  className={`group/strip relative flex flex-col justify-between p-4 rounded-xl border bg-[#111827] transition-all duration-300 min-h-[90px] hover:z-50
                                    ${isHighlighted 
                                      ? 'border-slate-700 hover:border-slate-500 opacity-100 shadow-sm' 
                                      : 'border-slate-800/40 opacity-30 grayscale hover:grayscale-0 hover:opacity-100'
                                    }
                                  `}
                                >
                                  <div className="mb-2 flex justify-between items-start gap-2">
                                    <h4 className={`text-sm font-bold leading-snug line-clamp-2 transition-colors ${isHighlighted ? 'text-slate-200' : 'text-slate-500'}`}>
                                      {segment.name}
                                    </h4>
                                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-mono border whitespace-nowrap shrink-0 ${getMaturityBadgeStyle(segment.maturity)}`}>
                                      {segment.maturity}
                                    </span>
                                  </div>

                                  <div className="flex flex-wrap gap-1.5 mt-auto pt-3 border-t border-slate-800/60">
                                    {segment.tags.slice(0, 2).map((tag, tIdx) => {
                                      let tagStyle = 'bg-slate-800 border-slate-700 text-slate-400';
                                      if (tag === 'High-Margin IP') tagStyle = 'bg-indigo-900/30 border-indigo-500/40 text-indigo-300';
                                      else if (tag === 'Attritable Mass') tagStyle = 'bg-emerald-900/30 border-emerald-500/40 text-emerald-300';
                                      else if (tag === 'Deep Tech') tagStyle = 'bg-rose-900/30 border-rose-500/40 text-rose-300';
                                      else if (tag === 'Missing Middle') tagStyle = 'bg-blue-900/30 border-blue-500/40 text-blue-300';
                                      else if (tag === 'Dual-Use') tagStyle = 'bg-teal-900/30 border-teal-500/40 text-teal-300';
                                      else if (tag === 'Decision Advantage') tagStyle = 'bg-purple-900/30 border-purple-500/40 text-purple-300';

                                      return (
                                        <span key={tIdx} className={`px-1.5 py-0.5 rounded text-[9px] font-mono border block whitespace-nowrap ${tagStyle}`}>
                                          {tag}
                                        </span>
                                      );
                                    })}
                                  </div>

                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-72 p-4 bg-[#0B1120] rounded-xl border border-slate-700 shadow-[0_20px_50px_rgba(0,0,0,0.9)] opacity-0 invisible group-hover/strip:opacity-100 group-hover/strip:visible transition-all z-[100] pointer-events-none">
                                    <h5 className="font-bold text-white text-sm mb-2 border-b border-slate-800 pb-2">{segment.name}</h5>
                                    
                                    <div className="flex flex-wrap gap-1.5 mb-3">
                                      {segment.tags.map(tag => (
                                        <span key={tag} className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-slate-800 text-slate-300 border border-slate-700">
                                          {tag}
                                        </span>
                                      ))}
                                    </div>

                                    <p className="text-xs text-slate-300 mb-3 leading-relaxed">{renderWithJargon(segment.desc)}</p>
                                    <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">Key Targets</div>
                                    <div className={`text-xs font-semibold ${layer.textColor}`}>{renderWithJargon(segment.examples)}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-0 md:pl-16">
                            {getSortedSegments(layer.segments).map((segment, idx) => {
                              const isHighlighted = activeMapFilter === 'All' || 
                                (activeFilterType === 'thesis' 
                                  ? segment.tags.includes(activeMapFilter)
                                  : segment.maturity === activeMapFilter);
                              
                              return (
                                <div 
                                  key={idx} 
                                  className={`bg-[#111827] rounded-xl p-5 flex flex-col h-full transition-all duration-300 border
                                    ${isHighlighted 
                                      ? 'border-slate-700 hover:border-slate-500 opacity-100 shadow-sm' 
                                      : 'border-slate-800/40 opacity-30 grayscale hover:grayscale-0 hover:opacity-100'
                                    }
                                  `}
                                >
                                  <div className="flex justify-between items-start mb-3">
                                    <h4 className={`text-md font-bold transition-colors pr-2 ${isHighlighted ? 'text-slate-200' : 'text-slate-500'}`}>
                                      {segment.name}
                                    </h4>
                                    <span className={`px-2 py-0.5 rounded-lg text-[9px] font-mono border whitespace-nowrap shrink-0 ${getMaturityBadgeStyle(segment.maturity)}`}>
                                      {segment.maturity}
                                    </span>
                                  </div>
                                  
                                  <div className="flex flex-wrap gap-1.5 mb-4">
                                    {segment.tags.slice(0, 2).map(tag => {
                                      let tagStyle = 'bg-slate-800 border-slate-700 text-slate-400';
                                      if (tag === 'High-Margin IP') tagStyle = 'bg-indigo-900/30 border-indigo-500/40 text-indigo-300';
                                      else if (tag === 'Attritable Mass') tagStyle = 'bg-emerald-900/30 border-emerald-500/40 text-emerald-300';
                                      else if (tag === 'Deep Tech') tagStyle = 'bg-rose-900/30 border-rose-500/40 text-rose-300';
                                      else if (tag === 'Missing Middle') tagStyle = 'bg-blue-900/30 border-blue-500/40 text-blue-300';
                                      else if (tag === 'Dual-Use') tagStyle = 'bg-teal-900/30 border-teal-500/40 text-teal-300';
                                      else if (tag === 'Decision Advantage') tagStyle = 'bg-purple-900/30 border-purple-500/40 text-purple-300';

                                      return (
                                        <div key={tag} className="group/tag relative inline-block cursor-help">
                                          <span className={`px-2 py-0.5 rounded text-[10px] font-mono border block ${tagStyle}`}>
                                            {tag}
                                          </span>
                                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-3 bg-[#0B1120] text-slate-300 text-[11px] leading-relaxed rounded-xl border border-slate-700 shadow-[0_15px_40px_rgba(0,0,0,0.8)] opacity-0 invisible group-hover/tag:opacity-100 group-hover/tag:visible transition-all z-[100] pointer-events-none font-sans normal-case text-left tracking-normal whitespace-normal">
                                            <span className="font-bold text-white block mb-1">{tag}</span>
                                            <span className="text-slate-400">{thesisDict[tag]}</span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  <p className="text-sm text-slate-400 mb-6 flex-grow leading-relaxed">{renderWithJargon(segment.desc)}</p>

                                  <div className="pt-4 border-t border-slate-800/60 mt-auto">
                                    <span className="text-xs font-mono text-slate-500 uppercase tracking-wider block mb-1">Key Players</span>
                                    <span className={`text-sm font-medium ${isHighlighted ? layer.textColor : 'text-slate-500'}`}>
                                      {renderWithJargon(segment.examples)}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* VIEW: Competitive Target Universe */}
          {activeView === 'ecosystem' && (
            <div className="max-w-7xl mx-auto space-y-8 pb-12">
              
              <div className="border-b border-slate-800 pb-6">
                <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-3">
                  05 // Market Map
                </div>
                <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5 mb-3">
                  <div className="flex items-center gap-3">
                    <Globe className="w-8 h-8 text-blue-400 shrink-0" />
                    <h2 className="text-3xl font-serif font-bold text-white">The Target Universe</h2>
                  </div>
                  <div className="px-3 py-1.5 bg-cyan-950/40 border border-cyan-500/30 text-cyan-400 text-[10px] sm:text-xs font-mono font-bold uppercase tracking-widest rounded-lg whitespace-nowrap shadow-[0_0_15px_rgba(34,211,238,0.15)] w-fit">
                    [ {visibleCompaniesCount} ASSETS IN SCOPE ]
                  </div>
                </div>
                <p className="text-slate-400 text-lg max-w-3xl mb-8">
                  A detailed peer-group analysis of incumbents and challengers, isolated by sovereign jurisdiction and capital profile.
                </p>
                
                <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest mr-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5"/> Sovereign Lens:</span>
                    {geoFilters.map(filter => (
                      <button
                        key={filter.id}
                        onClick={() => setActiveGeoFilter(filter.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-mono font-bold tracking-wider transition-all ${
                          activeGeoFilter === filter.id 
                            ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.3)]' 
                            : 'bg-slate-900 border border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-500'
                        }`}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>

                  <div className="relative w-full md:w-64 shrink-0">
                    <div className="flex items-center bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 focus-within:border-blue-500/50 focus-within:bg-slate-900 transition-all shadow-inner">
                      <Search className="w-3.5 h-3.5 text-slate-500 mr-2 shrink-0" />
                      <input
                        type="text"
                        placeholder="Locate firm..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bg-transparent border-none outline-none text-xs font-mono text-white placeholder-slate-500 w-full focus:ring-0"
                      />
                      {searchQuery && (
                        <button onClick={() => setSearchQuery('')} className="shrink-0 p-0.5 hover:bg-slate-800 rounded transition-colors">
                          <X className="w-3 h-3 text-slate-400 hover:text-white" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* NEW COMPLIANCE PATHWAY ROW - Rendered completely independent of the flex container above */}
                <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-800/40">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest mr-2 flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5"/> Compliance Pathway:
                  </span>
                  <button
                    onClick={() => setActiveComplianceFilter('All')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-mono font-bold tracking-wider transition-all ${
                      activeComplianceFilter === 'All' 
                        ? 'bg-rose-900/40 border border-rose-500 text-white shadow-[0_0_15px_rgba(244,63,94,0.3)]' 
                        : 'bg-slate-900 border border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-500'
                    }`}
                  >
                    All Validated
                  </button>
                  {complianceFilters.map(filter => (
                    <div key={filter} className="group/grc relative inline-block cursor-help">
                      <button
                        onClick={() => setActiveComplianceFilter(filter)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-mono font-bold tracking-wider transition-all ${
                          activeComplianceFilter === filter 
                            ? 'bg-rose-900/40 border border-rose-500 text-white shadow-[0_0_15px_rgba(244,63,94,0.3)]' 
                            : 'bg-slate-900 border border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-500'
                        }`}
                      >
                        {filter === 'DUAL' ? 'Dual-Use (EAR)' : filter}
                      </button>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 p-3 bg-[#0B1120] text-slate-300 text-[11px] leading-relaxed rounded-xl border border-slate-700 shadow-[0_15px_40px_rgba(0,0,0,0.8)] opacity-0 invisible group-hover/grc:opacity-100 group-hover/grc:visible transition-all z-[100] pointer-events-none font-sans normal-case text-left tracking-normal whitespace-normal">
                        <span className="font-bold text-rose-400 block mb-1">{filter === 'DUAL' ? 'Dual-Use (EAR)' : filter}</span>
                        <span className="text-slate-400">{thesisDict[filter]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-6 p-4 bg-slate-900/50 rounded-xl border border-slate-800">
                <span className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Legend:</span>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(59,130,246,0.8)]"></div>
                  <span className="text-sm text-slate-300">Neo-Prime (Anchor)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-slate-400 border border-slate-500"></div>
                  <span className="text-sm text-slate-300">Growth Challenger</span>
                </div>
                <div className="flex items-center gap-2 border-r border-slate-700 pr-6">
                  <div className="w-3 h-3 rounded-full border border-dashed border-slate-500"></div>
                  <span className="text-sm text-slate-300">Legacy Prime / Incumbent</span>
                </div>
                <div className="flex items-center gap-2 group/legend relative cursor-help">
                  <CheckCircle2 className="w-4 h-4 text-amber-500/80" />
                  <span className="text-sm font-semibold text-amber-500/90">KR Verified</span>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 bg-[#0B1120] text-slate-300 text-[11px] leading-relaxed rounded-xl border border-amber-500/20 shadow-[0_15px_40px_rgba(0,0,0,0.8)] opacity-0 invisible group-hover/legend:opacity-100 group-hover/legend:visible transition-all z-[100] pointer-events-none">
                    <span className="font-bold text-amber-500 block mb-1">Knowledge Ridge Validated</span>
                    <span className="text-slate-400">This target's commercial viability and Program of Record (PoR) probability has been double-verified by our human expert council.</span>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {layers.map((layer) => {
                  const Icon = layer.icon;
                  return (
                    <div key={layer.id} className="bg-[#111827] border border-slate-800 rounded-2xl overflow-hidden flex flex-col md:flex-row">
                      
                      <div className={`p-6 md:w-64 border-b md:border-b-0 md:border-r border-slate-800 bg-slate-900/40 relative overflow-hidden flex-shrink-0`}>
                        <div className={`absolute -left-12 -top-12 w-32 h-32 rounded-full blur-3xl opacity-20 ${layer.color.split(' ')[0]}`} />
                        <div className="relative z-10">
                          <div className="flex items-center gap-3 mb-2">
                            <Icon className={`w-5 h-5 ${layer.textColor}`} />
                            <span className="text-xs font-mono text-slate-500 tracking-wider uppercase">Layer {layer.id}</span>
                          </div>
                          <h3 className="text-lg font-bold text-white leading-tight">{layer.name}</h3>
                        </div>
                      </div>

                      <div className="flex-grow p-6 grid grid-cols-1 xl:grid-cols-2 gap-x-8 gap-y-6">
                        {getSortedSegments(layer.segments).map((segment, idx) => (
                          <div key={idx} id={`ecosystem-segment-${getSafeId(segment.name)}`} className="flex flex-col">
                            <h4 className="text-sm font-semibold text-slate-400 mb-3 uppercase tracking-wider border-b border-slate-800/60 pb-2 transition-colors duration-500">
                              {segment.name}
                            </h4>
                            
                            <div className="flex flex-wrap gap-2">
                              {segment.companies && segment.companies.map((company, cIdx) => {
                                const isGeoFilteredOut = activeGeoFilter !== 'Global' && !geoFilters.find(f => f.id === activeGeoFilter)?.codes.includes(company.hq);
                                const isSearchFilteredOut = searchQuery && !company.name.toLowerCase().includes(searchQuery.toLowerCase());
                                const isComplianceFilteredOut = activeComplianceFilter !== 'All' && !company.tags.includes(activeComplianceFilter) && company.itar !== activeComplianceFilter;
                                const isFilteredOut = isGeoFilteredOut || isSearchFilteredOut || isComplianceFilteredOut;

                                return (
                                  <button
                                    key={cIdx}
                                    onClick={() => setActiveCompany(company)}
                                    className={`
                                      group relative flex flex-col items-start p-2.5 rounded-lg border transition-all duration-300 cursor-pointer flex-1 min-w-[180px] sm:min-w-[190px]
                                      ${getTokenStyle(company.tier)}
                                      ${isFilteredOut ? 'opacity-20 grayscale hover:opacity-100 hover:grayscale-0' : 'opacity-100'}
                                    `}
                                  >
                                    <div className="absolute top-2 right-2 px-1 py-0.5 rounded bg-slate-900 border border-slate-700 text-[8px] font-mono text-slate-400 font-bold tracking-widest shadow-inner">
                                      {company.hq}
                                    </div>
                                    
                                    <div className="flex items-center w-full gap-1.5 mb-1.5 pr-6">
                                      <div className="flex items-center gap-1.5 truncate">
                                        {company.tier === 'neoprime' && <Zap className="w-3 h-3 text-blue-300 shrink-0" />}
                                        {company.tier === 'legacy' && <Building className="w-3 h-3 text-slate-500 shrink-0" />}
                                        <span className="font-bold text-[13px] tracking-wide truncate">{company.name}</span>
                                      </div>
                                      
                                      <div className="shrink-0 rounded-full bg-amber-500/10 border border-amber-500/20" title="Knowledge Ridge Expert Verified">
                                        <CheckCircle2 className="w-2.5 h-2.5 text-amber-500/80 m-[1px]" />
                                      </div>
                                    </div>
                                    
                                    {company.tags.filter(tag => !thesisFilters.includes(tag)).length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-0.5">
                                        {company.tags.filter(tag => !thesisFilters.includes(tag)).map(tag => (
                                          <span key={tag} className={`text-[8px] leading-none font-mono px-1.5 py-0.5 rounded border ${getTagStyle(tag)}`}>
                                            {tag}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    
                                    <div className="w-full mt-2 pt-1.5 border-t border-slate-700/50 flex items-center justify-between text-[8px] font-mono text-slate-500 uppercase tracking-widest">
                                      <span className="truncate">{company.stage}</span>
                                      <span className="opacity-50 mx-1">•</span>
                                      <span className="shrink-0">{company.emp} EMP</span>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>

                    </div>
                  );
                })}
              </div>

              <div className="relative mt-8 pt-10">
                <div className="absolute top-0 left-0 w-full h-12 bg-gradient-to-b from-[#0a0f1a]/0 to-[#0a0f1a] z-10 pointer-events-none" />
                <div className="flex justify-center gap-3 mb-8 opacity-40">
                  <div className="w-2 h-2 rounded-full bg-slate-600 animate-pulse" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-slate-600 animate-pulse" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full bg-slate-600 animate-pulse" style={{ animationDelay: '300ms' }} />
                </div>

                <div className="bg-[#111827]/80 backdrop-blur-md border border-slate-700/60 rounded-2xl p-8 md:p-10 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-3xl pointer-events-none transition-transform duration-1000 group-hover:scale-110" />
                  
                  <div className="flex flex-col md:flex-row items-center gap-8 relative z-10">
                    <div className="flex-1 space-y-4 text-center md:text-left">
                      <div className="inline-flex items-center gap-2 text-indigo-400 text-xs font-mono font-bold tracking-widest uppercase mb-2 bg-indigo-950/30 px-3 py-1.5 rounded-lg border border-indigo-500/30">
                        <BrainCircuit className="w-3.5 h-3.5" /> Proprietary Agentic Synthesis
                      </div>
                      <h3 className="text-3xl font-serif font-bold text-white">The Synthesis Engine</h3>
                      <p className="text-slate-400 leading-relaxed text-lg max-w-2xl">
                        The landscape above is a static snapshot. Frontier’s value lies in our <span className="text-slate-200 font-semibold">Agentic Reasoning Engine</span>—synthesizing unstructured global signals in real-time to curate the exact targets that match your strategic constraints.
                      </p>
                    </div>
                    
                    <div className="shrink-0 w-full md:w-auto">
                      <button 
                        onClick={() => setIsInquiryOpen(true)}
                        className="w-full md:w-auto px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl transition-all shadow-[0_0_25px_rgba(79,70,229,0.3)] hover:shadow-[0_0_35px_rgba(79,70,229,0.5)] flex items-center justify-center gap-3 group/btn"
                      >
                        <BrainCircuit className="w-5 h-5" />
                        <span>Initiate Bespoke Synthesis</span>
                        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* VIEW: Investor Lens */}
          {activeView === 'investor' && (
            <div className="max-w-6xl mx-auto space-y-10 pb-12">
              <div className="border-b border-slate-800 pb-6">
                <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-3">
                  06 // Funding Landscape
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <PieChart className="w-8 h-8 text-blue-400 shrink-0" />
                  <h2 className="text-3xl font-serif font-bold text-white">Capital Deployment Strategies by Asset Class</h2>
                </div>
                <p className="text-slate-400 text-lg max-w-3xl">
                  See where capital is flowing across the stack. Select an investor profile to reveal primary allocation layers, risk mandates, and target exit multiples. <span className="text-blue-400">Click any highlighted layer to explore its specific companies.</span>
                </p>
              </div>

              <div className="flex flex-wrap gap-4">
                {investorProfiles.map((profile) => {
                  const ProfileIcon = profile.icon;
                  return (
                    <button
                      key={profile.id}
                      onClick={() => setActiveInvestor(profile)}
                      className={`px-5 py-3.5 rounded-xl border transition-all flex items-center gap-3 font-semibold ${
                        activeInvestor.id === profile.id
                          ? profile.bg + ' text-white shadow-[0_0_15px_rgba(255,255,255,0.05)]'
                          : 'bg-slate-900/40 border-slate-700/50 text-slate-400 hover:bg-slate-800 hover:text-slate-300'
                      }`}
                    >
                      <ProfileIcon className={`w-5 h-5 ${activeInvestor.id === profile.id ? profile.color : 'text-slate-500'}`} />
                      {profile.name}
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="col-span-1 lg:col-span-5 flex flex-col gap-3">
                  <h3 className="text-sm uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-2">
                    <Layers className="w-4 h-4" /> Allocation Focus
                  </h3>
                  
                  {layers.map((layer) => {
                    const Icon = layer.icon;
                    const isFocused = activeInvestor.targetLayers.includes(layer.id);
                    
                    return (
                      <button
                        key={layer.id}
                        onClick={() => {
                          setActiveLayer(layer);
                          setActiveView('landscape');
                        }}
                        className={`relative w-full text-left p-4 rounded-xl border transition-all duration-500 flex items-center gap-4
                          ${isFocused 
                            ? `${layer.activeColor} transform scale-[1.02] z-10 cursor-pointer` 
                            : 'bg-slate-900/20 border-slate-800/50 opacity-40 grayscale hover:grayscale-0 hover:opacity-80 cursor-pointer'
                          }
                        `}
                      >
                        <div className={`p-3 rounded-lg bg-slate-950/50 ${isFocused ? layer.textColor : 'text-slate-500'}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <div className="text-[10px] font-mono text-slate-500 mb-1 flex justify-between items-center tracking-wider">
                            <span>LAYER {layer.id}</span>
                            {isFocused && (
                              <span className="flex items-center gap-1 text-emerald-400 font-bold bg-emerald-950/50 px-2 py-0.5 rounded border border-emerald-500/30 animate-pulse">
                                <Target className="w-3 h-3" /> ALPHA
                              </span>
                            )}
                          </div>
                          <h3 className={`font-bold text-lg leading-tight ${isFocused ? 'text-white' : 'text-slate-400'}`}>
                            {layer.name}
                          </h3>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="col-span-1 lg:col-span-7 space-y-6">
                  <div className="bg-[#111827] border border-slate-800 rounded-2xl p-6 md:p-8 relative overflow-hidden transition-all duration-300">
                    <div className={`absolute -top-32 -right-32 w-96 h-96 rounded-full blur-[100px] opacity-10 transition-colors duration-500 ${activeInvestor.bg.split(' ')[0]}`} />
                    
                    <div className="relative z-10 space-y-8">
                      <div>
                        <h3 className={`text-sm font-mono tracking-widest uppercase mb-2 ${activeInvestor.color}`}>
                          {activeInvestor.role}
                        </h3>
                        <h2 className="text-3xl font-bold text-white mb-5">{activeInvestor.name} Mandate</h2>
                        <div className="bg-slate-900/60 p-6 rounded-xl border-l-4 border-slate-700 italic">
                          <p className="text-lg text-slate-300 leading-relaxed font-serif">
                            "{activeInvestor.thesis}"
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="bg-[#0a0f1a] p-5 rounded-xl border border-slate-800/80">
                          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-emerald-400" /> Target Multiples
                          </h4>
                          <p className="text-white font-medium text-lg">{renderWithJargon(activeInvestor.multiples)}</p>
                        </div>
                        <div className="bg-[#0a0f1a] p-5 rounded-xl border border-slate-800/80">
                          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                            <Shield className="w-4 h-4 text-blue-400" /> Edge Moat
                          </h4>
                          <p className="text-slate-300 text-sm leading-relaxed font-medium">{renderWithJargon(activeInvestor.moat)}</p>
                        </div>
                        <div className="bg-[#0a0f1a] p-5 rounded-xl border border-slate-800/80 md:col-span-2">
                          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                            <Activity className="w-4 h-4 text-rose-400" /> Critical Due Diligence: {activeInvestor.criticalMetric}
                          </h4>
                          <p className="text-slate-400 text-sm leading-relaxed">{renderWithJargon(activeInvestor.metricDesc)}</p>
                        </div>
                      </div>

                      <div className="pt-5 border-t border-slate-800/60">
                        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Primary Focus Segments</h4>
                        <div className="flex flex-wrap gap-2">
                          {activeInvestor.targetLayers.map(layerId => {
                            const layer = layers.find(l => l.id === layerId);
                            const LayerIcon = layer.icon;
                            return (
                              <span key={layerId} className="px-3 py-1.5 bg-slate-900 border border-slate-800 text-slate-300 text-sm rounded-lg flex items-center gap-2">
                                <LayerIcon className={`w-3.5 h-3.5 ${layer.textColor}`} />
                                {layer.name}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* VIEW: M&A Heatmap */}
          {activeView === 'mna' && (
            <div className="max-w-6xl mx-auto space-y-10 pb-12">
              
              <div className="border-b border-slate-800 pb-6">
                <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-3">
                  07 // Strategic M&A
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <GitMerge className="w-8 h-8 text-rose-500 shrink-0" />
                  <h2 className="text-3xl font-serif font-bold text-white">Exit Architecture: Mapping Legacy Technical Debt</h2>
                </div>
                <p className="text-slate-400 text-lg max-w-3xl mb-8">
                  See the operational stack through the eyes of the acquirer. Select a strategic buyer to reveal their critical technology gaps and understand the exit logic driving premium multiples.
                </p>

                <div className="flex bg-[#0a0f1a] p-1 rounded-xl border border-slate-700 shadow-inner w-fit">
                  <button 
                    onClick={() => setMnaTab('landscape')} 
                    className={`px-5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2 ${
                      mnaTab === 'landscape' ? 'bg-rose-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-300'
                    }`}
                  >
                    <GitMerge className="w-4 h-4" /> Market Exits
                  </button>
                  <button 
                    onClick={() => setMnaTab('casestudy')} 
                    className={`px-5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2 ${
                      mnaTab === 'casestudy' ? 'bg-rose-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-300'
                    }`}
                  >
                    <Activity className="w-4 h-4" /> Neo-Prime Playbook
                  </button>
                </div>
              </div>

              {mnaTab === 'landscape' ? (
                <>
                  <div className="flex flex-wrap gap-4">
                    {exitCategories.map((category, idx) => {
                      const CategoryIcon = category.icon;
                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            setActiveExitCategory(category);
                            setActiveBuyer(category.buyers[0]);
                          }}
                          className={`px-5 py-3 rounded-xl border transition-all flex items-center gap-2 font-semibold ${
                            activeExitCategory.title === category.title
                              ? 'bg-slate-800 border-slate-500 text-white shadow-[0_0_15px_rgba(255,255,255,0.05)]'
                              : 'bg-slate-900/40 border-slate-700/50 text-slate-400 hover:bg-slate-800 hover:text-slate-300'
                          }`}
                        >
                          <CategoryIcon className={`w-4 h-4 ${activeExitCategory.title === category.title ? 'text-rose-400' : 'text-slate-500'}`} />
                          {category.title.split(' (')[0]}
                        </button>
                      );
                    })}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="col-span-1 lg:col-span-5 flex flex-col gap-3">
                      <h3 className="text-sm uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-2">
                        <Layers className="w-4 h-4" /> Acquisition Targets
                      </h3>
                      
                      {layers.map((layer) => {
                        const Icon = layer.icon;
                        const isTarget = activeBuyer.targetLayers.includes(layer.id);
                        
                        return (
                          <div
                            key={layer.id}
                            className={`relative w-full text-left p-4 rounded-xl border transition-all duration-500 flex items-center gap-4
                              ${isTarget 
                                ? `${layer.activeColor} transform scale-[1.02] z-10` 
                                : 'bg-slate-900/20 border-slate-800/50 opacity-40 grayscale hover:grayscale-0 hover:opacity-80'
                              }
                            `}
                          >
                            <div className={`p-3 rounded-lg bg-slate-950/50 ${isTarget ? layer.textColor : 'text-slate-500'}`}>
                              <Icon className="w-6 h-6" />
                            </div>
                            <div className="flex-1">
                              <div className="text-[10px] font-mono text-slate-500 mb-1 flex justify-between items-center tracking-wider">
                                <span>LAYER {layer.id}</span>
                                {isTarget && (
                                  <span className="flex items-center gap-1 text-rose-400 font-bold bg-rose-950/50 px-2 py-0.5 rounded border border-rose-500/30 animate-pulse">
                                    <Target className="w-3 h-3" /> GAP
                                  </span>
                                )}
                              </div>
                              <h3 className={`font-bold text-lg leading-tight ${isTarget ? 'text-white' : 'text-slate-400'}`}>
                                {layer.name}
                              </h3>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="col-span-1 lg:col-span-7 space-y-6">
                      <div className="flex flex-wrap gap-2">
                        {activeExitCategory.buyers.map((buyer, idx) => (
                          <button
                            key={idx}
                            onClick={() => setActiveBuyer(buyer)}
                            className={`px-4 py-2 rounded-lg border transition-all text-sm font-semibold ${
                              activeBuyer.name === buyer.name
                                ? 'bg-rose-900/20 border-rose-500/50 text-white'
                                : 'bg-slate-900/40 border-slate-700/50 text-slate-400 hover:border-slate-500 hover:bg-slate-800'
                            }`}
                          >
                            {buyer.name}
                          </button>
                        ))}
                      </div>

                      <div className="bg-[#111827] border border-slate-800 rounded-2xl p-6 md:p-8 relative overflow-hidden transition-all duration-300">
                        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-[80px] opacity-10 bg-rose-500 pointer-events-none transition-all duration-500" />
                        <div className="relative z-10">
                          <div className="flex justify-between items-start mb-8">
                            <div>
                              <h2 className="text-3xl font-bold text-white mb-2">{activeBuyer.name}</h2>
                              <span className="text-xs font-mono text-slate-400 bg-slate-900 px-2 py-1 rounded border border-slate-700">{activeBuyer.ticker}</span>
                            </div>
                            <div className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${
                              activeBuyer.urgency === 'Extreme' || activeBuyer.urgency === 'Critical' ? 'bg-rose-950/50 text-rose-400 border border-rose-500/30' :
                              activeBuyer.urgency === 'High' ? 'bg-orange-950/50 text-orange-400 border border-orange-500/30' :
                              'bg-blue-950/50 text-blue-400 border border-blue-500/30'
                            }`}>
                              <AlertTriangle className="w-3.5 h-3.5" /> {activeBuyer.urgency} Urgency
                            </div>
                          </div>
                          <div className="space-y-6">
                            <div className="bg-[#0a0f1a] p-5 rounded-xl border border-slate-800/80 shadow-inner">
                              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                                <Crosshair className="w-4 h-4 text-rose-400" /> Critical Technology Gap
                              </h4>
                              <p className="text-white font-medium text-lg">{renderWithJargon(activeBuyer.gap)}</p>
                            </div>
                            <div>
                              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                                <Briefcase className="w-4 h-4 text-slate-400" /> Strategic M&A Thesis
                              </h4>
                              <p className="text-slate-300 leading-relaxed text-[15px]">{renderWithJargon(activeBuyer.thesis)}</p>
                            </div>
                            <div className="border-t border-slate-800/60 pt-5">
                              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                                <Activity className="w-4 h-4 text-emerald-400" /> Recent Market Signal
                              </h4>
                              <p className="text-slate-400 text-sm italic">"{renderWithJargon(activeBuyer.recentMove)}"</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-[#0B1120] border border-slate-800 rounded-2xl p-6 md:p-10 shadow-xl animate-fade-in">
                  <div className="mb-6">
                    <div className="flex items-center gap-2 text-blue-400 font-mono text-xs font-bold tracking-widest uppercase mb-3">
                      <Zap className="w-4 h-4" /> M&A Case Study
                    </div>
                    <h2 className="text-3xl font-serif font-bold text-white mb-4">Neo-Prime Playbook: Anduril's Vertical Roll-up</h2>
                    <p className="text-slate-400 text-[15px] leading-relaxed max-w-4xl">
                      While legacy primes acquire software to fix technical debt, Anduril executes a reverse playbook. By dominating the top software layers of the stack (<strong className="text-white">L4 &amp; L5</strong>), they systematically acquire hardware "shells" and "metabolism" (<strong className="text-white">L1-L3</strong>) to prove their Lattice OS can turn any physical asset into an autonomous weapon.
                    </p>
                  </div>

                  <div className="space-y-1.5 relative">
                     {/* L5 */}
                     <div className="flex flex-col md:flex-row bg-[#111827] border border-slate-700/50 rounded-xl overflow-hidden shadow-sm">
                        <div className="md:w-48 lg:w-52 shrink-0 bg-[#151c2d] p-4 border-b md:border-b-0 md:border-r border-slate-700/50 flex flex-col justify-center">
                          <div className="flex items-center gap-2 text-indigo-400 mb-1">
                            <BrainCircuit className="w-4 h-4" /> <span className="text-[10px] font-mono tracking-widest uppercase">Layer 5</span>
                          </div>
                          <h3 className="text-lg font-bold text-white">Mission Intel</h3>
                        </div>
                        <div className="flex-1 p-4 flex flex-col justify-center">
                          <div className="flex items-center gap-3 mb-1.5">
                            <span className="px-2 py-0.5 bg-blue-900/30 border border-blue-500/40 text-blue-300 text-[9px] font-mono tracking-widest uppercase rounded">Core IP</span>
                            <h4 className="text-base font-bold text-white">Lattice AI / Menace</h4>
                          </div>
                          <p className="text-sm text-slate-400 leading-relaxed">Native software capability. The autonomous decision-making brain and electronic warfare (EW) sensor fusion algorithms.</p>
                        </div>
                     </div>

                     {/* L4 */}
                     <div className="flex flex-col md:flex-row bg-[#111827] border border-slate-700/50 rounded-xl overflow-hidden shadow-sm">
                        <div className="md:w-48 lg:w-52 shrink-0 bg-[#151c2d] p-4 border-b md:border-b-0 md:border-r border-slate-700/50 flex flex-col justify-center">
                          <div className="flex items-center gap-2 text-blue-400 mb-1">
                            <Network className="w-4 h-4" /> <span className="text-[10px] font-mono tracking-widest uppercase">Layer 4</span>
                          </div>
                          <h3 className="text-lg font-bold text-white">Interoperability</h3>
                        </div>
                        <div className="flex-1 p-4 flex flex-col justify-center">
                          <div className="flex items-center gap-3 mb-1.5">
                            <span className="px-2 py-0.5 bg-blue-900/30 border border-blue-500/40 text-blue-300 text-[9px] font-mono tracking-widest uppercase rounded">Core IP</span>
                            <h4 className="text-base font-bold text-white">Lattice OS</h4>
                          </div>
                          <p className="text-sm text-slate-400 leading-relaxed">The foundational open-architecture operating system. The integration fabric that connects and commands all acquired physical assets.</p>
                        </div>
                     </div>

                     {/* Divider */}
                     <div className="py-2.5 flex flex-col items-center justify-center relative z-20">
                        <div className="h-2.5 w-px bg-rose-500/40 absolute top-0"></div>
                        <div className="text-[8px] font-mono text-rose-500/90 uppercase tracking-widest bg-[#0B1120] px-3 z-10 font-bold">Roll-up Acquisition Targets ↓</div>
                        <div className="h-2.5 w-px bg-rose-500/40 absolute bottom-0"></div>
                     </div>

                     {/* L3 */}
                     <div className="flex flex-col md:flex-row bg-[#0a0f1a] border border-slate-800 rounded-xl overflow-hidden shadow-sm hover:border-slate-700 transition-colors">
                        <div className="md:w-48 lg:w-52 shrink-0 bg-[#0d1324] p-4 border-b md:border-b-0 md:border-r border-slate-800 flex flex-col justify-center">
                          <div className="flex items-center gap-2 text-teal-500 mb-1">
                            <Radio className="w-4 h-4" /> <span className="text-[10px] font-mono tracking-widest uppercase">Layer 3</span>
                          </div>
                          <h3 className="text-lg font-bold text-white">Transport Mesh</h3>
                        </div>
                        <div className="flex-1 p-4 flex flex-col justify-center">
                          <div className="flex items-center gap-3 mb-1.5">
                            <span className="px-2 py-0.5 bg-rose-950/40 border border-rose-900/50 text-rose-400/80 flex items-center gap-1.5 text-[9px] font-mono tracking-widest uppercase rounded"><GitMerge className="w-3 h-3" /> Acquired Apr 2024</span>
                            <h4 className="text-base font-bold text-white">ExoAnalytic Solutions</h4>
                          </div>
                          <p className="text-sm text-slate-400 leading-relaxed">Global optical telescope network for Space Domain Awareness. Expands Lattice into orbit to bid on major Space Force contracts as a Prime.</p>
                        </div>
                     </div>

                     {/* L2 */}
                     <div className="flex flex-col md:flex-row bg-[#0a0f1a] border border-slate-800 rounded-xl overflow-hidden shadow-sm hover:border-slate-700 transition-colors">
                        <div className="md:w-48 lg:w-52 shrink-0 bg-[#0d1324] p-4 border-b md:border-b-0 md:border-r border-slate-800 flex flex-col justify-center">
                          <div className="flex items-center gap-2 text-emerald-500 mb-1">
                            <Cpu className="w-4 h-4" /> <span className="text-[10px] font-mono tracking-widest uppercase">Layer 2</span>
                          </div>
                          <h3 className="text-lg font-bold text-white">Compute & Energy</h3>
                        </div>
                        <div className="flex-1 p-4 flex flex-col justify-center">
                          <div className="flex items-center gap-3 mb-1.5">
                            <span className="px-2 py-0.5 bg-rose-950/40 border border-rose-900/50 text-rose-400/80 flex items-center gap-1.5 text-[9px] font-mono tracking-widest uppercase rounded"><GitMerge className="w-3 h-3" /> Acquired Jun 2023</span>
                            <h4 className="text-base font-bold text-white">Adranos</h4>
                          </div>
                          <p className="text-sm text-slate-400 leading-relaxed">ALITEC solid rocket motors. Vertically integrates propulsion to solve the "AUKUS Munitions Bottleneck" for proprietary kinetic interceptors.</p>
                        </div>
                     </div>

                     {/* L1 */}
                     <div className="flex flex-col md:flex-row bg-[#0a0f1a] border border-slate-800 rounded-xl overflow-hidden shadow-sm hover:border-slate-700 transition-colors">
                        <div className="md:w-48 lg:w-52 shrink-0 bg-[#0d1324] p-4 border-b md:border-b-0 md:border-r border-slate-800 flex flex-col justify-center">
                          <div className="flex items-center gap-2 text-slate-400 mb-1">
                            <Rocket className="w-4 h-4" /> <span className="text-[10px] font-mono tracking-widest uppercase">Layer 1</span>
                          </div>
                          <h3 className="text-lg font-bold text-white">Physical Edge</h3>
                        </div>
                        <div className="flex-1 p-4 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
                          <div className="md:col-span-5">
                            <div className="flex items-center gap-3 mb-1.5">
                              <span className="px-2 py-0.5 bg-rose-950/40 border border-rose-900/50 text-rose-400/80 flex items-center gap-1.5 text-[9px] font-mono tracking-widest uppercase rounded"><GitMerge className="w-3 h-3" /> Acquired Sep 2023</span>
                            </div>
                            <h4 className="text-base font-bold text-white mb-1.5">Blue Force Tech</h4>
                            <p className="text-sm text-slate-400 leading-relaxed">The "Fury" autonomous fighter jet, allowing Anduril to compete directly against Legacy Primes for the CCA program.</p>
                          </div>
                          <div className="md:col-span-7 pt-3 md:pt-0 border-t md:border-t-0 md:border-l border-slate-800 md:pl-6">
                            <div className="flex items-center gap-3 mb-1.5">
                              <span className="px-2 py-0.5 bg-rose-950/40 border border-rose-900/50 text-rose-400/80 flex items-center gap-1.5 text-[9px] font-mono tracking-widest uppercase rounded"><GitMerge className="w-3 h-3" /> Acquired Early 2022</span>
                            </div>
                            <h4 className="text-base font-bold text-white mb-1.5">Dive Technologies</h4>
                            <p className="text-sm text-slate-400 leading-relaxed">"Dive-LD" 3D-printed XL-UUV. Secured major AUKUS maritime contracts by proving Lattice OS endurance on submersibles.</p>
                          </div>
                        </div>
                     </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* VIEW: Execution */}
          {activeView === 'execution' && (
            <div className="max-w-5xl mx-auto space-y-8 pb-12">
              
              <div className="bg-gradient-to-r from-slate-900 to-indigo-950/30 border border-slate-800 rounded-2xl p-8 mb-8">
                <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-3">
                  08 // Execution Roadmap
                </div>
                <h2 className="text-3xl font-serif font-bold text-white mb-3">Execution: From Sourcing to Asset Deployment</h2>
                <p className="text-slate-400 text-lg leading-relaxed mb-4">
                  <span className="text-slate-200 font-medium">A structured lifecycle for identifying, auditing, and scaling high-conviction defense tech positions.</span> Moving from landscape awareness to capital deployment requires a shift from market data to bespoke alpha. We propose a three-pronged execution roadmap to de-risk entry and maximize the 'Resilience Premium'.
                </p>
              </div>

              <div className="bg-[#111827] border border-slate-800 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row gap-6 md:gap-8 hover:border-slate-700 transition-colors">
                <div className="flex flex-col items-center gap-3 shrink-0 md:w-28 pt-2">
                  <div className="w-16 h-16 rounded-full bg-blue-900/20 text-blue-400 flex items-center justify-center border border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.1)]">
                    <Target className="w-7 h-7" />
                  </div>
                  <span className="text-xs font-bold text-slate-500 tracking-widest uppercase">Phase I</span>
                </div>
                
                <div className="flex-1 space-y-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <h3 className="text-xl font-bold text-white">Thematic Deal Origination</h3>
                    <span className="px-3 py-1 bg-blue-950/50 border border-blue-500/30 text-blue-400 text-[10px] font-mono font-bold uppercase tracking-widest rounded-lg whitespace-nowrap">
                      [ T + 48 HOURS ]
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Thematic Sourcing</div>
                      <p className="text-sm text-slate-300 leading-relaxed">
                        {renderWithJargon("Instead of generic watchlists, we identify targets by mapping your specific investment mandate against the 'Operational Stack' gaps.")}
                      </p>
                    </div>
                    <div className="bg-emerald-900/10 border border-emerald-500/20 rounded-xl p-5">
                      <div className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mb-2">Execution Action</div>
                      <p className="text-sm text-emerald-400/90 leading-relaxed">
                        {renderWithJargon("Deploy our proprietary reasoning engine to scan the global landscape for 'off-market' targets that fit your precise criteria.")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-sm pt-3 border-t border-slate-800/60">
                    <FileText className="w-4 h-4 text-blue-400" />
                    <span className="font-bold text-[11px] text-blue-400 tracking-wider uppercase">Deliverable:</span>
                    <span className="text-slate-300 font-medium">A proprietary deal flow pipeline and target short-list.</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#111827] border border-slate-800 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row gap-6 md:gap-8 hover:border-slate-700 transition-colors">
                <div className="flex flex-col items-center gap-3 shrink-0 md:w-28 pt-2">
                  <div className="w-16 h-16 rounded-full bg-purple-900/20 text-purple-400 flex items-center justify-center border border-purple-500/20 shadow-[0_0_15px_rgba(168,85,247,0.1)]">
                    <ClipboardCheck className="w-7 h-7" />
                  </div>
                  <span className="text-xs font-bold text-slate-500 tracking-widest uppercase">Phase II</span>
                </div>
                
                <div className="flex-1 space-y-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <h3 className="text-xl font-bold text-white">4-Pillar Defense Audit</h3>
                    <span className="px-3 py-1 bg-purple-950/50 border border-purple-500/30 text-purple-400 text-[10px] font-mono font-bold uppercase tracking-widest rounded-lg whitespace-nowrap">
                      [ T + 5 DAYS ]
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Sector-Specialist Audit</div>
                      <p className="text-sm text-slate-300 leading-relaxed">
                        {renderWithJargon("Shift from generic agile diligence to a formal defense audit validated by domain experts.")}
                      </p>
                    </div>
                    <div className="bg-emerald-900/10 border border-emerald-500/20 rounded-xl p-5">
                      <div className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mb-3">The 4-Pillars</div>
                      <ul className="text-sm text-emerald-400/90 leading-relaxed space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-500/50 mt-1 text-[10px]">▶</span>
                          <span><span className="font-semibold text-emerald-300">Revenue Quality:</span> {renderWithJargon("Migration from SBIR/OTA to Programs of Record (PoR)")}.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-500/50 mt-1 text-[10px]">▶</span>
                          <span><span className="font-semibold text-emerald-300">Technical Moat:</span> Validation of hardware-software integration &amp; unit economics.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-500/50 mt-1 text-[10px]">▶</span>
                          <span><span className="font-semibold text-emerald-300">Sovereign Compliance:</span> {renderWithJargon("ITAR")}, {renderWithJargon("FedRAMP")}, and export risks.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-500/50 mt-1 text-[10px]">▶</span>
                          <span><span className="font-semibold text-emerald-300">Integration Surface:</span> Measuring "Vendor Lock-in" potential and verifying API-driven, agentic interfaces cleared for DoD Cloud Computing SRG Impact Level 5 (IL5) or Impact Level 6 (IL6).</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-sm pt-3 border-t border-slate-800/60">
                    <FileText className="w-4 h-4 text-purple-400" />
                    <span className="font-bold text-[11px] text-purple-400 tracking-wider uppercase">Deliverable:</span>
                    <span className="text-slate-300 font-medium">De-risked assets with verified commercial viability.</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#111827] border border-slate-800 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row gap-6 md:gap-8 hover:border-slate-700 transition-colors">
                <div className="flex flex-col items-center gap-3 shrink-0 md:w-28 pt-2">
                  <div className="w-16 h-16 rounded-full bg-rose-900/20 text-rose-400 flex items-center justify-center border border-rose-500/20 shadow-[0_0_15px_rgba(225,29,72,0.1)]">
                    <GitMerge className="w-7 h-7" />
                  </div>
                  <span className="text-xs font-bold text-slate-500 tracking-widest uppercase">Phase III</span>
                </div>
                
                <div className="flex-1 space-y-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <h3 className="text-xl font-bold text-white">Portfolio Engineering &amp; Exit Strategy</h3>
                    <span className="px-3 py-1 bg-rose-950/50 border border-rose-500/30 text-rose-400 text-[10px] font-mono font-bold uppercase tracking-widest rounded-lg whitespace-nowrap">
                      [ ONGOING ADVISORY ]
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Portfolio Value Creation</div>
                      <p className="text-sm text-slate-300 leading-relaxed">
                        {renderWithJargon("Focus strictly on engineering exits by mapping the portfolio against the 'Strategic Acquirer Heatmap'.")}
                      </p>
                    </div>
                    <div className="bg-emerald-900/10 border border-emerald-500/20 rounded-xl p-5">
                      <div className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mb-2">Execution Action</div>
                      <p className="text-sm text-emerald-400/90 leading-relaxed">
                        {renderWithJargon("Align target IP with the 'Technical Debt' of legacy primes to orchestrate premium integration multiples at exit.")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-sm pt-3 border-t border-slate-800/60">
                    <FileText className="w-4 h-4 text-rose-400" />
                    <span className="font-bold text-[11px] text-rose-400 tracking-wider uppercase">Deliverable:</span>
                    <span className="text-slate-300 font-medium">A resilient portfolio architecture optimized for premium strategic exits.</span>
                  </div>
                </div>
              </div>

              {/* Strategic Recap (Moved from Exec Summary) */}
              <section className="mt-12 space-y-0 relative z-20 bg-[#0f172a] border border-slate-800 rounded-2xl p-2 shadow-lg">
                <button 
                  onClick={() => setIsTldrExpanded(!isTldrExpanded)}
                  className="w-full flex items-center justify-between p-4 bg-transparent hover:bg-slate-800/50 rounded-xl transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-cyan-900/20 border border-cyan-500/20 group-hover:border-cyan-500/40 transition-colors">
                      <Target className="w-4 h-4 text-cyan-500" />
                    </div>
                    <div className="text-left">
                      <div className="text-xs font-mono text-slate-300 font-bold uppercase tracking-widest group-hover:text-cyan-400 transition-colors">
                        Strategic Recap: Alpha Matrix
                      </div>
                      <div className="text-sm text-slate-500 mt-0.5 hidden sm:block">
                        Expand for a final review of optimal entry points, target multiples, and avoid zones before initiating.
                      </div>
                    </div>
                  </div>
                  <div className="p-1.5 rounded-lg bg-slate-900/50 text-slate-500 group-hover:text-white group-hover:bg-slate-800 transition-colors">
                    {isTldrExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                </button>
                
                {isTldrExpanded && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in p-4 pt-2">
                    {/* Card 1: Risk-Adjusted Entry */}
                    <div className="bg-blue-900/10 border border-blue-500/30 rounded-xl p-5 hover:bg-blue-900/20 transition-colors shadow-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <Network className="w-4 h-4 text-blue-400" />
                        <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Best Risk-Adjusted Entry</h3>
                      </div>
                      <div className="text-lg font-bold text-white mb-2">L4: Interoperability Fabric</div>
                      <p className="text-xs text-slate-400 leading-relaxed">{renderWithJargon("Software middleware breaking vendor lock-in. Features highest contract stickiness and lowest ITAR friction.")}</p>
                    </div>

                    {/* Card 2: Highest Multiple */}
                    <div className="bg-indigo-900/10 border border-indigo-500/30 rounded-xl p-5 hover:bg-indigo-900/20 transition-colors shadow-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <BrainCircuit className="w-4 h-4 text-indigo-400" />
                        <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Highest Multiple Potential</h3>
                      </div>
                      <div className="text-lg font-bold text-white mb-2">L5: Mission Intelligence</div>
                      <p className="text-xs text-slate-400 leading-relaxed">{renderWithJargon("Agentic AI executing missions. Commands premium tech multiples (20x-30x Forward Rev) due to pure-software margins.")}</p>
                    </div>

                    {/* Card 3: Capital Efficiency */}
                    <div className="bg-emerald-900/10 border border-emerald-500/30 rounded-xl p-5 hover:bg-emerald-900/20 transition-colors shadow-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-4 h-4 text-emerald-400" />
                        <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Highest Cap Efficiency</h3>
                      </div>
                      <div className="text-lg font-bold text-white mb-2">Dual-Use Architectures</div>
                      <p className="text-xs text-slate-400 leading-relaxed">{renderWithJargon("Commercial revenue offsets burn rate while awaiting MOD/Global Defense Programs of Record. Safest bridge across the \"Valley of Death\".")}</p>
                    </div>

                    {/* Card 4: Avoid Zone */}
                    <div className="bg-rose-900/10 border border-rose-500/30 rounded-xl p-5 hover:bg-rose-900/20 transition-colors shadow-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-4 h-4 text-rose-400" />
                        <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider">The "Avoid" Zone</h3>
                      </div>
                      <div className="text-lg font-bold text-white mb-2">Legacy Metal-Bending</div>
                      <p className="text-xs text-slate-400 leading-relaxed">{renderWithJargon("With Neo-Primes now securing $B+ Enterprise production contracts, the window for legacy primes to bridge their technical debt via M&A is rapidly closing. Hardware platforms without proprietary software face severe margin compression.")}</p>
                    </div>
                  </div>
                )}
              </section>

              {/* Closing Call to Action with Leadership Profile */}
              <div className="mt-12 bg-gradient-to-br from-[#111827] to-[#0B1120] border border-indigo-500/20 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 md:gap-12 relative overflow-hidden group shadow-[0_20px_50px_rgba(0,0,0,0.3)]">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[100px] group-hover:bg-indigo-500/20 transition-colors pointer-events-none" />
                
                {/* Leadership Profile */}
                <div className="flex flex-col items-center md:items-start shrink-0 text-center md:text-left z-10 border-b md:border-b-0 md:border-r border-slate-800/80 pb-8 md:pb-0 md:pr-12">
                  <img 
                    src="/images/profile_balbinder.png" 
                    alt="Balbinder Singh Bhatia" 
                    className="w-28 h-28 rounded-full border-[3px] border-[#1E293B] shadow-[0_0_25px_rgba(0,0,0,0.6)] object-cover mb-5"
                  />
                  <div className="text-[10px] font-bold text-indigo-400 uppercase tracking-[0.2em] mb-2">
                    Practice Leadership
                  </div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <h4 className="text-xl font-bold text-white tracking-wide">Balbinder Singh Bhatia</h4>
                    <a 
                      href="https://www.linkedin.com/in/bal1der/" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-slate-400 hover:text-[#0A66C2] transition-colors"
                      title="View LinkedIn Profile"
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                  </div>
                  <div className="text-sm text-slate-400">
                    Managing Partner, Frontier
                  </div>
                </div>

                {/* CTA Text */}
                <div className="flex-1 flex flex-col items-center md:items-start text-center md:text-left z-10">
                  <h3 className="text-3xl font-serif font-bold text-white mb-4">Initiate Strategic Mandate</h3>
                  <p className="text-slate-400 text-lg mb-8 leading-relaxed max-w-xl">
                    Ready to move from observation to deployment? Engage our practice leadership directly to begin bespoke thematic sourcing aligned with your exact investment parameters.
                  </p>
                  <button 
                    onClick={() => setIsInquiryOpen(true)}
                    className="px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl transition-all shadow-[0_0_20px_rgba(79,70,229,0.4)] hover:shadow-[0_0_30px_rgba(79,70,229,0.6)] flex items-center gap-3 text-lg"
                  >
                    <Command className="w-5 h-5" /> Request Target Sourcing Brief
                  </button>
                </div>
              </div>

            </div>
          )}

          {/* VIEW: Allied Market Entry Case Study */}
          {activeView === 'allied_case' && (
            <div className="max-w-5xl mx-auto space-y-8 pb-12 animate-fade-in">
              <div className="border-b border-slate-800 pb-6">
                <div className="text-xs font-mono text-blue-400 font-bold tracking-[0.2em] uppercase mb-3">
                  09 // Allied Market Entry
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <BookOpen className="w-8 h-8 text-blue-400 shrink-0" />
                  <h2 className="text-3xl font-serif font-bold text-white">Strategic Advisory Dossier: Allied Market Entry &amp; Alignment</h2>
                </div>
                <p className="text-slate-400 text-lg max-w-4xl">
                  A board-level diagnostic mapping East Asian industrial manufacturing capacity against U.S. Department of Defense (DoD) compliance boundaries and Defense Tech 2.0 interoperability requirements. This blueprint outlines the strategic pathways to bridge the gap between sovereign hardware capacity and U.S. Program of Record (PoR) demands.
                </p>
              </div>

              {/* Part 1 Content */}
              <div className="space-y-10">
                
                {/* Executive Architecture */}
                <div className="bg-[#111827] border border-slate-800 rounded-2xl p-6 md:p-8 hover:border-slate-700 transition-colors">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Network className="w-5 h-5 text-indigo-400" />
                    Executive Architecture: The Allied Gap Inversion
                  </h3>
                  <p className="text-slate-400 text-[15px] leading-relaxed mb-6">
                    A common strategic error made by allied industrials entering the U.S. market is assuming that legacy hardware capabilities (Defense 1.0/1.5) translate into immediate U.S. DoD procurement. To bridge the gap between East Asian industrial capacity and U.S. demand, we map <strong className="text-white">LIG Defense & Aerospace</strong> across the 5-Layer taxonomy to isolate exactly what they can manufacture domestically versus what they can legally sell and execute inside the United States.
                  </p>
                  
                  {/* ASCII-style Diagram turned into flex UI */}
                  <div className="bg-[#0a0f1a] border border-slate-800 rounded-xl p-6 font-mono text-xs sm:text-sm text-slate-400 overflow-x-auto custom-scrollbar">
                    <div className="min-w-[600px]">
                      <div className="flex justify-center mb-4">
                        <div className="px-4 py-2 bg-slate-900 border border-slate-700 text-slate-300 font-bold rounded-lg text-center">
                          [ LIG DOMESTIC SENEGAL ]<br/>
                          <span className="text-[10px] text-slate-500 font-normal">(Seoul / Gumi Foundry)</span>
                        </div>
                      </div>
                      
                      <div className="space-y-2 mb-6 max-w-lg mx-auto">
                        <div className="flex items-center gap-4"><span className="text-indigo-400 w-12 text-right">L5:</span> <span>Fire-control / C2 Systems (1.5 Hard-coded)</span></div>
                        <div className="flex items-center gap-4"><span className="text-blue-400 w-12 text-right">L4:</span> <span>Siloed Proprietary Architectures</span></div>
                        <div className="flex items-center gap-4"><span className="text-teal-400 w-12 text-right">L3:</span> <span>Shipborne EW & Tactical Communication Radios</span></div>
                        <div className="flex items-center gap-4"><span className="text-emerald-400 w-12 text-right">L2:</span> <span>Analog Rocket Seekers, Radars & Avionics</span></div>
                        <div className="flex items-center gap-4"><span className="text-slate-400 w-12 text-right">L1:</span> <span>Precision-Guided Missiles & Armoured Hulls</span></div>
                      </div>

                      <div className="flex justify-center mb-6">
                        <div className="flex flex-col items-center">
                          <ArrowDown className="w-5 h-5 text-rose-500 mb-2" />
                          <div className="px-4 py-2 bg-rose-950/30 border border-rose-500/50 text-rose-400 font-bold rounded-lg text-center uppercase tracking-widest shadow-[0_0_15px_rgba(244,63,94,0.1)]">
                            [ THE GRC WALL ]<br/>
                            <span className="text-[10px] text-rose-500/70 font-normal">(Buy American Act / Non-DFARS)</span>
                          </div>
                          <div className="h-6 w-px bg-slate-700 mt-2"></div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-8 max-w-2xl mx-auto">
                        <div className="border-t border-slate-700 pt-4 relative">
                           <div className="absolute -top-[9px] left-1/2 -translate-x-1/2 w-4 h-4 bg-[#0a0f1a] flex items-center justify-center"><ChevronDown className="w-4 h-4 text-slate-600" /></div>
                           <div className="text-center font-bold text-slate-500 mb-4">(US Segment Gaps)</div>
                           <div className="space-y-2">
                             <div className="flex items-center gap-2"><span className="text-indigo-400">L5:</span> Agentic AI / Swarming</div>
                             <div className="flex items-center gap-2"><span className="text-blue-400">L4:</span> JADC2 Open Middleware</div>
                           </div>
                        </div>
                        <div className="border-t border-slate-700 pt-4 relative">
                           <div className="absolute -top-[9px] left-1/2 -translate-x-1/2 w-4 h-4 bg-[#0a0f1a] flex items-center justify-center"><ChevronDown className="w-4 h-4 text-slate-600" /></div>
                           <div className="text-center font-bold text-emerald-500 mb-4">(US Compliance Path)</div>
                           <div className="space-y-2">
                             <div className="flex items-center gap-2"><span className="text-slate-400">L1:</span> Onshore "Shell" (Ghost Robotics)</div>
                             <div className="flex items-center gap-2"><span className="text-blue-400">L4:</span> Software Wrapper (Palantir MOU)</div>
                           </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pillar 1: Prioritization */}
                <div className="bg-[#111827] border border-slate-800 rounded-2xl overflow-hidden hover:border-slate-700 transition-colors shadow-lg">
                  <div className="p-6 md:p-8 border-b border-slate-800 bg-slate-900/40">
                    <div className="text-[10px] font-mono font-bold text-blue-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                      <Target className="w-3.5 h-3.5" /> Pillar I
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Market Entry & Vector Prioritization</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      Our 5-Layer Stack disaggregates LIG's native capabilities to isolate high-conviction U.S. modernization vectors. We map these capabilities to current U.S. Programs of Record (PoRs) and prioritize optimal entry strategies based on regulatory friction and technological alignment.
                    </p>
                  </div>
                  
                  <div className="p-0 overflow-x-auto custom-scrollbar">
                    <table className="w-full text-left text-sm whitespace-nowrap md:whitespace-normal">
                      <thead>
                        <tr className="bg-[#0a0f1a] border-b border-slate-800 text-xs font-mono text-slate-500 uppercase tracking-wider">
                          <th className="p-4 pl-6 md:pl-8 font-semibold w-1/4">5-Layer Stack</th>
                          <th className="p-4 font-semibold w-1/4">Sovereign Domestic Baseline</th>
                          <th className="p-4 font-semibold w-1/4">Def Tech 2.0 Target Vector</th>
                          <th className="p-4 pr-6 md:pr-8 font-semibold w-1/4">U.S. Entry Prioritization</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/60 text-slate-300">
                        <tr className="hover:bg-slate-800/30 transition-colors">
                          <td className="p-4 pl-6 md:pl-8 font-medium text-indigo-400">L5: Mission Intelligence</td>
                          <td className="p-4 text-slate-400 text-xs">Traditional hard-coded C2 & tracking</td>
                          <td className="p-4 text-xs font-semibold text-white">Agentic AI & Swarm Orchestration</td>
                          <td className="p-4 pr-6 md:pr-8">
                            <span className="inline-block px-2 py-0.5 rounded bg-rose-950/50 text-rose-400 border border-rose-500/20 text-[10px] font-bold tracking-widest uppercase mb-1">RESTRICT / ALLIANCE ONLY</span>
                            <p className="text-[11px] text-slate-400 leading-relaxed">Foreign ownership restrictions (CFIUS) heavily guard U.S. AI. Direct M&A is restricted; entry is prioritized through joint software ventures.</p>
                          </td>
                        </tr>
                        <tr className="hover:bg-slate-800/30 transition-colors">
                          <td className="p-4 pl-6 md:pl-8 font-medium text-blue-400">L4: Interoperability Fabric</td>
                          <td className="p-4 text-slate-400 text-xs">Siloed, proprietary hardware architectures</td>
                          <td className="p-4 text-xs font-semibold text-white">Data Fusion & OS (e.g., Lattice)</td>
                          <td className="p-4 pr-6 md:pr-8">
                            <span className="inline-block px-2 py-0.5 rounded bg-amber-950/50 text-amber-400 border border-amber-500/20 text-[10px] font-bold tracking-widest uppercase mb-1">HIGH TECHNOLOGY GAP</span>
                            <p className="text-[11px] text-slate-400 leading-relaxed">Allied hardware must integrate with U.S. open-architecture operating systems to be compatible with CJADC2 streams. High priority for acquisition.</p>
                          </td>
                        </tr>
                        <tr className="hover:bg-slate-800/30 transition-colors">
                          <td className="p-4 pl-6 md:pl-8 font-medium text-teal-400">L3: Transport Mesh</td>
                          <td className="p-4 text-slate-400 text-xs">Airborne EW pods, shipborne radar arrays</td>
                          <td className="p-4 text-xs font-semibold text-white">Software-Defined RF & Jam-Resilient LEO</td>
                          <td className="p-4 pr-6 md:pr-8">
                            <span className="inline-block px-2 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-600 text-[10px] font-bold tracking-widest uppercase mb-1">LOW PRIORITY</span>
                            <p className="text-[11px] text-slate-400 leading-relaxed">Strong domestic capability but U.S. market is highly secure and locked by domestic space primes. High friction vector.</p>
                          </td>
                        </tr>
                        <tr className="hover:bg-slate-800/30 transition-colors">
                          <td className="p-4 pl-6 md:pl-8 font-medium text-emerald-400">L2: Compute & Energy</td>
                          <td className="p-4 text-slate-400 text-xs">Analog thermal seekers, radar-frequency seekers</td>
                          <td className="p-4 text-xs font-semibold text-white">Edge AI Hardware (Rad-Hardened GPUs)</td>
                          <td className="p-4 pr-6 md:pr-8">
                            <span className="inline-block px-2 py-0.5 rounded bg-emerald-950/50 text-emerald-400 border border-emerald-500/20 text-[10px] font-bold tracking-widest uppercase mb-1">NATIVE STRENGTH</span>
                            <p className="text-[11px] text-slate-400 leading-relaxed">Excellent hardware capability, but cannot be imported directly into the DoD pipeline as a completed system due to BAA non-compliance.</p>
                          </td>
                        </tr>
                        <tr className="hover:bg-slate-800/30 transition-colors">
                          <td className="p-4 pl-6 md:pl-8 font-medium text-slate-300">L1: Physical Edge</td>
                          <td className="p-4 text-slate-400 text-xs">Guided interceptors, Chiron MANPADS</td>
                          <td className="p-4 text-xs font-semibold text-white">C-UAS Interception & Attritable Mass</td>
                          <td className="p-4 pr-6 md:pr-8">
                            <span className="inline-block px-2 py-0.5 rounded bg-blue-900/40 text-blue-400 border border-blue-500/30 text-[10px] font-bold tracking-widest uppercase mb-1">ONSHORE LOCALIZATION VECTOR</span>
                            <p className="text-[11px] text-slate-400 leading-relaxed">Sovereign hardware must be packaged inside BAA-compliant physical platforms (such as Ghost Robotics) to serve as delivery shells for U.S. installations.</p>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pillar 2: Federal Regulations & Compliance */}
                <div className="bg-[#111827] border border-slate-800 rounded-2xl overflow-hidden hover:border-slate-700 transition-colors shadow-lg">
                  <div className="p-6 md:p-8 border-b border-slate-800 bg-slate-900/40">
                    <div className="text-[10px] font-mono font-bold text-emerald-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                      <Shield className="w-3.5 h-3.5" /> Pillar II
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Federal Regulations, Compliance & Onshore Readiness</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      U.S. market entry requires navigating complex regulatory frameworks (ITAR, CMMC, and the Buy American Act). A critical GRC distinction exists between Japanese and South Korean entry pathways that structurally dictates onshore footprint and capital requirements.
                    </p>
                  </div>
                  
                  <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    {/* Japan Pathway */}
                    <div className="bg-[#0a0f1a] border border-emerald-500/20 rounded-xl p-6 relative overflow-hidden group">
                      <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 rounded-bl-full -z-10" />
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-3xl">🇯🇵</span>
                        <div>
                          <h4 className="font-bold text-white text-lg">The Japanese Pathway</h4>
                          <span className="text-[10px] font-mono text-emerald-400 font-bold uppercase tracking-widest bg-emerald-950/40 border border-emerald-500/30 px-2 py-0.5 rounded">DFARS Qualifying</span>
                        </div>
                      </div>
                      <p className="text-sm text-slate-400 leading-relaxed mb-4">
                        Japan holds official DFARS Qualifying Country status. The Buy American Act (BAA) restrictions can be legally waived for Japanese-origin components.
                      </p>
                      <div className="border-t border-slate-800/60 pt-4 mt-auto">
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Strategic Execution</div>
                        <div className="text-sm text-slate-300 font-medium leading-relaxed">
                          Allows Japanese material science and sensor firms to act as Tier 2 hardware suppliers directly to U.S. Neo-Primes, without the massive capital expenditure of building onshore U.S. factories.
                        </div>
                      </div>
                    </div>

                    {/* South Korea Pathway */}
                    <div className="bg-[#0a0f1a] border border-rose-500/20 rounded-xl p-6 relative overflow-hidden group">
                      <div className="absolute top-0 right-0 w-24 h-24 bg-rose-500/10 rounded-bl-full -z-10" />
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-3xl">🇰🇷</span>
                        <div>
                          <h4 className="font-bold text-white text-lg">The South Korean Pathway</h4>
                          <span className="text-[10px] font-mono text-rose-400 font-bold uppercase tracking-widest bg-rose-950/40 border border-rose-500/30 px-2 py-0.5 rounded">BAA Restricted</span>
                        </div>
                      </div>
                      <p className="text-sm text-slate-400 leading-relaxed mb-4">
                        South Korea is currently a non-DFARS country. Completed defense systems face a steep, legally mandated domestic-content threshold under the BAA.
                      </p>
                      <div className="border-t border-slate-800/60 pt-4 mt-auto">
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Strategic Execution</div>
                        <div className="text-sm text-slate-300 font-medium leading-relaxed">
                          South Korean primes must execute onshore acquisitions (e.g., LIG's Ghost Robotics transaction) to establish a FOCI-mitigated, CMMC Level 2 compliant U.S. manufacturing footprint.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pillar 3: Strategic M&A & Partnership Matchmaking */}
                <div className="bg-[#111827] border border-slate-800 rounded-2xl overflow-hidden hover:border-slate-700 transition-colors shadow-lg">
                  <div className="p-6 md:p-8 border-b border-slate-800 bg-slate-900/40">
                    <div className="text-[10px] font-mono font-bold text-rose-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                      <GitMerge className="w-3.5 h-3.5" /> Pillar III
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Strategic M&A & Partnership Matchmaking</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      To successfully cross the GRC wall, LIG D&A utilizes a dual-track partnership strategy. We map their capital allocation profile to active U.S. Growth Challengers to establish compliant, high-synergy market pathways:
                    </p>
                  </div>
                  
                  <div className="p-6 md:p-8">
                    <div className="relative">
                      {/* Connecting Line */}
                      <div className="hidden md:block absolute left-1/2 top-8 bottom-8 w-px bg-slate-800/80 -translate-x-1/2"></div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 relative z-10">
                        {/* Track 1: Layer 1 Acquisition */}
                        <div className="bg-[#0a0f1a] border border-slate-800 rounded-xl p-6 relative overflow-hidden group hover:border-rose-500/30 transition-colors">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/5 rounded-bl-full -z-10" />
                          <div className="flex items-start justify-between mb-4 border-b border-slate-800/60 pb-4">
                            <div>
                              <div className="text-[10px] font-mono text-rose-400 font-bold uppercase tracking-widest mb-1">M&A Acquisition (L1)</div>
                              <h4 className="font-bold text-white text-lg">The "Trojan Horse" Shell</h4>
                            </div>
                            <div className="p-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-400">
                              <Rocket className="w-4 h-4" />
                            </div>
                          </div>
                          
                          <div className="space-y-4">
                            <div>
                              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Target Asset</span>
                              <div className="text-sm font-bold text-slate-200">Ghost Robotics (Philadelphia, PA)</div>
                            </div>
                            <div>
                              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Deal Structure</span>
                              <div className="text-sm text-slate-300">60% Controlling Stake ($240M)</div>
                            </div>
                            <div className="bg-slate-900/50 p-3 rounded-xl border border-slate-800">
                              <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest block mb-1.5 flex items-center gap-1.5"><Target className="w-3 h-3 text-rose-400" /> Strategic Synergy</span>
                              <p className="text-xs text-slate-400 leading-relaxed">
                                LIG's acquisition establishes an onshore, legally cleared vehicle. This enables them to package their high-quality domestic electronics and sensors inside a BAA-compliant physical edge platform.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Track 2: Layer 4 Alliance */}
                        <div className="bg-[#0a0f1a] border border-slate-800 rounded-xl p-6 relative overflow-hidden group hover:border-blue-500/30 transition-colors">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-bl-full -z-10" />
                          <div className="flex items-start justify-between mb-4 border-b border-slate-800/60 pb-4">
                            <div>
                              <div className="text-[10px] font-mono text-blue-400 font-bold uppercase tracking-widest mb-1">Strategic Alliance (L4/L5)</div>
                              <h4 className="font-bold text-white text-lg">The "Brain Swap"</h4>
                            </div>
                            <div className="p-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-400">
                              <Network className="w-4 h-4" />
                            </div>
                          </div>
                          
                          <div className="space-y-4">
                            <div>
                              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Target Asset</span>
                              <div className="text-sm font-bold text-slate-200">Palantir Technologies</div>
                            </div>
                            <div>
                              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Deal Structure</span>
                              <div className="text-sm text-slate-300">Strategic MOU & Technology Integration</div>
                            </div>
                            <div className="bg-slate-900/50 p-3 rounded-xl border border-slate-800">
                              <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest block mb-1.5 flex items-center gap-1.5"><BrainCircuit className="w-3 h-3 text-blue-400" /> Strategic Synergy</span>
                              <p className="text-xs text-slate-400 leading-relaxed">
                                Integrates LIG's physical airspace defense interceptors with U.S.-accredited AI targeting suites. Instantly positions their hardware as an interoperable, smart system ready for high-value allied export markets.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </main>

      {/* Active Company Quick-Look Modal */}
      {activeCompany && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0a0f1a]/80 backdrop-blur-sm"
          onClick={() => setActiveCompany(null)}
        >
          <div 
            className="bg-[#111827] border border-slate-700 rounded-2xl w-full max-w-2xl shadow-[0_0_40px_rgba(0,0,0,0.5)] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className={`p-6 border-b border-slate-800 flex justify-between items-start rounded-t-2xl ${activeCompany.tier === 'neoprime' ? 'bg-blue-900/10' : ''}`}>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${
                    activeCompany.tier === 'neoprime' ? 'bg-blue-900/50 border-blue-500 text-blue-300' : 
                    activeCompany.tier === 'legacy' ? 'bg-slate-800 border-slate-600 text-slate-400' : 
                    'bg-slate-800 border-slate-500 text-slate-300'
                  }`}>
                    {activeCompany.tier}
                  </span>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-900 text-slate-400 border border-slate-700 font-bold tracking-widest">
                    HQ: {activeCompany.hq}
                  </span>
                  {activeCompany.tags.filter(tag => !thesisFilters.includes(tag)).map(tag => (
                    <span key={tag} className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${getTagStyle(tag)}`}>
                      {tag === 'DUAL' ? 'Dual-Use (EAR)' : tag}
                    </span>
                  ))}
                </div>
                <h2 className="text-3xl font-bold text-white mt-2">{activeCompany.name}</h2>
              </div>
              <button 
                onClick={() => setActiveCompany(null)}
                className="p-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-400 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-900/40 rounded-xl border border-slate-800 shadow-inner">
                <div>
                  <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">Founded</div>
                  <div className="text-[15px] font-bold text-slate-200">{activeCompany.founded}</div>
                </div>
                <div className="border-l border-slate-700/50 pl-4">
                  <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">Ownership</div>
                  <div className="text-[15px] font-bold text-slate-200">{activeCompany.ownership}</div>
                </div>
                <div className="border-l border-slate-700/50 pl-4">
                  <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">Export &amp; Reg</div>
                  <div className={`text-[15px] font-bold ${activeCompany.itar.includes('Restricted') || activeCompany.itar.includes('Compliant') ? 'text-rose-400' : 'text-emerald-400'}`}>
                    {activeCompany.itar}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <Award className="w-3.5 h-3.5" /> Core Mission
                </h4>
                <p className="text-slate-200 text-sm leading-relaxed">{renderWithJargon(activeCompany.desc)}</p>
              </div>
              
              <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-blue-400" /> Competitive Moat
                </h4>
                <p className="text-blue-100/80 text-sm">{renderWithJargon(activeCompany.moat)}</p>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <Activity className="w-3.5 h-3.5 text-emerald-400" /> Exit / Market Signal
                </h4>
                <p className="text-slate-300 text-sm">{renderWithJargon(activeCompany.signal)}</p>
              </div>
            </div>
            
            <div className="p-4 border-t border-slate-800 bg-slate-900/30 flex justify-end rounded-b-2xl">
              <button 
                onClick={() => setActiveCompany(null)}
                className="text-sm font-semibold text-slate-400 hover:text-white transition-colors"
              >
                Close View
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BD Inquiry Modal - Prompt Interface */}
      {isInquiryOpen && (
        <div 
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-[#0a0f1a]/85 backdrop-blur-md animate-fade-in"
          onClick={() => setIsInquiryOpen(false)}
        >
          <div 
            className="bg-[#0B1120] border border-slate-700/80 rounded-2xl w-full max-w-lg shadow-[0_20px_60px_rgba(0,0,0,0.9)] flex flex-col overflow-hidden relative"
            onClick={e => e.stopPropagation()}
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-500" />
            
            <div className="p-6 md:p-8 border-b border-slate-800">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2 text-indigo-400 mb-4">
                  <Command className="w-5 h-5" />
                  <span className="text-xs font-mono font-bold tracking-[0.2em] uppercase">Frontier Reasoning</span>
                </div>
                <button 
                  onClick={() => setIsInquiryOpen(false)}
                  className="p-1.5 text-slate-500 hover:text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <h2 className="text-2xl font-serif font-bold text-white mb-2">Mission Briefing</h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                Provide your exact strategic constraints. Our agentic intelligence engine will curate a bespoke, high-conviction target list within <span className="text-indigo-400 font-semibold">48 hours</span>.
              </p>
            </div>
            
            <div className="p-6 md:p-8 space-y-6 bg-[#111827]">
              <div className="bg-slate-900/60 p-5 rounded-xl border border-slate-800/80 relative">
                <div className="absolute -top-2.5 left-4 bg-[#111827] px-2 text-[10px] font-mono text-slate-500 uppercase tracking-widest">Active Context</div>
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-4">
                    <span className="text-slate-500 text-xs uppercase tracking-wider font-semibold">Initiating Entity:</span>
                    <span className="text-white font-mono text-sm tracking-wide bg-slate-800 px-2 py-1 rounded">{firmName || 'GUEST'}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-4">
                    <span className="text-slate-500 text-xs uppercase tracking-wider font-semibold">Strategic Mandate:</span>
                    <span className={`font-mono text-sm tracking-wide bg-slate-800 px-2 py-1 rounded truncate max-w-[200px] sm:max-w-xs ${userMandate ? 'text-cyan-400' : 'text-slate-500 italic'}`}>
                      {userMandate || 'Unspecified (Broad Sourcing)'}
                    </span>
                  </div>
                </div>
              </div>

              <label className={`flex items-start gap-3 p-4 rounded-xl border transition-colors cursor-pointer ${krUpsellActive ? 'bg-amber-900/20 border-amber-500/40 shadow-[0_0_15px_rgba(245,158,11,0.1)]' : 'bg-[#0a0f1a] border-slate-700 hover:border-amber-500/30'}`}>
                <input 
                  type="checkbox" 
                  className="mt-1 accent-amber-500 bg-slate-900 border-slate-700 w-4 h-4 rounded shrink-0 cursor-pointer" 
                  checked={krUpsellActive}
                  onChange={(e) => setKrUpsellActive(e.target.checked)}
                />
                <div>
                  <div className={`text-sm font-bold flex items-center gap-2 ${krUpsellActive ? 'text-amber-500' : 'text-slate-300'}`}>
                    <UserCheck className="w-4 h-4" /> Elevate to Expert Validation (Knowledge Ridge)
                  </div>
                  <div className="text-xs text-slate-400 mt-1.5 leading-relaxed pr-2">
                    Include a 1-on-1 qualitative validation briefing from our curated panel of former MOD/Global Defense and Prime Contractor subject matter experts to verify the commercial viability of your bespoke target list.
                  </div>
                </div>
              </label>

              <div className="space-y-4 pt-2 border-t border-slate-800/60">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Strategic Prompt</label>
                  <div className="relative">
                    <textarea 
                      rows="4"
                      placeholder="Frontier, I am looking for targets in [Geography, e.g., AUKUS/GCC] that solve for [Specific Tech Gap] within a [Valuation/Timeline] window. Initiate Synthesis." 
                      className="w-full bg-[#0a0f1a] border border-slate-700 rounded-xl px-4 py-3 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all text-[15px] font-serif italic resize-none leading-relaxed"
                    ></textarea>
                    <div className="absolute bottom-3 right-3 text-indigo-500/40 pointer-events-none">
                      <BrainCircuit className="w-5 h-5" />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Direct Contact (Email / Phone)</label>
                  <input 
                    type="text" 
                    placeholder="partner@fund.com" 
                    className="w-full bg-[#0a0f1a] border border-slate-700 rounded-xl px-4 py-3 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all text-sm font-medium"
                  />
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-800 bg-[#0B1120] flex flex-col-reverse sm:flex-row justify-end gap-3">
              <button 
                onClick={() => setIsInquiryOpen(false)}
                className="px-6 py-2.5 bg-transparent hover:bg-slate-800 text-slate-400 font-semibold rounded-xl transition-all text-sm"
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  setIsInquiryOpen(false);
                  alert("Mission Briefing submitted successfully. Our agentic sprint has begun. A lead analyst will deliver your synthesis shortly."); 
                }}
                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl transition-all shadow-[0_0_15px_rgba(79,70,229,0.4)] text-sm flex items-center justify-center gap-2"
              >
                <Command className="w-4 h-4" /> Request Target Sourcing Brief
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- AGENT UI COMPONENTS --- */}
      
      {/* Trigger Button */}
      {!isAgentOpen && activeView !== 'cover' && (
        <button 
          onClick={() => setIsAgentOpen(true)} 
          className="fixed bottom-6 right-6 z-[60] flex items-center gap-3 px-4 py-2.5 bg-slate-900/90 backdrop-blur-md border border-slate-700/80 rounded-full shadow-[0_0_20px_rgba(0,0,0,0.5)] hover:border-teal-500/50 hover:shadow-[0_0_30px_rgba(20,184,166,0.3)] transition-all group"
        >
          <div className="p-1 rounded-full bg-teal-500/20 group-hover:bg-teal-500/30 transition-colors">
            <Command className="w-4 h-4 text-teal-400" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-200">Frontier Agent</span>
        </button>
      )}

      {/* Side Panel */}
      {isAgentOpen && (
        <div className="fixed inset-y-0 right-0 w-80 sm:w-96 bg-[#0a0f1a]/95 backdrop-blur-xl border-l border-slate-700 shadow-[-20px_0_50px_rgba(0,0,0,0.8)] z-[250] flex flex-col transform transition-transform duration-300 translate-x-0">
          {/* Header */}
          <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-teal-500/10 border border-teal-500/30">
                <Command className="w-4 h-4 text-teal-400" />
              </div>
              <span className="text-xs font-bold text-white uppercase tracking-widest">Strategy Agent</span>
            </div>
            <button onClick={() => setIsAgentOpen(false)} className="p-1.5 hover:bg-slate-800 rounded-md text-slate-400 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Chat History */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
             {chatHistory.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                   <div className={`max-w-[85%] rounded-2xl p-3 ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-br-none' : 'bg-slate-800/80 border border-slate-700 text-slate-300 rounded-bl-none'}`}>
                      {typeof msg.text === 'string' ? <p className="text-sm leading-relaxed">{msg.text}</p> : msg.text}
                      {msg.action && (
                         <button 
                           onClick={msg.action.onClick}
                           className="mt-3 w-full px-3 py-2 bg-[#0B1120] hover:bg-slate-900 border border-slate-700 rounded-lg text-[10px] font-bold text-teal-400 uppercase tracking-widest flex items-center justify-between transition-colors"
                         >
                            {msg.action.label} <ArrowRight className="w-3 h-3" />
                         </button>
                      )}
                   </div>
                </div>
             ))}

             {/* Suggested Prompts (Always visible for guidance) */}
             <div className="flex flex-col gap-2 mt-6 pt-4 border-t border-slate-800/60 px-1">
               <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1 flex items-center gap-1.5">
                 <Target className="w-3 h-3" /> Try Asking:
               </span>
               <button 
                 onClick={() => handleAgentSubmit(null, "Show me players based in the GCC region")}
                 className="text-left px-3 py-2.5 bg-slate-800/40 hover:bg-slate-700/80 border border-slate-700/60 hover:border-teal-500/40 rounded-xl text-xs text-slate-300 transition-all flex items-center justify-between group"
               >
                 <span>"Show me players based in the GCC region"</span>
                 <ArrowRight className="w-3 h-3 text-slate-500 group-hover:text-teal-400 transition-colors" />
               </button>
               <button 
                 onClick={() => handleAgentSubmit(null, "What is the strategic moat of Anduril?")}
                 className="text-left px-3 py-2.5 bg-slate-800/40 hover:bg-slate-700/80 border border-slate-700/60 hover:border-teal-500/40 rounded-xl text-xs text-slate-300 transition-all flex items-center justify-between group"
               >
                 <span>"What is the strategic moat of Anduril?"</span>
                 <ArrowRight className="w-3 h-3 text-slate-500 group-hover:text-teal-400 transition-colors" />
               </button>
               <button 
                 onClick={() => handleAgentSubmit(null, "Show me the Autonomous Cyber Warfare segment")}
                 className="text-left px-3 py-2.5 bg-slate-800/40 hover:bg-slate-700/80 border border-slate-700/60 hover:border-teal-500/40 rounded-xl text-xs text-slate-300 transition-all flex items-center justify-between group"
               >
                 <span>"Show me the Autonomous Cyber Warfare segment"</span>
                 <ArrowRight className="w-3 h-3 text-slate-500 group-hover:text-teal-400 transition-colors" />
               </button>
             </div>

             <div ref={chatEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-slate-800 bg-slate-900/30">
             <form onSubmit={handleAgentSubmit} className="relative">
                <input 
                   type="text"
                   value={agentQuery}
                   onChange={(e) => setAgentQuery(e.target.value)}
                   placeholder="Ask about Neo-Primes or Segments..."
                   className="w-full bg-[#0B1120] border border-slate-700 rounded-xl pl-4 pr-10 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
                />
                <button type="submit" disabled={!agentQuery.trim()} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-teal-400 disabled:opacity-50 transition-colors">
                   <Send className="w-4 h-4" />
                </button>
             </form>
             <div className="text-center mt-2 flex justify-center items-center gap-1.5 opacity-60">
               <span className="text-[9px] text-slate-500 font-mono uppercase tracking-widest">Shortcut:</span>
               <kbd className="bg-slate-800 px-1.5 py-0.5 rounded border border-slate-700 text-[9px] font-mono text-slate-300 shadow-sm">Cmd/Ctrl + K</kbd>
             </div>
          </div>
        </div>
      )}

    </div>
  );
}